import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { setDateLocale } from '@/lib/dateUtils';
import { useAuth } from '@/contexts/AuthContext';
import { marketingTranslationsEn, marketingTranslationsIt } from '@/contexts/marketingTranslations';
import { marketingFaqTranslationsEn, marketingFaqTranslationsIt } from '@/contexts/marketingFaqTranslations';
import { isPublicApplyContext } from '@/lib/publicApplySite';

export type Language = 'en' | 'it';

export function translateForLanguage(
  language: Language,
  key: string,
  params?: Record<string, string | number>,
): string {
  let text = translations[language][key] || translations.en[key] || key;
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, String(v));
    });
  }
  return text;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};

export const LANGUAGES: { value: Language; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'it', label: 'Italiano' },
];

const translations: Record<Language, Record<string, string>> = {
  en: {
    ...marketingTranslationsEn,
    ...marketingFaqTranslationsEn,
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.positions': 'Job Positions',
    'nav.applications': 'Applications',
    'nav.postings': 'Postings',
    'nav.company': 'Company',
    'nav.companySettings': 'Open company settings',
    'nav.adminBackoffice': 'Backoffice',
    'nav.profile': 'Profile',
    'nav.addCompany': 'Add Company',
    'nav.selectCompany': 'Select Company',
    'nav.newPosition': 'New Position',
    'nav.awaitingDecision': 'Awaiting Decision',
    'nav.menu': 'Navigation menu',
    'nav.mainNavigation': 'Main application navigation links',
    'nav.reportIssue': 'Report a bug or issue',

    'support.title': 'Report a bug or issue',
    'support.subtitle': 'Describe what went wrong. We will receive technical context to help investigate.',
    'support.categoryLabel': 'Type',
    'support.category.bug': 'Bug',
    'support.category.feedback': 'Feedback',
    'support.category.other': 'Other',
    'support.messageLabel': 'Description',
    'support.messagePlaceholder': 'What happened? What did you expect? Steps to reproduce help us fix issues faster.',
    'support.messageTooShort': 'Please enter at least 10 characters.',
    'support.attachmentsLabel': 'Screenshots (optional)',
    'support.attachmentsHint': 'Up to 5 images, PNG/JPEG/WebP, max 2 MB each.',
    'support.attachments.add': 'Add',
    'support.attachments.remove': 'Remove image',
    'support.attachments.max': 'You can attach up to 5 images.',
    'support.attachments.invalidType': 'Use PNG, JPEG, or WebP images only.',
    'support.attachments.tooLarge': 'Each image must be 2 MB or smaller.',
    'support.attachments.uploadFailed': 'Image upload failed. Try again or send without screenshots.',
    'support.diagnostics.toggle': 'What we send automatically',
    'support.diagnostics.route': 'Current page',
    'support.diagnostics.release': 'App version',
    'support.diagnostics.company': 'Selected company',
    'support.diagnostics.errors': 'Recent errors in this session',
    'support.diagnostics.none': 'none recorded',
    'support.consentLabel': 'I authorize sending technical diagnostic data to resolve this issue.',
    'support.consentRequired': 'Please confirm diagnostic data sharing before submitting.',
    'support.submit': 'Send report',
    'support.submitting': 'Sending…',
    'support.success': 'Report sent. We will get back to you if needed.',
    'support.successTitle': 'Thank you',
    'support.successDescription': 'Your report was sent to our team. You can continue using HiroMatch.',
    'support.sendAnother': 'Send another report',
    'support.backToDashboard': 'Back to dashboard',
    'support.errorGeneric': 'Could not send the report. Try again later.',
    'support.errorUnavailable': 'Support email is temporarily unavailable. Try again in a few minutes.',
    'support.errorRateLimit': 'Too many reports in the last hour. Please wait before sending another.',

    // Onboarding eligibility / upgrade-instead-of-create modal
    'onboarding.upgradeInsteadOfCreate.title': 'You already have a company',
    'onboarding.upgradeInsteadOfCreate.body': 'Each account can manage one SME company. You are admin of:',
    'onboarding.upgradeInsteadOfCreate.planLabel': 'Plan',
    'onboarding.upgradeInsteadOfCreate.tip': 'Need more capacity? Upgrade your current plan instead of creating a new company.',
    'onboarding.upgradeInsteadOfCreate.cta.upgrade': 'Upgrade plan',

    // Profile page
    'profile.title': 'My profile',
    'profile.subtitle': 'Manage your account, plan, and security',
    'profile.tab.account': 'Account',
    'profile.tab.billing': 'Plan',
    'profile.tab.security': 'Security',
    'profile.tab.companies': 'Companies',
    'profile.tab.notifications': 'Notifications',
    // v3-notif (27/05/26) — User notification preferences tab
    'notifPrefs.heading': 'Email notifications',
    'notifPrefs.subheading': 'Choose what arrives in your inbox. The bell inside the app keeps you posted in real time.',
    'notifPrefs.usingDefaultsBanner': 'You are using the recommended defaults. Adjust any switch to override.',
    'notifPrefs.cat.application_received.title': 'New applications',
    'notifPrefs.cat.application_received.desc': 'When a candidate applies — guaranteed signal even if screening fails or is blocked by your plan limits.',
    'notifPrefs.cat.application_screened.title': 'Screening completed',
    'notifPrefs.cat.application_screened.desc': 'AI screening result. By default this lives only in the app — flip the switch if you want it in your inbox too.',
    'notifPrefs.cat.application_state.title': 'Application state changes',
    'notifPrefs.cat.application_state.desc': 'Hired, rejected, withdrawn, offer declined, pipeline error.',
    'notifPrefs.cat.interview_lifecycle.title': 'Interview lifecycle',
    'notifPrefs.cat.interview_lifecycle.desc': 'Scheduled, cancelled or report ready — calendar-critical.',
    'notifPrefs.cat.team_events.title': 'Team events',
    'notifPrefs.cat.team_events.desc': 'Invitation responses and team member removals.',
    'notifPrefs.master.inApp.title': 'In-app notifications (bell)',
    'notifPrefs.master.inApp.desc': 'Master switch. When off, the bell in the top bar stays silent for every category.',
    'notifPrefs.digest.title': 'Daily digest',
    'notifPrefs.digest.desc': 'Bundle non-urgent emails into a single recap. Only positions that opted in to daily digest will participate.',
    'notifPrefs.digest.hourLabel': 'Delivery hour (your local time)',
    'notifPrefs.digest.hourHint': '0–23 — for example 9 for a morning recap.',
    'notifPrefs.save': 'Save preferences',
    'notifPrefs.saved': 'Preferences saved',
    'notifPrefs.saveError': 'Could not save preferences',
    'notifPrefs.reset': 'Reset to defaults',
    'notifPrefs.resetConfirm.title': 'Reset notification preferences?',
    'notifPrefs.resetConfirm.description': 'Your custom preferences will be removed and the recommended defaults will apply again.',
    'notifPrefs.resetDone': 'Reset to defaults',
    'notifPrefs.complianceNote': 'Billing, security and password emails are always delivered regardless of these settings.',
    'profile.header.companiesOwned': '{n} owned companies',
    'profile.header.individual': 'Individual account',
    'profile.header.agencyOwner': 'Agency Owner',
    'profile.header.agencyMember': 'Agency Member',
    'profile.header.invitedMember': 'Team Member',
    'profile.header.memberOf': 'Member of {owner}',
    'profile.billing.movedFromSettings': 'Plan settings moved here from Company Settings — every user manages their own subscription on their profile.',
    'subscription.member.cardTitle': 'Plan Information',
    'subscription.member.managedByOwner': 'This plan is managed by {owner}',
    'subscription.member.contactToChange': 'Contact your administrator to change the plan',
    'subscription.member.contactEmail': 'Administrator contact',
    'upgrade.member.title': 'Plan limit reached',
    'upgrade.member.description': 'Contact {owner} to upgrade the plan for your team.',
    'upgrade.member.cta': 'Close',
    'companies.create.member.title': 'You cannot create a company',
    'companies.create.member.description': 'You are a member of another organization. Ask your administrator to remove you before creating your own company.',
    'invitation.error.target_is_agency_owner': 'This user is an agency owner and cannot be invited as a member.',
    'invitation.error.target_belongs_to_other_owner': 'This user already belongs to another organization. They must leave it before joining yours.',
    'invitation.error.fallback': 'Failed to send invitation',
    'memberships.leave.cta': 'Leave',
    'memberships.leave.title': 'Leave this company?',
    'memberships.leave.description': 'You will lose access to {company}. You can be re-invited later by an administrator.',
    'memberships.leave.confirm': 'Leave company',
    'memberships.leave.success': 'You have left {company}',
    'memberships.leave.error.owner': 'You cannot leave a company you own. Delete the company instead.',
    'memberships.leave.error.last_admin': 'You are the last admin. Promote another member before leaving.',
    'memberships.leave.error.fallback': 'Failed to leave company',
    'memberships.delete.cta': 'Delete',
    'memberships.delete.title': 'Delete this company?',
    'memberships.delete.description': 'This will permanently delete {company} and all its data (job positions, candidates, members, billing). This action cannot be undone.',
    'memberships.delete.typeToConfirm': 'Type {name} to confirm',
    'memberships.delete.confirm': 'Delete company',
    'memberships.delete.success': 'Company {company} deleted',
    'memberships.delete.error.fallback': 'Failed to delete company',
    'profile.displayName': 'Display Name',
    'profile.email': 'Email',
    'profile.timeZone': 'Time zone',
    'profile.timeZoneDesc': 'Used for interview scheduling and dates in your local time.',
    'profile.saveChanges': 'Save Changes',
    'profile.profileUpdated': 'Profile updated',
    'profile.changeAvatar': 'Change Avatar',
    'profile.memberships': 'My Memberships',
    'profile.notifications': 'Notification Preferences',
    'profile.emailNotif': 'Email notifications',
    'profile.emailNotifDesc': 'Receive updates and alerts via email',
    'profile.inAppNotif': 'In-app notifications',
    'profile.inAppNotifDesc': 'Show notifications inside the platform',
    'profile.notifyAbout': 'Notify me about:',
    'profile.newApps': 'New applications',
    'profile.newAppsDesc': 'When a candidate applies to a position',
    'profile.screeningDone': 'Screening completed',
    'profile.screeningDoneDesc': 'When AI screening finishes processing',
    'profile.interviewReports': 'Interview reports',
    'profile.interviewReportsDesc': 'When an interview report is ready',
    'profile.offerResponses': 'Offer responses',
    'profile.offerResponsesDesc': 'When a candidate responds to an offer',
    'profile.changePassword': 'Change Password',
    'profile.currentPassword': 'Current Password',
    'profile.newPassword': 'New Password',
    'profile.confirmPassword': 'Confirm New Password',
    'profile.updatePassword': 'Update Password',
    'profile.passwordUpdated': 'Password updated',
    'profile.passwordMismatch': 'New password and confirmation do not match.',
    'profile.passwordTooWeak': 'Password is too weak. Use at least 6 characters.',
    'profile.passwordWrongCurrent': 'Current password is incorrect.',
    'profile.passwordRequiresRecentLogin': 'For security, please sign in again to change your password.',
    'profile.passwordGenericError': 'Could not update password. Please try again.',
    'profile.signInMethods': 'Sign-in methods',
    'profile.signInGoogle': 'Google',
    'profile.signInEmailPassword': 'Email & password',
    'profile.signInGoogleOnlyDesc': 'You sign in with Google. You can add a HiroMatch password below to also sign in with email.',
    'profile.signInPasswordOnlyDesc': 'You sign in with your email and password.',
    'profile.signInBothDesc': 'You can sign in with Google or with your email and password.',
    'profile.hiromatchPassword': 'Reset Password',
    'profile.resetPassword': 'Reset Password',
    'profile.resetPasswordButton': 'Reset Password',
    'profile.setPasswordDesc': 'Add a password to sign in with email in addition to Google.',
    'profile.passwordResetDesc': 'We will send a secure link to your email to set a new password.',
    'profile.passwordResetEmailTo': 'The reset link will be sent to {email}.',
    'profile.setPassword': 'Set HiroMatch password',
    'profile.sendPasswordReset': 'Send password reset link',
    'profile.passwordResetSending': 'Sending…',
    'profile.passwordResetSent': 'Check your inbox — we sent a link to set or change your password.',
    'profile.passwordResetError': 'Could not send the reset link. Please try again.',
    'profile.identitySection': 'Identity',
    'profile.preferencesSection': 'Preferences',
    'profile.securitySection': 'Security',
    'profile.dangerZone': 'Danger zone',
    'profile.uploadAvatar.tooLarge': 'Image too large. Max 2MB.',
    'profile.uploadAvatar.invalidType': 'Unsupported image type. Use PNG, JPEG or WebP.',
    'profile.uploadAvatar.failed': 'Avatar upload failed. Please try again.',
    'profile.uploadAvatar.success': 'Avatar updated',
    'profile.memberships.empty': "You don't belong to any company yet — create one or join via invite.",
    'profile.appearance': 'Appearance',
    'profile.theme': 'Theme',
    'profile.language': 'Language',
    'profile.languageSaveError': 'Could not save language preference.',
    'profile.light': 'Light',
    'profile.dark': 'Dark',
    'profile.system': 'System',
    'profile.logout': 'Logout',
    'profile.logoutDesc': 'Sign out of your account on this device.',
    'profile.disableAccount': 'Disable Account',
    'profile.disableAccountShort': 'Disable',
    'profile.disableAccountDesc': 'Temporarily disable your account. You will be removed from all companies and will no longer be able to log in. Contact support to reactivate.',
    'profile.disableAccountConfirm': 'Are you sure? Your account will be disabled, you will be removed from all companies, and you will no longer be able to log in. Contact support to reactivate.',
    'profile.accountDisabled': 'Account disabled',
    'profile.permanentDeleteAccount': 'Delete Account',
    'profile.deleteAccountShort': 'Delete',
    'profile.permanentDeleteAccountDesc': 'Permanently delete your account and all associated data. This action is irreversible.',
    'profile.permanentDeleteAccountConfirm': 'This action is permanent and cannot be undone. Your account, all data, and memberships will be permanently deleted.',
    'profile.accountPermanentlyDeleted': 'Account permanently deleted',
    'profile.typeEmail': 'Type your email to confirm',

    // Dashboard
    'dashboard.openPositions': 'Open Positions',
    'dashboard.activePostings': 'Active Postings',
    'dashboard.applications': 'Applications',
    'dashboard.processing': 'Processing',
    'dashboard.awaitingDecision': 'Awaiting Decision',
    'dashboard.offerAcceptance': 'Offer Acceptance',
    'dashboard.medianTimeToHire': 'Median Time to Hire',
    'dashboard.medianTimeToFill': 'Median Time to Fill',
    'dashboard.tooltip.openPositions': '{count} positions open in the selected period; {aging} of them have been open for more than 30 days.',
    'dashboard.tooltip.activePostings': 'Active job postings across all positions in the selected period.',
    'dashboard.tooltip.applications': 'Applications received in the selected period. Total all-time: {total}.',
    'dashboard.tooltip.processing': 'Applications received in the selected period and currently being processed by AI screening.',
    'dashboard.tooltip.awaitingDecision': 'Applications received in the selected period and currently awaiting recruiter action.',
    'dashboard.tooltip.offerAcceptance': 'HIRED / (HIRED + OFFER_DECLINED) for offers made in the selected period.',
    'dashboard.tooltip.medianTimeToHire': 'Days from application to hire (median) in the selected period. Target: 20 days.',
    'dashboard.tooltip.medianTimeToFill': 'Days from position creation to first hire (median). Target: 30 days.',
    'dashboard.client': 'Client',
    'dashboard.aggregated_topPositions': 'Top positions across all clients',
    'dashboard.aggregated_recentApps': 'Recent applications across all clients',
    'dashboard.aggregated_trend': 'Applications trend (portfolio)',
    'dashboard.aggregated_timeInState': 'Median time in state (portfolio)',
    'dashboard.portfolio_aiCredits.title': 'AI Credits — portfolio total',
    'dashboard.portfolio_aiCredits.subtitle': 'Sum across all client companies',
    'dashboard.portfolio_aiCredits.unlimited': 'Unlimited on at least one client',
    'dashboard.portfolio_aiCredits.used': '{used} of {granted} used',
    'dashboard.portfolio_aiCredits.remaining': '{remaining} remaining',
    'dashboard.recentApplications': 'Recent Applications',
    'dashboard.viewAll': 'View all',
    'dashboard.noRecent': 'No recent applications',
    'dashboard.candidate': 'Candidate',
    'dashboard.position': 'Position',
    'dashboard.source': 'Source',
    'dashboard.score': 'Score',
    'dashboard.status': 'Status',
    'dashboard.7days': '7 days',
    'dashboard.30days': '30 days',
    'dashboard.90days': '90 days',
    'dashboard.allTime': 'All time',
    'dashboard.tab_company': 'Company',
    'dashboard.tab_plan': 'My Plan',
    'dashboard.plan_limits': 'Plan Limits',
    'dashboard.plan_ai_credits': 'AI Credits',
    'dashboard.plan_info': 'About Your Plan',
    'dashboard.plan_info_desc': 'These metrics show the aggregated usage across all your client companies. Switch to a specific company tab to see detailed metrics for that company.',
    'dashboard.aggregated_metrics': 'All Companies - Aggregated',
    'dashboard.aggregated_funnel': 'Recruiting Funnel - All Companies',
    'dashboard.conversion_kpis': 'Conversion Rates',
    'dashboard.loading': 'Loading...',
    'dashboard.loading_plan_data': 'Loading plan data...',
    'dashboard.loading_companies': 'Loading data... ({loaded}/{total} companies)',
    'dashboard.aggregated_positions_tooltip': 'Total open positions across all your companies',
    'dashboard.aggregated_postings_tooltip': 'Total active postings across all your companies',
    'dashboard.aggregated_applications_tooltip': 'Applications in selected period. Total all-time: {total}',
    'dashboard.aggregated_processing_tooltip': 'Applications currently being processed across all companies',
    'dashboard.aggregated_awaiting_tooltip': 'Applications awaiting decision across all companies',
    'dashboard.tth_tooltip': 'Days from application to hire (median). Target: 20 days',
    'dashboard.ttf_tooltip': 'Days from position creation to first hire (median). Target: 30 days',
    'dashboard.applicantToInterview': 'Applicant → Interview',
    'dashboard.interviewToHire': 'Interview → Hire',
    'dashboard.applicantToHire': 'Applicant → Hire',
    'dashboard.applicantToReject': 'Applicant → Reject',
    'dashboard.recruitmentFunnel': 'Recruitment Funnel',
    'dashboard.pipelineStatus': 'Pipeline status',
    'dashboard.pipelineStatusHint': 'Snapshot for applications received in the selected period. Tap a cell to open the filtered list.',
    'dashboard.pipelineTab.action': 'Action',
    'dashboard.pipelineTab.pipeline': 'Pipeline',
    'dashboard.pipelineTab.outcomes': 'Outcomes',
    'dashboard.pipelineTab.actionHint': 'Needs your attention today — decisions, processing, upgrades, or errors.',
    'dashboard.pipelineTab.pipelineHint': 'Applications moving through or waiting to enter AI screening.',
    'dashboard.pipelineTab.outcomesHint': 'Closed results in the selected period.',
    'dashboard.funnelOutcomes': 'Closed outcomes',
    'dashboard.funnelOutcomesHint': 'Terminal results in the selected period (not part of the success funnel above).',
    'dashboard.pipelineTooltip.new': 'New applications not yet processed by AI.',
    'dashboard.pipelineTooltip.ready': 'CV saved — ready for you to start AI screening.',
    'dashboard.pipelineTooltip.upgrade': 'Blocked by plan limits — upgrade or wait for cap rollover.',
    'dashboard.pipelineTooltip.processing': 'AI screening currently running.',
    'dashboard.pipelineTooltip.stuck': 'Processing appears stuck — may need a stop or retry.',
    'dashboard.pipelineTooltip.awaiting': 'Screening done — waiting for your decision.',
    'dashboard.pipelineTooltip.error': 'Pipeline error — review and retry or reject.',
    'dashboard.pipelineTooltip.hired': 'Marked as hired in this period.',
    'dashboard.pipelineTooltip.rejected': 'Rejected in this period.',
    'dashboard.pipelineTooltip.withdrawn': 'Withdrawn in this period.',
    'dashboard.pipelineTooltip.offerDeclined': 'Offer declined in this period.',
    'dashboard.noDataPeriod': 'No application data for this period',
    'dashboard.applicationsByChannel': 'Applications by Channel',
    'dashboard.noData': 'No data',
    'dashboard.applied': 'Applied',
    'dashboard.hired': 'Hired',
    'dashboard.hireRate': 'Hire rate',
    'dashboard.insufficientData': 'insufficient data',
    'dashboard.topPositions': 'Top Positions by Volume',
    'dashboard.issuesReview': '{count} issue(s) requiring review',
    'dashboard.noPositionsData': 'No positions with applications in this period',
    'dashboard.rejected': 'Rejected',
    'dashboard.topAwaiting': 'Awaiting',
    'dashboard.topErrors': 'Errors',
    'dashboard.rejectRate': 'Reject Rate',
    'dashboard.medianTimePerState': 'Median Time per State (days)',
    'dashboard.noHistoryData': 'No history data available for closed applications',
    'dashboard.days': 'days',
    'dashboard.median': 'median',
    'dashboard.applicationTrend': 'Application Trend',
    'dashboard.noDataForPeriod': 'No data for this period',
    'dashboard.stateNew': 'New',
    'dashboard.stateProcessing': 'Processing',
    'dashboard.stateAwaitingDecision': 'Awaiting Decision',
    'dashboard.stateReviewRequired': 'Review Required',
    'dashboard.stateRejected': 'Rejected',
    'dashboard.stateHired': 'Hired',
    'dashboard.stateOfferDeclined': 'Offer Declined',
    'dashboard.stateWithdrawn': 'Withdrawn',
    'dashboard.funnelApplied': 'Applied',
    'dashboard.funnelScreened': 'Screened',
    'dashboard.funnelInterview': 'Interview',
    'dashboard.funnelDecision': 'Decision',
    'dashboard.funnelHired': 'Hired',
    'dashboard.tooltipScreened': 'Has AI score, screening report, or progressed beyond initial processing',
    'dashboard.tooltipInterview': 'At least one interview Scheduled or Completed',
    'dashboard.tooltipDecision': 'In AWAITING_DECISION, OFFER_DECLINED, or HIRED state',

    // Job Positions
    'positions.newPosition': 'New Position',
    'positions.searchPositions': 'Search positions...',
    'positions.allStatus': 'All Status',
    'positions.draft': 'Draft',
    'positions.active': 'Active',
    'positions.closed': 'Closed',
    'positions.cancelled': 'Cancelled',
    'positions.title': 'Title',
    'positions.status': 'Status',
    'positions.apps': 'Applications',
    'positions.applicationsTab': 'Applications',
    'positions.postings': 'Postings',
    'positions.created': 'Created',
    'positions.updated': 'Updated',
    'positions.noPositions': 'No positions found',
    'emptyState.noCompany.title': 'Create your company first',
    'emptyState.noCompany.body': "You don't belong to any company yet. Create one to start managing job positions, candidates and your team.",
    'emptyState.noCompany.cta': 'Create company',
    'emptyState.noCompany.body.positions': 'Job positions live inside a company. Create yours to start posting roles and receiving applications.',
    'emptyState.noCompany.body.applications': 'Applications live inside a company. Create yours to start receiving and reviewing candidates.',
    'positions.editPosition': 'Edit Position',
    'positions.closePosition': 'Close Position',
    'positions.activatePosition': 'Activate Position',
    'positions.deletePosition': 'Delete Position',
    'positions.deleteConfirm': 'Choose how to delete this position:',
    'positions.softDeleteTitle': 'Cancel (soft delete)',
    'positions.softDeleteDesc': 'Sets status to Cancelled. All data is preserved in the database.',
    'positions.hardDeleteTitle': 'Permanently delete',
    'positions.hardDeleteDesc': 'Removes the position and its postings from the database. Applications are preserved for audit.',
    'positions.softDeleteAction': 'Cancel Position',
    'positions.hardDeleteAction': 'Permanently Delete',
    'positions.positionCancelled': 'Position cancelled',
    'positions.positionDeleted': 'Position permanently deleted',
    'positions.positionUpdated': 'Position updated',
    'positions.mustBeActiveToPost': 'Activate the position first before publishing a posting',
    'positions.positionCreated': 'Position created',
    'positions.loadFailed': 'Failed to load position',
    'positions.atLeastOneMember': 'Assign at least one team member to this position',
    'positions.description': 'Description',
    'positions.seniority': 'Seniority',
    'positions.contractType': 'Contract Type',
    'positions.workMode': 'Work Mode',
    'positions.location': 'Location',
    'positions.notSpecified': 'Not specified',
    'positions.salaryMin': 'Salary Min',
    'positions.salaryMax': 'Salary Max',
    'positions.currency': 'Currency',
    'positions.team': 'Team',
    'positions.overviewLanguageHint': 'Used for the public posting, and for AI screening and reports on received applications.',
    'positions.overviewNoAssignedMembers': 'No members assigned',
    'positions.allTeams': 'All teams',
    'positions.noTeam': 'No team',
    'positions.aiAssistant': 'AI Assistant',
    'positions.closeAI': 'Close AI',
    'positions.aiTryAsking': 'Try asking:',
    'positions.aiPromptWrite': 'Write a job description',
    'positions.aiPromptRequirements': 'Suggest requirements',
    'positions.aiPromptImprove': 'Improve the description',
    'positions.aiSuggestions': 'AI Suggestions',
    'positions.aiDismiss': 'Dismiss',
    'positions.aiApplySuggestions': 'Apply Suggestions',
    'positions.aiAutoFill': 'Auto-fill form',
    'positions.aiAutoFillLoading': 'Generating suggestions…',
    'positions.aiAutoFillTooltip': 'Chat with the AI to generate field suggestions',
    'positions.aiAutoFillReadyTooltip': 'AI is ready to fill the form — click to apply suggestions',
    'positions.aiAskPlaceholder': 'Ask the AI for help…',
    'positions.descriptionPlaceholder': 'Job description, responsibilities, requirements…',
    'positions.titleExample': 'e.g. Senior Frontend Developer',
    'positions.selectPlaceholder': 'Select…',
    'positions.selectLocation': 'Select location…',
    'positions.selectTeam': 'Select team…',
    'positions.other': 'Other…',
    'positions.customSeniority': 'Enter custom seniority…',
    'positions.customContractType': 'Enter custom contract type…',
    'positions.noCompanyLocations': 'Add office locations in Company settings first',
    'positions.noCompanyTeams': 'Add teams in Company settings first',
    'positions.noMembersFound': 'No members found',
    'positions.seniorityJunior': 'Junior',
    'positions.seniorityJuniorMid': 'Junior–Mid',
    'positions.seniorityMid': 'Mid',
    'positions.seniorityMidSenior': 'Mid–Senior',
    'positions.senioritySenior': 'Senior',
    'positions.contractFullTime': 'Full-time',
    'positions.contractPartTime': 'Part-time',
    'positions.contractFreelance': 'Freelance',
    'positions.contractInternship': 'Internship',
    'positions.contractTemporary': 'Temporary',
    'positions.workModeOnSite': 'On-site',
    'positions.workModeHybrid': 'Hybrid',
    'positions.workModeRemote': 'Remote',
    'positions.savingState': 'Saving…',
    'positions.requirePhone': 'Require phone number',
    'positions.requireLinkedIn': 'Require LinkedIn profile',
    'positions.socialProfiles': 'Social profiles',
    'positions.addSocialProfile': 'Add',
    'positions.socialProfilesEmpty': 'No social fields on the application form. Add one to collect LinkedIn, Instagram, Facebook, or other links.',
    'positions.applicationDocuments': 'Application documents',
    'positions.addDocumentField': 'Add',
    'positions.applicationDocumentsEmpty': 'No document uploads on the application form. Add Resume, Presentation Letter, or Portfolio.',
    'positions.documentRequired': 'Required',
    'positions.removeDocumentField': 'Remove document field',
    'positions.documentType.resume': 'Resume',
    'positions.documentType.presentation_letter': 'Presentation letter',
    'positions.documentType.portfolio': 'Portfolio',
    'positions.documentTypeHint.resume': 'PDF resume / CV',
    'positions.documentTypeHint.presentation_letter': 'PDF presentation or cover letter',
    'positions.documentTypeHint.portfolio': 'PDF portfolio (view only for recruiters in MVP)',
    'positions.socialRequired': 'Required',
    'positions.socialCustomLabel': 'Custom label',
    'positions.removeSocialProfile': 'Remove social profile',
    'positions.socialType.linkedin': 'LinkedIn',
    'positions.socialType.instagram': 'Instagram',
    'positions.socialType.facebook': 'Facebook',
    'positions.socialType.tiktok': 'TikTok',
    'positions.socialType.website': 'Website',
    'positions.socialType.custom': 'Custom',
    'positions.socialTypeHint.linkedin': 'LinkedIn profile URL',
    'positions.socialTypeHint.instagram': 'Instagram profile URL',
    'positions.socialTypeHint.facebook': 'Facebook profile URL',
    'positions.socialTypeHint.tiktok': 'TikTok profile URL',
    'positions.socialTypeHint.website': 'Personal or portfolio website',
    'positions.socialTypeHint.custom': 'Choose a custom label for this link',
    'apply.socialRequired': '{label} is required',
    'apply.socialInvalid': 'Enter a valid URL for {label}',
    'positions.notifyCandidate': 'Email notification to candidates',
    'positions.allowDuplicateApplications': 'Allow duplicate applications',
    'positions.allowDuplicateApplicationsHint': 'Same candidate can reapply with identical data',
    // v3-notif (27/05/26) — Position notification settings section
    'positionNotif.section.title': 'Team notifications',
    'positionNotif.section.desc': 'Decide what this position emits to its team members. User-level preferences still apply on top — the email goes out only if both layers agree.',
    'positionNotif.usingDefaultsBanner': 'Using system defaults — save to lock the current values to this position.',
    'positionNotif.readonlyBanner': 'You need write or admin access to edit these settings.',
    'positionNotif.emitApplicationReceived': 'Emit on new application',
    'positionNotif.emitApplicationReceived.hint': 'Guaranteed signal — fires even if screening later fails.',
    'positionNotif.emitApplicationScreened': 'Emit on screening completed',
    'positionNotif.emitApplicationScreened.hint': 'Controls in-app bell alerts and optional email when AI screening finishes. Off by default — the score still appears on the application card.',
    'positionNotif.emitApplicationState': 'Emit on application state changes',
    'positionNotif.emitApplicationState.hint': 'Hired, rejected, withdrawn, offer declined, pipeline error.',
    'positionNotif.emitInterviewLifecycle': 'Emit on interview lifecycle',
    'positionNotif.emitInterviewLifecycle.hint': 'Scheduled, cancelled, report ready.',
    'positionNotif.enableDailyDigest': 'Include in daily digest',
    'positionNotif.enableDailyDigest.hint': 'When on, recipients who opted in to the daily digest receive a bundled recap for this position.',
    'positionNotif.save': 'Save settings',
    'positionNotif.saved': 'Notification settings saved',
    'positionNotif.saveError': 'Could not save notification settings',
    'positions.autoRejectScore': 'Auto-reject score threshold',
    'positions.outputLanguage': 'AI & candidate language',
    'positions.outputLanguageHint': 'Changing this updates AI screening and interview reports, candidate emails, and the public posting page — form labels, role badges (e.g. Remote), and the confirmation screen.',
    'positions.outputLanguageIt': 'Italian',
    'positions.outputLanguageEn': 'English',
    // Sprint 3 (S3.E) — post-create wizard
    'positions.wizardCreatedTitle': 'Position created',
    'positions.wizardCreatedSubtitle': 'Activate it now to start collecting candidates, or keep editing in draft.',
    'positions.wizardLandingLabel': 'Public landing page',
    'positions.wizardLandingHint': 'The link works once the position is active.',
    'positions.wizardSaveAsDraft': 'Save as draft',
    'positions.wizardActivate': 'Activate now',
    'positions.linkCopied': 'Link copied to clipboard',
    'positions.activated': 'Position activated. The link is now live.',
    'positions.activatedPopupBlocked': 'Position activated, but your browser blocked the new tab. Use “View public page” from the detail to open it.',
    'positions.copy': 'Copy',
    'positions.openLink': 'Open',
    'positions.assignedMembers': 'Assigned Members',
    'positions.assignedMembersCreator': 'Creator',
    'positions.assignedMembersAllAssigned': 'All members are assigned',
    'positions.assignedMembersSearch': 'Search by name or email…',
    'positions.searchMembers': 'Search members...',
    
    'positions.overview': 'Overview',
    'positions.history': 'History',
    'positions.versions': 'Versions',
    'positions.noHistory': 'No history available',
    'positions.noVersions': 'No versions available',
    'positions.noApplications': 'No applications yet',
    'positions.current': 'Current',
    'positions.view': 'View',
    'positions.version': 'Version',
    'positions.salary': 'Salary',
    'positions.contract': 'Contract',
    'positions.options': 'Options',
    'positions.viewPosting': 'View Posting',
    'positions.copyUrl': 'Copy URL',
    'positions.postingActivated': 'Posting activated',
    'positions.postingDeactivated': 'Posting deactivated',
    'positions.noDetails': 'No details',

    // Applications
    'applications.searchCandidates': 'Search candidates...',
    'applications.allStatus': 'All Status',
    'applications.new': 'New',
    'applications.processing': 'Processing',
    'applications.processingStuck': 'Processing stuck',
    'applications.inQueue': 'In queue',
    'applications.awaitingDecision': 'Awaiting Decision',
    'applications.interview': 'Interview',
    'applications.rejected': 'Rejected',
    'applications.hired': 'Hired',
    'applications.reviewRequired': 'Review Required',
    'applications.withdrawn': 'Withdrawn',
    'applications.offerDeclined': 'Offer Declined',
    'applications.candidate': 'Candidate',
    'applications.email': 'Email',
    'applications.position': 'Position',
    'applications.source': 'Source',
    'applications.applied': 'Applied',
    'applications.updated': 'Updated',
    'applications.score': 'Score',
    'applications.status': 'Status',
    'applications.statusPinTooltip': 'Pin a status group to the top (works with column sort)',
    'applications.allPositions': 'All Positions',
    'applications.team': 'Team',
    'applications.allTeams': 'All teams',
    'applications.star': 'Star',
    'applications.starAction': 'Add to shortlist',
    'applications.unstarAction': 'Remove from shortlist',
    'applications.unreadIndicator': 'Unread application',
    'applications.bulkMarkRead': 'Mark as read',
    'applications.bulkMarkReadSuccess': '{count} candidates marked as read',
    'applications.bulkMarkReadFailed': 'Could not mark candidates as read — try again',
    'applications.bulkMarkUnread': 'Mark as unread',
    'applications.bulkMarkUnreadSuccess': '{count} candidates marked as unread',
    'applications.bulkMarkUnreadFailed': 'Could not mark candidates as unread — try again',
    'applications.sortByStar': 'Sort by shortlist',
    'applications.sortByInbox': 'Sort by read status',
    'applications.allStarred': 'All candidates',
    'applications.starredOnly': 'Shortlisted only',
    'applications.unstarredOnly': 'Not shortlisted',
    'applications.noApplications': 'No applications found',
    'applications.noApplicationsForFilter': 'No candidates match the current filters.',
    'applications.clearFilters': 'Clear filters',
    'applications.loadMore': 'Load more',
    'applications.hydratingList': 'Loading… {loaded} / {total}',
    'pagination.showing': 'Showing {from}–{to} of {total}',
    'pagination.pageOf': 'Page {page} of {totalPages}',
    'pagination.previous': 'Previous',
    'pagination.next': 'Next',
    'applications.loadingMore': 'Loading…',
    // Sprint 5 (S5.D) — bulk select
    'applications.bulkSelectAll': 'Select all',
    'applications.bulkSelectedCount': '{count} selected',
    'applications.bulkClear': 'Clear',
    'applications.bulkReject': 'Reject',
    'applications.bulkRejectTitle': 'Reject {count} candidates',
    'applications.bulkRejectDesc': 'The note + email opt-in apply to every selected candidate.',
    'applications.bulkRejectConfirm': 'Reject {count}',
    'applications.bulkRejectSuccess': '{count} candidates rejected',
    'applications.bulkRejectPartial': '{count} candidates failed — check the rows and retry',
    'applications.bulkProcess': 'Processing',
    'applications.bulkStop': 'Stop processing',
    'applications.bulkStopTooltip': 'Stop in-flight AI screening without re-running (no credits used)',
    'applications.bulkStopSuccess': 'Stopped processing for {count} candidates',
    'applications.bulkStopPartial': '{count} candidates could not be stopped — check the rows and retry',
    'applications.bulkStopSkipped': 'No in-flight processing to stop for {count} selected rows',
    'applications.bulkStopClearedStale': 'Cleared stale processing indicator for {count} candidates',
    'applications.bulkStopApiUnavailable': 'Stop processing is not available on the server yet. Deploy Cloud Functions (abort-stuck) and try again.',
    'applications.rowStopping': 'Stopping…',
    'applications.bulkProcessSuccess': '{count} candidates queued for AI screening',
    'applications.bulkProcessPartial': '{count} candidates could not be processed — check the rows and retry',
    'applications.queueActive': 'Active',
    'applications.queueArchive': 'Archive',
    'applications.allSources': 'All sources',
    'applications.bulkArchive': 'Archive',
    'applications.bulkUnarchive': 'Restore',
    'applications.bulkArchiveSuccess': '{count} candidates archived',
    'applications.bulkArchivePartial': '{count} candidates could not be archived — check the rows and retry',
    'applications.bulkUnarchiveSuccess': '{count} candidates restored to the active queue',
    'applications.bulkUnarchivePartial': '{count} candidates could not be restored — check the rows and retry',
    'applications.bulkUnarchiveUnavailable': 'Restore failed — archive API may not be deployed yet. Run firebase deploy --only functions, then retry.',
    'applications.bulkDelete': 'Delete permanently',
    'applications.bulkDeleteTitle': 'Delete {count} archived candidates?',
    'applications.bulkDeleteDescription': 'This cannot be undone. Applications, documents, pipeline history, and uploaded files will be permanently removed from the server.',
    'applications.bulkDeleteConfirm': 'Delete {count}',
    'applications.bulkDeleteSuccess': '{count} candidates permanently deleted',
    'applications.bulkDeletePartial': '{count} candidates could not be deleted — check the rows and retry',
    'applications.bulkDeleteUnavailable': 'Delete failed — bulk delete API may not be deployed yet. Run firebase deploy --only functions, then retry.',
    'applications.archivedBanner': 'This candidate is in the archive and is hidden from the active queue.',
    'applications.archiveAction': 'Archive',
    'applications.unarchiveAction': 'Restore to active queue',
    // Pending billing recovery (process-now / process-pending)
    'application.pendingBilling.badge': 'Pending billing',
    'application.pendingBilling.tooltip': 'CV saved — upgrade your plan to process this candidate',
    'application.pendingBilling.processNow': 'Process now',
    'application.pendingBilling.processing': 'Processing…',
    'application.pendingBilling.processSuccess': 'Processing started. The pipeline is running.',
    'application.pendingBilling.processBilling': 'Plan still insufficient. Upgrade your plan to continue.',
    'application.pendingBilling.processAll': 'Process all pending ({count})',
    'application.pendingBilling.reviewInApplications': 'Review in Applications ({count})',
    'application.openApplicationsConfirm.title': 'Review candidates to process',
    'application.openApplicationsConfirm.body':
      '{count} candidates are ready for AI screening. You will review them in the Applications list with the correct filter applied.',
    'application.openApplicationsConfirm.credits':
      'Your plan can process about {count} more candidates this period — process in batches from the toolbar.',
    'application.openApplicationsConfirm.hint':
      'All matching rows will be selected. Click Processing when you are ready; large queues run in batches of 30.',
    'application.openApplicationsConfirm.confirm': 'Open Applications ({count})',
    'applications.bulkReadySelected':
      '{count} candidates selected — click Processing in the toolbar when ready.',
    'application.pendingBilling.bulkProcessing': 'Processing {count} candidates…',
    'application.pendingBilling.bulkSuccess': '{processed} processed, {skipped} skipped',
    'application.pendingBilling.bulkAllSkipped': 'Plan exhausted. Upgrade further to process the remaining {count} candidates.',
    'application.pendingBilling.bannerTitle': '{count} candidates ready to process',
    'application.pendingBilling.bannerBody': 'Your plan upgrade unlocked {count} pending CVs. Run the AI screening now.',
    'application.pendingBilling.bannerProgressTitle': 'Processing candidates: {done} of {total}',
    'application.pendingBilling.bannerProgressBody':
      'AI screening is running. Completed rows leave the queue automatically.',
    'application.pendingBilling.bannerInboundScreening':
      '+ {count} new application(s) from postings are also screening automatically.',
    'application.identityMismatch.badge': 'Mismatch',
    'application.identityMismatch.badgeContact': 'Contact mismatch',
    'application.identityMismatch.tooltipTitle': 'Application form differs from the resume',
    'application.identityMismatch.tooltipTitleContact': 'Contact details differ from the resume',
    'application.identityMismatch.panelTitle': 'Mismatch',
    'application.identityMismatch.panelTitleContact': 'Contact mismatch',
    'application.identityMismatch.panelIntro': 'Application form data does not match the analyzed resume.',
    'application.identityMismatch.panelIntroContact':
      'Email, phone, or LinkedIn on the form differ from the resume — often a typo or an alternate contact. Screening used the resume profile.',
    'application.identityMismatch.panelFooter':
      'Screening evaluated the resume profile. The name at the top reflects the application form.',
    'application.identityMismatch.field.name': 'Name',
    'application.identityMismatch.field.email': 'Email',
    'application.identityMismatch.field.phone': 'Phone',
    'application.identityMismatch.field.linkedin': 'LinkedIn',
    'application.identityMismatch.fieldLine': '{label}: {formValue} (form) → {resumeValue} (resume)',
    'application.processAllConfirm.title': 'Start AI screening',
    'application.processAllConfirm.bodyFirstTime':
      '{count} candidates have not been screened yet and will enter the AI pipeline.',
    'application.processAllConfirm.bodyRetry':
      '{count} candidates were blocked by a billing limit after screening started. They will be retried.',
    'application.processAllConfirm.bodyMixed':
      '{firstTime} candidates will be screened for the first time and {retry} will be retried after a previous billing block.',
    'application.processAllConfirm.confirm': 'Process {count}',

    'application.bulkProcessConfirm.title': 'Process selected candidates',
    'application.bulkProcessConfirm.intro': 'The following will be queued for AI screening:',
    'application.bulkProcessConfirm.lineFirstTime':
      '{count} first-time screening (never processed yet)',
    'application.bulkProcessConfirm.lineRestartProcessing':
      '{count} currently processing — will be stopped and re-run',
    'application.bulkProcessConfirm.lineBillingRetry':
      '{count} blocked by billing limit — will be retried',
    'application.bulkProcessConfirm.lineReprocess':
      '{count} already screened — will be re-processed',
    'application.bulkProcessConfirm.linePipelineRetry':
      '{count} failed previously — will be retried',
    'application.bulkProcessConfirm.creditsWarning':
      'Re-processing and restarting stuck applications consumes AI credits.',
    'application.bulkProcessConfirm.confirm': 'Process {count}',

    // BUG-09 — PENDING_UPGRADE: candidate received but submission cap exhausted.
    'application.pendingUpgrade.badge': 'Awaiting upgrade',
    'application.pendingUpgrade.tooltip':
      'Your monthly candidate limit was reached when this CV arrived. Upgrade your plan or wait for the next billing period to unlock AI screening.',
    'application.pendingUpgrade.detailTitle': 'AI processing locked',
    'application.pendingUpgrade.detailBody':
      'You hit your plan\'s monthly candidate limit when this application was submitted. The CV is safely stored — upgrade or wait for the next reset, then click "Process now" to run AI screening.',
    'application.pendingUpgrade.processNow': 'Process now',
    'application.pendingUpgrade.upgradeCta': 'Upgrade plan',
    'application.pendingUpgrade.bannerTitle':
      '{count} candidates waiting for AI screening',
    'application.pendingUpgrade.bannerBody':
      'Your plan capped these submissions before AI processing. Upgrade to unlock them all, or process them one by one.',

    // READY_TO_PROCESS — post-upgrade, persisted on BE, waiting for recruiter action.
    'application.readyToProcess.badge': 'To be processed',
    'application.readyToProcess.tooltip':
      'CV saved — your plan can process this candidate. Click Process now to start AI screening.',
    'application.readyToProcess.detailTitle': 'Ready for AI screening',
    'application.readyToProcess.detailBody':
      'This candidate is waiting for AI screening. Click Process now to start the pipeline.',
    'application.readyToProcess.bannerTitle': '{count} candidates ready to process',
    'application.readyToProcess.bannerBody':
      'Run AI screening now, or process candidates one by one from the list.',

    // Postings
    'postings.newPosting': 'New Posting',
    'postings.position': 'Position',
    'postings.channel': 'Channel',
    'postings.status': 'Status',
    'postings.apps': 'Apps',
    'postings.published': 'Published',
    'postings.created': 'Created',
    'postings.url': 'URL',
    'postings.copy': 'Copy',
    'postings.active': 'Active',
    'postings.inactive': 'Inactive',
    'postings.activate': 'Activate',
    'postings.deactivate': 'Deactivate',
    'postings.statusUpdateFailed': 'Failed to update posting status',
    'postings.noPostings': 'No postings yet',
    'postings.newJobPosting': 'New Job Posting',
    'postings.editPosting': 'Edit Posting',
    'postings.titleOverride': 'Title override (optional)',
    'postings.titlePlaceholder': 'Leave empty to use position title',
    'postings.selectPosition': 'Select position...',
    'postings.creating': 'Creating...',
    'postings.createPosting': 'Create Posting',
    'postings.postingCreated': 'Posting created',
    'postings.postingUpdated': 'Posting updated',
    'postings.postingDeleted': 'Posting deleted',
    'postings.deleteFailed': 'Failed to delete posting',
    'postings.deleteDisabledActive': 'Deactivate the posting before deleting it',
    'postings.deleteDisabledApps': 'Delete all applications linked to this posting before deleting it.',
    'postings.deleteConfirmTitle': 'Delete posting?',
    'postings.deleteConfirmBody': 'This permanently removes the inactive posting. This action cannot be undone.',
    'postings.customChannel': 'Custom channel…',
    'postings.customChannelPlaceholder': 'e.g. Instagram, WhatsApp group, job fair',
    'postings.urlCopied': 'URL copied',

    // Settings
    'settings.general': 'General',
    'settings.teams': 'Teams',
    'settings.members': 'Members',
    'settings.plan': 'Plan',
    'settings.plan.hero.title': 'Active plan on this company',
    'settings.plan.hero.descSme': 'Limits below apply to this workspace. AI Credits and seats are inherited from the subscription chosen by the company owner.',
    'settings.plan.hero.descAgency': 'This is an Agency plan: caps below are per single client workspace. Adding a new company adds a seat to the subscription with prorated billing.',
    'settings.plan.managedBy': 'Subscription managed by {name}',
    'settings.plan.managedByYou': 'You manage this subscription on your profile',
    'settings.plan.priceCustom': 'Custom price',
    'settings.plan.priceFree': 'Free / 7 days trial',
    'settings.plan.perMonth': 'month',
    'settings.plan.limits.title': 'Limits per company',
    'settings.plan.limits.positions': 'Active jobs',
    'settings.plan.limits.candidates': 'Candidates AI/month',
    'settings.plan.limits.seats': 'Members (seats)',
    'settings.plan.limits.aiCredits': 'AI Credits/month',
    'settings.plan.limits.hint': 'Numbers are per workspace. Candidates AI is the volume of CVs we can process with AI screening. AI Credits cover all other AI actions: candidate chat, job position assistant, call transcription, AutoFill JD and interview reports.',
    'settings.plan.features.title': 'What this plan includes',
    'settings.plan.features.aiScreening': 'AI screening on every applied CV',
    'settings.plan.features.callTranscription': 'Interview call transcription',
    'settings.plan.features.interviewReports': 'AI-generated interview reports',
    'settings.plan.features.aiAutofill': 'Job description AI autofill',
    'settings.plan.features.publicLandingPage': 'Public posting landing page',
    'settings.plan.features.emailNotifications': 'Email notifications & lifecycle automations',
    'settings.plan.features.multiCompany': 'Multi-company management (Agency)',
    'settings.plan.features.trialCreditsHint': 'Available during trial within the 25 included AI credits',
    'settings.plan.footer.note': 'To change or upgrade your subscription, open Plan in your profile.',
    'settings.plan.cta.manage': 'Manage subscription',
    'settings.plan.cta.goToProfile': 'Open Plan in profile',
    'settings.plan.loadError': 'Unable to load the company plan. Try again in a few seconds.',
    'settings.companyInfo': 'Company Information',
    'settings.logo': 'Logo',
    'settings.upload': 'Upload',
    'settings.logoUrlPlaceholder': 'Or paste URL: https://...',
    'settings.companyName': 'Company Name',
    'settings.publicName': 'Public Name',
    'settings.description': 'Description',
    'settings.uploadLogo': 'Upload',
    'settings.replaceLogo': 'Replace',
    'settings.removeLogo': 'Remove',
    'settings.defaultOutputLanguage': 'Default AI & candidate language',
    'settings.defaultOutputLanguageHint': 'Inherited by every new position (screening, reports, emails, public posting). Existing positions keep their own setting.',
    'settings.employeeCount': 'Employee Count',
    'settings.avgSalaryMin': 'Avg. salary min',
    'settings.avgSalaryMax': 'Avg. salary max',
    'settings.currency': 'Currency',
    'settings.offices': 'Locations',
    'settings.links': 'Links',
    'settings.offices.addCta': 'Add office',
    'settings.links.addCta': 'Add link',
    'settings.offices.empty': 'No offices yet. Add one to share locations on your job posting.',
    'settings.links.empty': 'No links yet. Add your website, LinkedIn, GitHub or any social profile.',
    'settings.saveChanges': 'Save Changes',
    'settings.settingsSaved': 'Settings saved',
    'settings.deleteCompany': 'Delete Company',
    'settings.deleteCompanyDesc': 'Remove {name} and all associated data permanently.',
    'settings.addTeam': 'Add Team',
    'settings.editTeam': 'Edit Team',
    'settings.teamName': 'Name',
    'settings.teamDescription': 'Description',
    'settings.teamMembers': 'Members',
    'settings.teamSaved': 'Team saved',
    'settings.inviteMember': 'Invite Member',
    'settings.inviteDesc': 'An invitation link will be generated and sent via email. The member will complete their profile upon registration.',
    'settings.inviteSent': 'Invitation sent',
    'settings.inviteLinkCopied': 'Invite link copied to clipboard',
    'settings.editMember': 'Edit Member',
    'settings.memberUpdated': 'Member updated',
    'settings.removeMember': 'Remove Member',
    'settings.removeConfirm': 'Are you sure? This action cannot be undone.',
    'settings.memberRemoved': 'Member removed',
    'settings.filteredBy': 'Filtered by:',
    'settings.clear': 'Clear',
    'settings.sendInvite': 'Send Invite',
    'settings.copyInviteLink': 'Copy Invite Link',
    'settings.resendInvite': 'Resend invitation',
    'settings.name': 'Name',
    'settings.email': 'Email',
    // Sprint 6 (S6.A) — invite dialog enhancements
    'settings.firstName': 'First name',
    'settings.lastName': 'Last name',
    'settings.roleHint': 'Admin can manage billing + members. Write can create positions + decisions. Read is observer only.',
    'settings.typeHint': 'Internal = on payroll. External = freelance / agency / partner — kept separate for reporting.',
    'settings.role': 'Role',
    'settings.team': 'Team',
    'settings.type': 'Type',
    'settings.status': 'Status',
    'settings.joined': 'Joined',
    'settings.internal': 'Internal',
    'settings.external': 'External',
    'settings.selectTeam': 'Select team...',
    'settings.pendingInvite': 'Pending invite',
    'settings.pending': 'Pending',
    'settings.declinedInvite': 'Declined invite',
    'settings.declined': 'Declined',
    'settings.active': 'Active',
    'settings.inactive': 'Inactive',
    'settings.deleteCompanyTitle': 'Delete Company',
    'settings.deleteCompanyConfirm': 'Are you sure you want to permanently delete {name}? All positions, applications, and data will be lost forever.',
    'settings.deleteCompanySuccess': 'Company deleted',
    'settings.deleteCompanyError': 'Failed to delete company',

    // Team page
    'team.addMember': 'Add Member',
    'team.editMember': 'Edit Member',
    'team.removeMember': 'Remove Member',

    // Notifications
    'notifications.title': 'Notifications',
    'notifications.markAllRead': 'Mark all as read',
    'notifications.empty': 'No notifications yet',
    'notifications.dismissed': 'Notification dismissed',
    'notifications.deleteFailed': 'Could not delete notification',
    'notifications.loadMore': 'Load more',
    'notifications.loadingMore': 'Loading…',
    'notifications.showingCount': 'Showing {count} notifications',
    'notifications.unreadCount': '{count} unread',

    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.moreActions': 'More actions',
    'errorBoundary.title': 'Something went wrong',
    'errorBoundary.body': 'We logged the error and are looking into it.',
    'errorBoundary.reloadHint': 'Try reloading the page.',
    // v3.2 polish (21/05/26) — pay-as-you-grow confirm modal for agency add company
    'addCompany.seatConfirm.title': 'Confirm new company seat',
    'addCompany.seatConfirm.subtitle': 'Adding a company increases your Agency subscription quantity. You will be charged the prorated amount immediately.',
    'addCompany.seatConfirm.currentSeats': 'Current seats',
    'addCompany.seatConfirm.newSeats': 'New seats',
    'addCompany.seatConfirm.pricePerSeat': 'Price per seat / month',
    'addCompany.seatConfirm.proratedNow': 'Charged now (prorated)',
    'addCompany.seatConfirm.newMonthlyTotal': 'New monthly total',
    'addCompany.seatConfirm.fineprint': 'The charge is issued by Stripe at company creation. You can manage your subscription anytime from Profile → Plan.',
    'addCompany.seatConfirm.cta': 'Confirm and continue',
    // Sprint 4 (S4.C) — manual application by recruiter
    'manualApply.cta': 'Add candidate',
    'manualApply.title': 'Add candidate manually',
    'manualApply.subtitle': 'The CV runs through the same screening pipeline as a public submission (cap check, dedup, AI scoring).',
    'manualApply.aiNotice': 'This candidate did not see the public application form. As the employer, you are responsible for informing them that AI is used to assist screening, in line with our AI Transparency notice.',
    'manualApply.position': 'Position',
    'manualApply.positionPlaceholder': 'Select a position…',
    'manualApply.noActivePositions': 'No active position — activate one first',
    'manualApply.noPositionsInCompany': 'No positions yet — create one under Job Positions first',
    'manualApply.source': 'Source',
    'manualApply.sourcePlaceholder': 'Select a source…',
    'manualApply.firstName': 'First name',
    'manualApply.lastName': 'Last name',
    'manualApply.email': 'Email',
    'manualApply.phone': 'Phone',
    'manualApply.linkedin': 'LinkedIn URL',
    'manualApply.cv': 'CV (PDF)',
    'manualApply.cvHint': 'Click to upload — max 15 MB',
    'manualApply.submit': 'Submit',
    'manualApply.success': 'Candidate added — pipeline starting.',
    'manualApply.errors.positionRequired': 'Select the position the candidate is applying for.',
    'manualApply.errors.sourceRequired': 'Select the application source.',
    'manualApply.errors.noActivePosting': 'This position has no active posting. Create at least one posting before adding a candidate.',
    'manualApply.errors.noPostingForSource': 'No active posting for source "{source}". Create a posting for this channel first.',
    'manualApply.errors.firstNameRequired': 'First name is required.',
    'manualApply.errors.lastNameRequired': 'Last name is required.',
    'manualApply.errors.emailRequired': 'Email is required.',
    'manualApply.errors.emailInvalid': 'Invalid email format.',
    'manualApply.errors.cvRequired': 'Upload the candidate\'s CV.',
    'manualApply.errors.cvTooLarge': 'CV exceeds the 15 MB limit.',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.remove': 'Remove',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.actions': 'Actions',
    'common.back': 'Back',
    'common.select': 'Select...',
    'common.close': 'Close',
    'common.loading': 'Loading...',
    'common.noResults': 'No results',

    // Responsive table
    'table.columns': 'Columns',
    'table.visibleColumns': 'Visible columns',
    'table.density': 'Density',
    'table.densityCompact': 'Compact',
    'table.densityComfortable': 'Comfortable',
    'table.resetColumns': 'Reset columns',

    // Onboarding
    'onboarding.title': 'Create your company',
    'onboarding.subtitle': 'Set up your organization to start recruiting',
    'onboarding.companyName': 'Company Name',
    'onboarding.publicName': 'Public Name',
    'onboarding.publicNameHint': 'Defaults to the Company Name if left empty.',
    'onboarding.description': 'Description',
    'onboarding.descriptionHint': 'min. {min} characters',
    'onboarding.continue': 'Continue',
    'onboarding.creating': 'Creating…',
    'onboarding.joinExisting': "I'll join an existing company",
    'onboarding.successTitle': "You're all set!",
    'onboarding.successDescSkipped': 'Your account is ready! 🎉',
    'onboarding.successDescCreated': "Your company has been created. Let's start hiring.",
    'onboarding.goToDashboard': 'Go to Dashboard',

    // Application Detail
    'appDetail.tabScreening': 'AI Screening',
    'appDetail.tabResume': 'Resume',
    'appDetail.tabPresentationLetter': 'Presentation Letter',
    'appDetail.tabPortfolio': 'Portfolio',
    'appDetail.tabDocuments': 'Documents',
    'appDetail.tabInterview': 'Interview',
    'appDetail.tabJourney': 'Journey',
    'appDetail.tabHistory': 'History',
    'appDetail.tabNotes': 'Notes',
    'appDetail.notesPrivateSection': 'Private note',
    'appDetail.notesYourPrivateNote': 'Your private note',
    'appDetail.notesYourTeamNote': 'Your team note',
    'appDetail.notesYourNote': 'Your note',
    'appDetail.notesPlaceholder': 'Add a private or team-visible note about this candidate…',
    'appDetail.notesVisibilityPrivate': 'Private',
    'appDetail.notesVisibilityPrivateHint': 'Only you (and the AI assistant for you) can see this note.',
    'appDetail.notesVisibilityPublic': 'Team',
    'appDetail.notesVisibilityPublicHint': 'Visible to everyone on your team and included in AI context.',
    'appDetail.notesSave': 'Save note',
    'appDetail.notesSaveSuccess': 'Note saved',
    'appDetail.notesSaveError': 'Failed to save note',
    'appDetail.notesLoadError': 'Failed to load notes',
    'appDetail.notesEdit': 'Edit',
    'appDetail.notesCancel': 'Cancel',
    'appDetail.notesCreatedAt': 'Created {date}',
    'appDetail.notesUpdatedAt': 'Updated {date}',
    'appDetail.notesTeamSection': 'Team notes',
    'appDetail.notesUnknownAuthor': 'Team member',
    'appDetail.analyzing': 'AI is analyzing this candidate...',
    'appDetail.noReport': 'No screening report available for this candidate.',
    'appDetail.pros': 'Pros',
    'appDetail.cons': 'Cons',
    'appDetail.strengths': 'Strengths',
    'appDetail.weaknesses': 'Weaknesses',
    'appDetail.missingData': 'Missing Data',
    'appDetail.suggestedQuestions': 'Suggested Interview Questions',
    'appDetail.aiChat': 'AI Chat',
    'appDetail.scheduleInterview': 'Interview transcript',
    'appDetail.reject': 'Reject',
    'appDetail.rejectCandidate': 'Reject Candidate',
    'appDetail.rejectNote': 'Rejection note (optional)...',
    'appDetail.confirmReject': 'Reject',
    'appDetail.rejectSendEmail': 'Send the candidate a rejection email',
    'appDetail.rejectSendEmailHint': 'Polite, branded message in the candidate\'s language. Check to send when rejecting.',
    'appDetail.withdrawCandidate': 'Withdraw candidate',
    'appDetail.withdrawConfirm': 'This will mark the application as withdrawn. You can reactivate the candidate later from the application detail page if needed.',
    'appDetail.withdrawNote': 'Withdrawal note (optional)...',
    'appDetail.confirmWithdraw': 'Withdraw',
    'appDetail.viewResume': 'View Resume',
    'appDetail.downloadResume': 'Download Resume',
    'appDetail.noResume': 'No resume file available for preview',
    'appDetail.viewPresentationLetter': 'View letter',
    'appDetail.downloadPresentationLetter': 'Download letter',
    'appDetail.noPresentationLetter': 'No presentation letter file available for preview',
    'appDetail.presentationLetterContent': 'Extracted content',
    'appDetail.viewPortfolio': 'View portfolio',
    'appDetail.downloadPortfolio': 'Download portfolio',
    'appDetail.noPortfolio': 'No portfolio file available for preview',
    'appDetail.processPortfolio': 'Process portfolio',
    'appDetail.processPortfolioTodo': 'Portfolio AI processing is not available yet',
    'appDetail.documents': 'Application Documents',
    'appDetail.download': 'Download',
    'appDetail.appliedOn': 'Applied',
    'appDetail.analyzed': 'Analyzed',
    'appDetail.consentTitle': 'Candidate consents (public application form)',
    'appDetail.consentGdpr': 'GDPR — personal data processing',
    'appDetail.consentAi': 'AI — application analysis',
    'appDetail.consentGranted': 'Accepted',
    'appDetail.consentNotRecorded': 'Not recorded',
    'appDetail.consentNotRecordedHint': 'Legacy application or manual recruiter upload',
    'appDetail.aiAuditTitle': 'AI processing audit (Art. 12)',
    'appDetail.aiAuditToggle': 'Technical details',
    'appDetail.aiAuditModel': 'Model',
    'appDetail.aiAuditProvider': 'Provider',
    'appDetail.aiAuditPromptHash': 'Prompt hash',
    'appDetail.aiAuditDocumentHash': 'Resume hash',
    'appDetail.aiAuditProcessedDocuments': 'Processed documents',
    'appDetail.aiAuditGeneratedAt': 'Generated at',
    'appDetail.aiAuditProfile': 'Profile',
    'appDetail.basedOn': 'Based on',
    'appDetail.scoreHistory': 'Score history',
    'appDetail.screeningHistory': 'Screening History',
    'appDetail.versions': '{count} versions',
    'appDetail.contact': 'Contact',
    'appDetail.contactFromForm': 'Contact — application form',
    'appDetail.contactFromResume': 'Contact — from resume',
    'appDetail.email': 'Email',
    'appDetail.phone': 'Phone',
    'appDetail.phoneFromForm': 'Phone (application form)',
    'appDetail.phoneFromResume': 'Phone (resume)',
    'appDetail.portfolio': 'Portfolio',
    'appDetail.locationFromResume': 'Location — from resume',
    'appDetail.currentPosition': 'Current Position',
    'appDetail.experience': 'Experience',
    'appDetail.skills': 'Skills',
    'appDetail.education': 'Education',
    'appDetail.languages': 'Languages',
    'appDetail.present': 'Present',
    'appDetail.structuredPending': 'Structured data will be available after screening completes',
    'appDetail.rawResume': 'Raw Resume Content',
    'appDetail.resumePending': 'Resume content will be available after screening completes',
    'appDetail.noInterviews': 'No interviews scheduled',
    'appDetail.noInterviewsHint': 'Use the Actions menu to schedule an interview transcript for this candidate',
    'appDetail.transcript': 'Transcript',
    'appDetail.aiReport': 'AI Report',
    'appDetail.candidate': 'Candidate',
    'appDetail.creator': 'Creator',
    'appDetail.requestApproval': 'Request Approval',
    'appDetail.makeOffer': 'Make Offer',
    'appDetail.markHired': 'Mark as Hired',
    'appDetail.comingSoon': 'Soon',
    'appDetail.comingSoonMsg': 'This feature is coming soon! We\'re working on bringing {label} to your workflow.',
    'appDetail.aiAssistant': 'AI Assistant',
    'appDetail.suggestedPrompts': 'Suggested prompts:',
    'appDetail.askCandidate': 'Ask about this candidate...',
    'appDetail.candidateRejected': 'Candidate rejected',
    'appDetail.applications': 'Applications',
    'appDetail.notFoundTitle': 'Application not found',
    'appDetail.notFoundBody': 'This candidate may have been withdrawn or the link is no longer valid. Try opening it again from the Applications list.',
    'appDetail.backToList': 'Back to Applications',
    'appDetail.rejectFailed': 'Failed to reject candidate',
    'appDetail.hireCandidate': 'Mark as Hired',
    'appDetail.hireNote': 'Hire note (optional)...',
    'appDetail.confirmHire': 'Mark as Hired',
    'appDetail.candidateHired': 'Candidate marked as hired',
    'appDetail.hireFailed': 'Failed to mark candidate as hired',
    'appDetail.resumeDownloadFailed': 'Failed to download resume',
    'appDetail.linkCopied': 'Link copied',
    'appDetail.copied': 'Copied!',
    'appDetail.actionRequired': 'Action required',
    'appDetail.yearsExp': '{years} years of experience',
    'appDetail.journeyApplied': 'Applied',
    'appDetail.journeyScreening': 'AI Screening',
    'appDetail.journeyAwaiting': 'Awaiting Decision',
    'appDetail.journeyInterview': 'Interview',
    'appDetail.journeyHired': 'Hired',
    'appDetail.journeyRejected': 'Rejected',
    'appDetail.journeyAppliedInfo': 'Applied via {channel} on {date}',
    'appDetail.journeyScreeningScore': 'AI Score: {score}/100',
    'appDetail.journeyPending': 'Pending...',
    'appDetail.journeyWaiting': 'Waiting for recruiter action',
    'appDetail.journeyScheduled': 'Scheduled for {date}',
    'appDetail.journeyInterviewCancelled': 'Was {date} — cancelled',
    'appDetail.addParticipantHint': 'Add at least 1 company member',
    'appDetail.participants': 'Participants',
    'appDetail.interviewScheduled': 'Interview Scheduled!',
    'appDetail.viewInCalendar': 'View in Google Calendar',
    'appDetail.withdraw': 'Withdraw',
    'appDetail.candidateWithdrawn': 'Candidate withdrawn',
    'appDetail.reactivate': 'Reactivate',
    'appDetail.reactivateCandidate': 'Reactivate candidate',
    'appDetail.reactivateConfirm': 'This will move the candidate back to Awaiting Decision. Previous screening and interview history are kept. You can reject, hire, or run new pipelines again.',
    'appDetail.reactivateNote': 'Note (optional)',
    'appDetail.reactivateNotePlaceholder': 'Reason for reactivation…',
    'appDetail.confirmReactivate': 'Reactivate',
    'appDetail.candidateReactivated': 'Candidate reactivated',
    'appDetail.reactivateFailed': 'Failed to reactivate candidate',
    'appDetail.withdrawFailed': 'Failed to withdraw',
    'appDetail.stateTransitions': 'State Transitions',
    'appDetail.noHistory': 'No history yet',
    'appDetail.historyInterviewSchedulingStarted': 'Interview scheduling started',
    'appDetail.historyInterviewScheduledFor': 'Interview scheduled for {date}',
    'appDetail.historyTranscriptionBotScheduled': 'Transcription notetaker scheduled to join at {joinAt}',
    'appDetail.historyTranscriptionBotScheduledLate':
      'Transcription notetaker: meeting slot {meeting}, effective join {joinAt} (scheduling finished after the slot — bot joins as soon as possible)',
    'appDetail.historyTranscriptionBotCancelled': 'Transcription notetaker cancelled{suffix}',
    'appDetail.historyInterviewRescheduledFromTo': 'Interview rescheduled: {previousDate} → {date}',
    'appDetail.historyInterviewCalendarUpdated': 'Calendar updated — interview on {date} (not the live meeting yet)',
    'appDetail.historyInterviewCalendarUpdatedShort': 'Calendar & invites step finished',
    'appDetail.historyInterviewPipelineCompleted': 'Interview scheduling pipeline completed',
    'appDetail.historyInterviewCancelledEvent': 'Interview cancelled',
    'appDetail.executorScreeningDocuments': 'Resume & documents processed',
    'appDetail.executorGenerateReport': 'AI report generated',
    'appDetail.executorSendEmailReport': 'Report email sent',
    'appDetail.docTypeResume': 'Resume',
    'appDetail.docTypePresentationLetter': 'Presentation letter',
    'appDetail.docTypePortfolio': 'Portfolio',
    'appDetail.docTypeResumeContent': 'Resume content',
    'appDetail.docTypeResumeStructured': 'Structured resume',
    'appDetail.docTypePresentationLetterContent': 'Letter content',
    'appDetail.docTypeScreeningReport': 'Screening report',
    'appDetail.historyDetailUploaded': 'Uploaded: {docs}',
    'appDetail.historyDetailGenerated': 'Generated: {docs}',
    'appDetail.historyDetailBasedOn': 'Based on: {docs}',
    'appDetail.historyDetailScore': 'Score {score}',
    'appDetail.executorCreateCalendar': 'Google Calendar event & Meet',
    'appDetail.executorIngestTranscript': 'Transcript loaded from provider',
    'appDetail.executorGenerateInterviewReport': 'AI interview report generated',
    'appDetail.executorSendInterviewInvite': 'Custom email to candidate (if enabled)',
    'appDetail.pipelineDebug': 'Pipeline Debug Detail',

    // Interview Modal
    'interviewModal.title': 'Schedule Interview',
    'interviewModal.titleTranscript': 'Schedule interview transcript',
    'interviewModal.dateTime': 'Date & Time *',
    'interviewModal.dateLabel': 'Date *',
    'interviewModal.timeLabel': 'Time *',
    'interviewModal.endTimeLabel': 'End *',
    'interviewModal.derivedDurationHint': 'Duration: {minutes} min (from start and end)',
    'interviewModal.pickDate': 'Pick a date',
    'interviewModal.participants': 'Participants *',
    'interviewModal.searchMembers': 'Search team members...',
    'interviewModal.addHint': 'Add at least 1 company member',
    'interviewModal.duration': 'Duration *',
    'interviewModal.type': 'Type *',
    'interviewModal.notes': 'Notes',
    'interviewModal.notesPlaceholder': 'Add notes for the interview (e.g., focus areas, preparation instructions)',
    'interviewModal.sendEmail': 'Send custom email to candidate',
    'interviewModal.sendEmailDesc': 'Personalized email invitation in addition to Calendar invite',
    'interviewModal.createGoogleCalendar': 'Create Google Calendar event',
    'interviewModal.createGoogleCalendarDesc': 'When off, paste your meeting link (Zoom, Google Meet, Teams, etc.) — used by the transcript bot / Recall.',
    'interviewModal.googleCalendarComingSoon': 'Creating events in Google Calendar from here is not available yet — use an external link above for now.',
    'interviewModal.externalMeetingUrl': 'External meeting link',
    'interviewModal.externalMeetingUrlDesc':
      'Paste the video call link from any platform (Google Meet, Microsoft Teams, Zoom, Outlook, etc.). The HiroMatch bot will join the call at the scheduled time — the meeting host must admit it — and generate a transcript after the interview.',
    'interviewModal.externalMeetingUrlPlaceholder': 'https://…',
    'interviewModal.enableTranscription': 'Meeting transcript',
    'interviewModal.enableTranscriptionDesc': 'Schedule a notetaker to join the meeting and produce a transcript after the call. Turn off when editing to cancel the scheduled notetaker.',
    'interviewModal.schedule': 'Schedule interview transcript',
    'interviewModal.scheduling': 'Scheduling transcript...',
    'interviewModal.scheduled': 'Interview transcript scheduled!',
    'interviewModal.viewCalendar': 'View in Google Calendar',
    'interviewModal.candidate': 'Candidate',
    'interviewModal.creator': 'Creator',
    'interviewModal.failed': 'Failed to schedule interview transcript',
    'interviewModal.blocked.trialTitle': 'Call transcription not included on Trial',
    'interviewModal.blocked.trialBody':
      'Interview scheduling with AI transcript is available from Starter onwards. Upgrade your plan to schedule interviews and generate call transcripts.',
    'interviewModal.blocked.creditsTitle': 'Not enough AI credits',
    'interviewModal.blocked.creditsBody':
      'You have used all AI credits for this billing period. Upgrade your plan or wait for the next reset to schedule new interview transcripts.',
    'interviewModal.blocked.upgradeCta': 'Upgrade plan',
    'interviewModal.videoCall': 'Video Call',
    'interviewModal.phoneCall': 'Phone Call',
    'interviewModal.inPerson': 'In-person',
    'interviewModal.titleEdit': 'Edit Interview',
    'interviewModal.update': 'Save changes',
    'interviewModal.updating': 'Updating...',
    'interviewModal.updated': 'Interview updated!',
    'appDetail.interviewColWhen': 'When',
    'appDetail.interviewColParticipants': 'Participants',
    'appDetail.interviewColLinks': 'Meet / Calendar',
    'appDetail.interviewColNumber': '#',
    'appDetail.interviewColCreatedAt': 'Created',
    'appDetail.interviewColCreatedBy': 'Created by',
    'appDetail.interviewEdit': 'Edit',
    'appDetail.interviewCancel': 'Cancel interview',
    'appDetail.interviewCancelled': 'Interview cancelled',
    'appDetail.interviewCancelFailed': 'Could not cancel interview',
    'appDetail.cancelInterviewTitle': 'Cancel interview',
    'appDetail.cancelInterviewDesc':
      'Attendees can be notified via Google Calendar and email. You can add an optional message for the cancellation email.',
    'appDetail.cancelInterviewMessageLabel': 'Message to attendees (optional)',
    'appDetail.cancelInterviewMessagePlaceholder':
      'Short note included in the cancellation email (for example reason or next steps).',
    'appDetail.cancelInterviewNotifyLabel': 'How to notify',
    'appDetail.cancelInterviewSendGoogle': 'Send update through Google Calendar (cancelled event)',
    'appDetail.cancelInterviewSendEmail': 'Send cancellation email to attendees',
    'appDetail.cancelInterviewChannelsRequired': 'Select at least one notification option.',
    'appDetail.cancelRecordingOnlyHint': 'This is a transcription-only recording — no calendar event will be cancelled and no emails will be sent.',
    'appDetail.cancellingInterview': 'Cancelling…',
    'appDetail.interviewExpandHint': 'Transcript & report',
    'appDetail.noTranscript': 'No transcript yet.',
    'appDetail.interviewNoAiReport': 'No AI report yet.',
    'appDetail.interviewRegenerateReport': 'Regenerate report',
    'appDetail.interviewReportGenerating': 'Generating…',
    'appDetail.interviewReportFailedHint': 'Report generation failed. You can try again.',
    'appDetail.interviewRegenerateReportSuccess': 'Interview report generation started.',
    'appDetail.interviewRegenerateReportFailed': 'Could not regenerate the interview report.',
    'appDetail.interviewColStatus': 'Status',
    'appDetail.interviewColTranscript': 'Transcript',
    'appDetail.interviewColRecallBot': 'Notetaker',
    'appDetail.interviewJoinBot': 'Join now',
    'appDetail.interviewJoinBotSuccess': 'Notetaker dispatched — it should join the meeting shortly.',
    'appDetail.interviewJoinBotFailed': 'Could not dispatch the notetaker. Check the meeting link and Recall configuration.',
    'appDetail.interviewTranscriptOn': 'On',
    'appDetail.interviewTranscriptOff': 'Off',
    'appDetail.interviewColDetails': 'Transcript & report',
    'appDetail.interviewNotesLabel': 'Notes',
    'appDetail.interviewStatus.scheduled': 'Scheduled',
    'appDetail.interviewStatus.updated': 'Updated',
    'appDetail.interviewStatus.completed': 'Completed',
    'appDetail.interviewStatus.cancelled': 'Cancelled',

    // Login
    'login.title': 'Sign in to your account',
    'login.subtitle': 'Enter your credentials to continue',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.submit': 'Sign in',
    'login.submitting': 'Signing in...',
    'login.google': 'Sign in with Google',
    'login.googleFailed': 'Google sign-in failed',
    'login.forgot': 'Forgot password?',
    'login.noAccount': 'Don\'t have an account?',
    'login.signup': 'Sign up',
    'login.error': 'Invalid credentials. Please try again.',
    'login.invalidEmail': 'Enter a valid email address.',
    'login.accountDisabled': 'This account is currently inactive. Please contact support.',
    'login.or': 'or',
    'login.forgotTitle': 'Reset your password',
    'login.forgotSubtitle': 'Enter your email and we\'ll send you a link to choose a new password.',
    'login.forgotSubmit': 'Send reset link',
    'login.forgotSubmitting': 'Sending…',
    'login.forgotSuccessTitle': 'Check your inbox',
    'login.forgotSuccessBody': 'If an account exists for this email, you\'ll receive a link to reset your password shortly.',
    'login.forgotError': 'Something went wrong. Please try again.',
    'login.backToLogin': 'Back to sign in',
    'login.passwordResetDone': 'Your password has been updated. You can sign in now.',

    // Signup
    'signup.title': 'Create your account',
    'signup.subtitle': 'Get started with hiromatch',
    'signup.name': 'Display Name',
    'signup.namePlaceholder': 'Your name',
    'signup.email': 'Email',
    'signup.password': 'Password',
    'signup.confirm': 'Confirm Password',
    'signup.submit': 'Create account',
    'signup.submitting': 'Creating account...',
    'signup.google': 'Sign up with Google',
    'signup.googleFailed': 'Google sign-up failed',
    'signup.hasAccount': 'Already have an account?',
    'signup.login': 'Sign in',
    'signup.passwordMismatch': 'Passwords do not match',
    'signup.error': 'Signup failed. Please try again.',
    'signup.accountType.title': 'What type of account are you creating?',
    'signup.accountType.sme.title': "I'm a company",
    'signup.accountType.sme.desc': 'Manage one company with your team',
    'signup.accountType.sme.price': 'from €99/month',
    'signup.accountType.sme.cta': 'Select',
    'signup.accountType.agency.title': "I'm an agency",
    'signup.accountType.agency.desc': 'Manage multiple clients with a recruiter team',
    'signup.accountType.agency.price': 'from €149/recruiter/month',
    'signup.accountType.agency.cta': 'Select',
    'signup.agencyTier.title': 'Which agency plan?',
    'signup.agencyTier.starter': 'Agency Starter',
    'signup.agencyTier.starterDesc': '€149/recruiter/month · 5 clients, 15 jobs/recruiter',
    'signup.agencyTier.pro': 'Agency Professional',
    'signup.agencyTier.proDesc': '€249/recruiter/month · 20 clients, 50 jobs/recruiter, transcripts',
    'signup.agencyTier.cadence': 'Billing',
    'signup.agencyTier.monthly': 'Monthly',
    'signup.agencyTier.annual': 'Annual (-10%)',
    'signup.agencyTier.continue': 'Continue',
    'signup.agencyTier.back': 'Back',
    'signup.agencyTier.contactSupport': 'Contact support',
    'signup.agencyTier.error': 'Could not enable agency account. Please try again.',
    'subscription.agency.title': 'Agency Subscription',
    'subscription.agency.currentPlan': 'Current plan',
    'subscription.agency.calculator.title': 'Price calculator',
    'subscription.agency.calculator.recruiters': 'Recruiters',
    'subscription.agency.calculator.basePrice': 'Base price per recruiter',
    'subscription.agency.calculator.volumeDiscount': 'Volume discount',
    'subscription.agency.calculator.annualDiscount': 'Annual discount',
    'subscription.agency.calculator.effective': 'Effective per recruiter',
    'subscription.agency.calculator.total': 'MONTHLY TOTAL',
    'subscription.agency.calculator.nextTier': 'Add {n} recruiter(s) to unlock -{p}%',
    'subscription.agency.cta.upgrade': 'Upgrade',
    'subscription.agency.cta.downgrade': 'Downgrade',
    'subscription.agency.cta.current': 'Current plan',
    'subscription.agency.cta.contact': 'Contact us',
    'dashboard.agency.tier.starter': 'Agency Starter',
    'dashboard.agency.tier.pro': 'Agency Professional',
    'dashboard.agency.clients': '{used}/{cap} clients',
    'dashboard.agency.recruiters': '{n} recruiter(s)',
    'dashboard.agency.totalMonth': '€{amount}/month',
    'dashboard.agency.manage': 'Manage subscription',
    'dashboard.agency.jobsCap': 'Active jobs (cross-client)',
    'dashboard.agency.clientsCap': 'Client companies',
    'dashboard.agency.recruitersWidget': 'Active recruiters',
    'dashboard.agency.transcripts': 'Transcripts',
    'onboarding.upgradeInsteadOfCreate.agencyCap.title': 'You\'ve reached the maximum',
    'onboarding.upgradeInsteadOfCreate.agencyCap.body': "You've used {used}/{cap} clients on your plan. Upgrade to Agency Professional to manage up to 20.",
    'onboarding.upgradeInsteadOfCreate.agencyCap.cta': 'Upgrade to Agency Pro',

    // Public Posting
    'apply.loading': 'Loading position...',
    'apply.notFound': 'Position not found',
    'apply.notFoundDesc': 'This position does not exist or the link is invalid.',
    'apply.closed': 'Position closed',
    'apply.closedDesc': 'This position is no longer accepting applications.',
    'apply.success': 'Application submitted!',
    'apply.thankYou': 'Thank you',
    'apply.received': 'Your application for {position} at {company} has been received successfully.',
    'apply.contact': 'We will contact you at {email} if your profile matches the position.',
    'apply.goodLuck': 'Good luck! 🍀',
    'apply.about': 'About {company}',
    'apply.employees': '{count} employees',
    'apply.submitTitle': 'Submit your application',
    'apply.firstName': 'First Name *',
    'apply.lastName': 'Last Name *',
    'apply.email': 'Email *',
    'apply.phone': 'Phone',
    'apply.linkedIn': 'LinkedIn',
    'apply.resume': 'Resume *',
    'apply.dragResume': 'Drag your resume here or click to browse',
    'apply.pdfMax': 'PDF, max 10MB',
    'apply.submit': 'Submit Application',
    'apply.submitting': 'Submitting...',
    'apply.gdprConsentRequired': 'You must accept personal data processing to submit your application.',
    'apply.aiConsentRequired': 'You must acknowledge AI processing to submit your application.',
    'apply.poweredBy': 'Powered by',
    'apply.workMode.On-site': 'On-site',
    'apply.workMode.Hybrid': 'Hybrid',
    'apply.workMode.Remote': 'Remote',
    'apply.contractType.Full-time': 'Full-time',
    'apply.contractType.Part-time': 'Part-time',
    'apply.contractType.Freelance': 'Freelance',
    'apply.contractType.Internship': 'Internship',
    'apply.contractType.Temporary': 'Temporary',
    'apply.seniority.Junior': 'Junior',
    'apply.seniority.Junior-Mid': 'Junior-Mid',
    'apply.seniority.Mid': 'Mid',
    'apply.seniority.Mid-Senior': 'Mid-Senior',
    'apply.seniority.Senior': 'Senior',
    'apply.firstNameRequired': 'First name is required',
    'apply.lastNameRequired': 'Last name is required',
    'apply.emailRequired': 'Email is required',
    'apply.emailInvalid': 'Invalid email address',
    'apply.phoneRequired': 'Phone number is required',
    'apply.phoneInvalid': 'Invalid phone number.',
    'apply.linkedInRequired': 'LinkedIn profile is required',
    'apply.linkedInInvalid': 'Invalid LinkedIn URL — paste your full profile URL or your vanity name (e.g. marco-rossi).',
    'apply.resumeRequired': 'Resume is required',
    'apply.documentRequired': '{label} is required',
    'apply.documentInvalid': 'Only PDF files up to 10 MB are accepted for {label}',
    'apply.rateLimit': 'You have reached the application limit. Please try again later.',
    'apply.duplicate': 'You have already submitted an application for this position.',
    'apply.validationError': 'Invalid data. Please check all fields and try again.',
    'apply.genericError': 'An error occurred. Please try again later.',
    'apply.networkError': 'Network error. Check your connection and try again.',

    // Billing
    'billing.aiNotEnabled': 'AI Assistant is not available on your current plan. [Upgrade your plan](/profile?tab=billing) to enable it.',
    'aiChat.plan_required': '🔒 The AI assistant is included from the **Starter** plan onwards. [Upgrade your plan](/profile?tab=billing) to unlock it.',
    'aiChat.balance_exhausted': '💳 You have used all the AI credits included in your plan this month. [Upgrade](/profile?tab=billing) or wait for the next billing cycle.',
    'aiChat.demo_unavailable': 'AI features are not available in demo mode.',
    'aiChat.generic_error': 'Something went wrong. Please try again.',
    // FloatingAiChat UI chrome
    'aiChat.title': 'AI Assistant',
    'aiChat.newChat': 'New chat',
    'aiChat.expand': 'Expand',
    'aiChat.minimize': 'Minimize',
    'aiChat.close': 'Close AI Assistant',
    'aiChat.open': 'Open AI Assistant',
    'aiChat.send': 'Send message',
    'aiChat.tryAsking': 'Try asking:',
    'aiChat.placeholder.default': 'Ask AI Assistant...',
    // Applications page chat
    'aiChat.applications.contextLabel': 'Context: Applications, Scores, Positions',
    'aiChat.applications.placeholder': 'Ask about applications...',
    'aiChat.applications.prompt1': 'Summarize the current application pipeline',
    'aiChat.applications.prompt2': 'Which candidates have the highest scores?',
    'aiChat.applications.prompt3': 'Show applications needing attention',
    'aiChat.applications.prompt4': 'Compare applicants for a specific position',
    // Position detail page chat
    'aiChat.positionDetail.contextLabel': 'Context: {title} — candidates, interviews & job description',
    'aiChat.positionDetail.placeholder': 'Ask about candidates, interviews or this position...',
    'aiChat.positionDetail.prompt1': 'How many candidates do I have and what is the status breakdown?',
    'aiChat.positionDetail.prompt2': 'Who are the top 3 candidates by score?',
    'aiChat.positionDetail.prompt3': 'Compare the interviews of the candidates who already had one',
    'aiChat.positionDetail.prompt4': 'Suggest improvements for this job description',
    // Application detail page chat (candidate review)
    'aiChat.candidate.contextLabel': 'Context: resume, presentation letter, portfolio, screening report, interviews, structured data, job position',
    'aiChat.candidate.prompt1': "What are this candidate's main strengths?",
    'aiChat.candidate.prompt2': 'Summarize what was discussed in the interviews',
    'aiChat.candidate.prompt3': 'Suggest follow-up interview questions based on what we already discussed',
    'trial.banner.info': "You're on the HiroMatch trial — {days} days remaining",
    'trial.banner.warning': 'Only {days} days left in your trial',
    'trial.banner.urgent_today': '⚠️ Your trial ends today',
    'trial.banner.urgent_tomorrow': '⚠️ Your trial ends tomorrow',
    'trial.banner.cta_choose_plan': 'Choose a plan',
    'trial.banner.cta_upgrade_now': 'Upgrade now',
    'trial.banner.cta_unlock': 'Unlock HiroMatch',
    'trial.expired.title': 'Trial expired',
    'trial.expired.body': 'Your 7-day trial period ended on {date}.',
    'trial.expired.subtitle': 'To keep using HiroMatch, choose a plan:',
    'trial.expired.cta': 'See plans',
    'trial.expired.banner.message': 'Your trial has ended. You can browse your data — choose a plan to take action again.',
    'trial.expired.banner.withDate': 'Your trial ended on {date}. Browse your data — choose a plan to take action again.',
    'trial.expired.support': 'Have questions? Contact us at',
    'trial.upgradeWall.title': 'Upgrade required',
    'trial.upgradeWall.trialExpiredBody': 'Your trial has ended. To continue using this feature, choose a plan.',
    'trial.upgradeWall.cta': 'Go to upgrade',
    // v3.2 polish (21/05/26) — reason-aware upgrade wall copy
    'upgradeWall.seats.title': 'You\'ve reached the maximum',
    'upgradeWall.seats.body': 'Your plan reached the maximum number of users for this workspace. Upgrade to invite more members.',
    'upgradeWall.positions.title': 'You\'ve reached the maximum',
    'upgradeWall.positions.body': 'You\'ve reached the maximum number of active job positions on this plan. Upgrade to open more roles.',
    'upgradeWall.candidates.title': 'You\'ve reached the maximum',
    'upgradeWall.candidates.body': 'New applications are still received, but won\'t be processed by AI until you upgrade or the monthly counter resets.',
    'upgradeWall.structural.title': 'You\'ve reached the maximum',
    'upgradeWall.structural.body': 'You\'ve reached a limit on your current plan. Upgrade to continue.',
    'upgradeWall.balance.title': 'AI Credits exhausted',
    'upgradeWall.balance.body': 'You\'ve used all AI Credits for this month. Candidates keep arriving and being processed. Upgrade your plan or buy a top-up to unlock chat, transcripts, autofill and interview reports.',
    'upgradeWall.planRequired.title': 'Higher plan required',
    'upgradeWall.planRequired.body': 'This feature is available on Starter and higher. Upgrade your plan to unlock it.',

    // Subscription (pricing v3)
    'subscription.title': 'Subscription',
    'subscription.currentPlan': 'Current plan',
    'subscription.toggle.monthly': 'Monthly',
    'subscription.toggle.annual': 'Annual',
    'subscription.toggle.annualSave': 'Save {percent}%',
    'subscription.priceFree': 'Free',
    'subscription.perMonth': '/month',
    'subscription.perYearBilled': 'Billed €{total}/year',
    'subscription.card.cta_upgrade': 'Upgrade',
    'subscription.card.cta_downgrade': 'Downgrade',
    'subscription.card.cta_current': 'Current plan',
    'subscription.card.cta_trial_active': 'Trial active',
    'subscription.card.cta_trial_included': 'Free trial included',
    'subscription.agency.loadingContext': 'Loading your agency subscription… If this takes more than a few seconds, try refreshing the page.',
    'subscription.card.recommended': '⭐ Recommended',
    'subscription.card.priceTrial': 'Free',
    'subscription.card.priceTrialPeriod': '/ {days} days',
    'subscription.card.trialBadge': '{days} days free',
    'subscription.card.activeBadge': 'Active plan',
    'subscription.limits.seats': '{n} seats',
    'subscription.limits.positions': '{n} active jobs',
    'subscription.limits.candidates': '{n} AI candidates / mo',
    'subscription.limits.candidatesNoMonth': '{n} AI candidates',
    // v3.5: rinominato — pool singolo "AI Credits"
    'subscription.limits.aiCompany': '{n} AI Credits',
    'subscription.limits.aiCompanyMonthly': '{n} AI Credits / mo',
    // @deprecated v3.5
    'subscription.limits.aiUser': '{n} AI credits (personal)',
    'subscription.limits.aiCreditsTotal': '{n} total AI credits',
    'subscription.limits.aiCreditsTotalMonthly': '{n} total AI credits / mo',
    'subscription.limits.transcripts': '{n} transcripts',
    'subscription.compare.title': 'Compare features',
    'subscription.compare.feature': 'Feature',
    'subscription.compare.row.positions': 'Job Positions',
    'subscription.compare.row.candidates': 'AI candidates / mo',
    'subscription.compare.row.aiCompany': 'AI Credits / mo',
    // @deprecated v3.5
    'subscription.compare.row.aiUser': 'Personal AI credits',
    'subscription.compare.row.transcripts': 'Transcripts / month',
    'subscription.compare.row.seats': 'Seats',
    'subscription.compare.row.callTranscription': 'Call transcription',
    'subscription.compare.creditsExplainer.title': "What's the difference between AI candidates and AI credits?",
    'subscription.compare.creditsExplainer.candidatesAi': '“AI candidates” is the monthly volume of candidates we can process with CV screening, interview report generation, call and report transcription, and Job Position AutoFill.',
    'subscription.compare.creditsExplainer.aiCredits': '"AI Credits" cover all AI-driven actions other than CV screening: candidate chat, Job Position assistant, call transcription, AutoFill JD, interview reports.',
    // v3.5: pool unico "AI Credits"
    'subscription.poolLabel.aiCompany': 'AI Credits',
    // @deprecated v3.5
    'subscription.poolLabel.aiUser': 'AI credits (user)',
    'subscription.poolLabel.candidates': 'Candidates',
    'subscription.poolLabel.transcripts': 'Transcripts',
    'subscription.poolLabel.seats': 'Seats',
    'subscription.poolLabel.positions': 'Job Positions',
    'subscription.poolLabel.ai_company': 'AI credits (company)',
    'subscription.poolLabel.ai_user': 'AI credits (user)',
    'subscription.poolLabel.recruiter_seats': 'Recruiter seats',
    'subscription.poolLabel.agency_clients': 'Agency clients',
    // ─── Pricing v3.2 (21/05/26) — Agency tier display & chooser ───────────
    'subscription.tier.freemium_trial': 'Trial',
    'subscription.tier.sme_starter': 'Starter',
    'subscription.tier.sme_growth': 'Growth',
    'subscription.tier.sme_professional': 'Professional',
    'subscription.tier.agency_starter': 'Agency Starter',
    'subscription.tier.agency_medium': 'Agency Medium',
    'subscription.tier.agency_professional': 'Agency Professional',
    'subscription.tier.agency_enterprise': 'Agency Enterprise',
    'subscription.agency.pricePerCompany': '€{price} × {n} companies = €{total}/mo',
    'subscription.agency.pricePerCompanyShort': '€{price}/company/mo',
    'subscription.agency.companiesUsedOf': '{used} of {cap} companies',
    'subscription.agency.companiesCap': 'Up to {n} managed companies',
    'subscription.agency.companiesUnlimited': 'Unlimited managed companies',
    'subscription.agency.chooserTitle': 'How many client companies do you manage?',
    'subscription.agency.chooserSubtitle': 'Pre-buy company seats for your subscription tier. You can change the number later from the Stripe portal.',
    'subscription.agency.chooserConfirm': 'Continue to checkout',
    'subscription.agency.chooserCancel': 'Cancel',
    'subscription.agency.chooserTierBand': 'Tier {tier}: {min}–{max} companies',
    'subscription.agency.chooserTotalPreview': 'You will pay €{total}/month for {n} companies',
    'subscription.agency.contactSales': 'Contact sales',
    'subscription.agency.enterpriseSalesPrompt': 'For 20+ managed companies, our sales team will tailor a custom Enterprise plan.',
    'subscription.feature.q3_2026': 'Coming Soon',
    'subscription.feature.q3_2026_tooltip': 'In development — coming soon',
    'subscription.feature.comingSoon': 'Coming Soon',
    'subscription.compare.row.crossClientMatching': 'Cross-client AI matching',
    'subscription.compare.row.centralizedCandidateDb': 'Centralized candidate DB',
    'subscription.compare.row.accountManager': 'Account manager',
    'subscription.compare.row.teamDedicato': 'Dedicated team',
    // ─── v3.2 Agency compare table rows ───────────────────────────────────
    'subscription.agency.compare.title': 'Compare Agency tiers',
    'subscription.agency.compare.row.companies': 'Managed companies',
    'subscription.agency.compare.row.pricePerCompany': 'Price / company / month',
    'subscription.agency.compare.row.jobsPerCompany': 'Active jobs / company',
    'subscription.agency.compare.row.candidatesPerCompany': 'AI candidates / company / mo',
    // v3.2 (21/05 19:30 ROLLBACK 19:28) — seats & AI Credits are PER COMPANY
    // (inherited from SME tier). Subscription-wide enforcement reverted by
    // CEO for competitiveness vs SME tiers.
    'subscription.agency.compare.row.seatsPerCompany': 'Seats / company',
    'subscription.agency.compare.row.aiCreditsPerCompany': 'AI Credits / company / mo',
    // v3.2 polish (21/05/26) — header card summary grid (4 cells)
    'subscription.agency.summary.jobs': '{n} jobs',
    'subscription.agency.summary.candidates': '{n} AI candidates',
    'subscription.agency.summary.seats': '{n} seats',
    'subscription.agency.summary.aiCredits': '{n} AI Credits',
    'subscription.agency.summary.perCompany': 'per company',
    'subscription.agency.summary.perCompanyMonth': 'per company / mo',
    // Legacy keys kept for read-time backward compat — no longer rendered.
    'subscription.agency.compare.row.seatsTotal': 'Total subscription seats',
    'subscription.agency.compare.row.aiCreditsTotal': 'Total AI Credits / mo',
    'subscription.agency.compare.row.aiUserPerCompany': 'AI Credits / user / mo',
    'subscription.agency.compare.row.support': 'Support channel',
    'subscription.agency.compare.row.centralizedDb': 'Centralized candidate DB',
    'subscription.agency.compare.row.crossClientAi': 'Cross-client AI matching',
    'subscription.agency.compare.row.accountManager': 'Dedicated account manager',
    'subscription.agency.compare.row.whiteLabel': 'White-label',
    'subscription.agency.compare.row.customAi': 'Custom AI models',
    'subscription.agency.compare.row.csm': 'Dedicated CSM team',
    'subscription.agency.support.email': 'Priority email',
    'subscription.agency.support.chat': 'Dedicated chat',
    'subscription.agency.support.accountManager': 'Account manager',
    'subscription.agency.support.csmTeam': 'Dedicated CSM team',
    'subscription.agency.support.custom': 'Custom',
    'subscription.agency.enterprise.description': 'For large headhunting agencies managing 20+ client companies. Custom contract, dedicated team, white-label, AI models tailored to your clients.',
    'subscription.agency.enterprise.contactCta': 'Contact us',
    'subscription.usage.title': 'Usage this month',
    'subscription.usage.unlimited': 'Unlimited',
    'subscription.usage.limitReached': 'Limit reached — upgrade for more capacity',
    'subscription.usage.empty': 'No usage data available.',
    'subscription.portal.title': 'Payment',
    'subscription.portal.cta': 'Manage on Stripe',
    'subscription.portal.adminOnly': 'Only admins can manage billing.',
    'subscription.checkout.success_toast': 'Plan updated to {plan}',
    'subscription.checkout.applied': 'Update applied — refreshing…',
    'subscription.checkout.error': 'Billing action failed',
    'subscription.checkout.processing': 'Processing…',
    'subscription.checkout.downgrade_confirm_title': 'Confirm downgrade',
    'subscription.checkout.downgrade_confirm_body': 'Are you sure you want to downgrade from {from} to {to}? Excess jobs/candidates may become inaccessible.',
    'subscription.checkout.threeDS_required': 'Bank verification required. Open the Stripe portal to complete.',
    'subscription.loading': 'Loading plans…',
    'subscription.loadError': 'Could not load plans.',
    'subscription.retry': 'Retry',
    'subscription.agencyDiscovery.title': 'Are you a recruiting agency?',
    'subscription.agencyDiscovery.subtitle': 'Manage multiple client companies with a single account',
    'subscription.agencyDiscovery.note': 'Your current company will become the first client of your agency. No data will be lost.',
    'agency.convert.cta.starter': 'Become Agency',
    'agency.convert.cta.pro': 'Become Agency Pro',
    'agency.tier.enterprise.title': 'Agency Enterprise',
    'agency.tier.enterprise.badge': 'Custom',
    'agency.tier.enterprise.priceLabel': 'From €499',
    'agency.tier.enterprise.priceUnit': '/recruiter/mo',
    'agency.tier.enterprise.feat1': 'Unlimited client companies',
    'agency.tier.enterprise.feat2': 'Unlimited jobs/recruiter',
    'agency.tier.enterprise.feat3': 'Full white-label (logo, domain, email)',
    'agency.tier.enterprise.feat4': 'Dedicated CSM + custom AI models',
    'agency.tier.enterprise.feat5': 'Setup fee €2-5k · Custom contract',
    'agency.tier.enterprise.cta': 'Contact Sales',
    'agency.tier.enterprise.contactSubject': 'Agency Enterprise pricing inquiry',
    'agency.tier.enterprise.contactBody': 'Hi HiroMatch team,\n\nI\'m interested in the Agency Enterprise tier. Here\'s a bit about my agency:\n\n- Number of recruiters: \n- Number of client companies: \n- Approximate jobs/month: \n\nLooking forward to hearing from you.',
    'agency.contactModal.title': 'Manage more companies?',
    'agency.contactModal.body': 'Contact us and we\'ll help you activate the right multi-company plan for you.',
    'agency.contactModal.cta': 'Contact us',
    'agency.contactModal.mailtoSubjectAddCompany': 'Multi-company plan — add company request',
    'agency.contactModal.mailtoBodyAddCompany': 'Hi HiroMatch team,\n\nI\'d like to manage more than one company on my account.\n\nCompany name: \nEmail: \n\nThanks!',
    'agency.contactModal.mailtoSubjectSignup': 'Multi-company plan — signup request',
    'agency.contactModal.mailtoBodySignup': 'Hi HiroMatch team,\n\nI\'m signing up and would like to discuss multi-company plans.\n\nCompany or agency name: \nEmail: \n\nThanks!',
    'agency.convert.confirmTitle': 'Confirm conversion?',
    'agency.convert.confirmBody': 'Your account will switch to {plan} and your current company will become the first client. You will be redirected to Stripe checkout.',
    'agency.convert.confirmCta': 'Proceed',
    'agency.convert.success': "Conversion complete. You're now managing your agency.",
    'upgrade.smeCap.title': 'You already have a company',
    'upgrade.smeCap.body': "You're admin of \"{name}\". Each account manages one SME — convert to Agency to manage multiple.",
    'upgrade.agencyStarterCap.title': 'You\'ve reached the maximum',
    'upgrade.agencyStarterCap.body': "You've used {used}/{cap} clients on Agency Starter. Upgrade to Agency Professional to reach 20.",
    'upgrade.agencyProCap.title': 'You\'ve reached the maximum on Agency Pro',
    'upgrade.agencyProCap.body': "You've reached {used}/{cap} clients, the Agency Professional limit. Contact support for Agency Enterprise.",
    'upgrade.agencyTieredCap.title': 'You\'ve reached the maximum',
    'upgrade.agencyTieredCap.body': "You've used {used}/{cap} clients on Agency {currentLabel}. Upgrade to Agency {nextLabel} to reach {nextCap}.",
    'upgrade.cta.upgradeAgencyNext': 'Upgrade to Agency {nextLabel}',
    'upgrade.cta.upgradeSme': 'Upgrade plan',
    'upgrade.cta.upgradeAgency': 'Upgrade to Agency Pro',
    'upgrade.cta.convertToAgency': 'Become Agency',
    'upgrade.cta.contactSupport': 'Contact support',
    'usage.section_title': 'Usage this month',
    'usage.agency_overview': 'Agency Overview',
    'usage.plan_level': 'Your Plan',
    'usage.selected_company': 'Selected Company',
    'usage.ai_company': 'AI Credits',
    // @deprecated v3.5
    'usage.ai_user': 'Personal AI Credits',
    'usage.candidates': 'Candidates processed',
    'usage.transcripts': 'Transcripts',
    'usage.positions': 'Active positions',
    'usage.seats': 'Team seats',
    'usage.of': 'of',
    'usage.unlimited': 'Unlimited',
    'usage.reset_date': 'Resets on {date}',
    'usage.approaching_cap': 'Almost out — ',
    'usage.cap_reached': 'Cap reached — ',
    'usage.cta_upgrade': 'Upgrade',
    'usage.cta_upgrade_to_starter': 'Upgrade to Starter',
    'usage.trial_no_ai': 'AI not available on trial',
    'usage.load_error': 'Could not load usage',
    'usage.retry': 'Retry',
  },
  it: {
    ...marketingTranslationsIt,
    ...marketingFaqTranslationsIt,
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.positions': 'Posizioni',
    'nav.applications': 'Candidature',
    'nav.postings': 'Annunci',
    'nav.company': 'Azienda',
    'nav.companySettings': 'Apri impostazioni azienda',
    'nav.adminBackoffice': 'Backoffice',
    'nav.profile': 'Profilo',
    'nav.addCompany': 'Aggiungi Azienda',
    'nav.selectCompany': 'Seleziona Azienda',
    'nav.newPosition': 'Nuova Posizione',
    'nav.awaitingDecision': 'In Attesa di Decisione',
    'nav.menu': 'Menu di navigazione',
    'nav.mainNavigation': 'Collegamenti principali dell\'applicazione',
    'nav.reportIssue': 'Invia bug o segnalazione',

    'support.title': 'Invia bug o segnalazione',
    'support.subtitle': 'Descrivi il problema. Riceveremo anche dati tecnici per aiutarci a risolverlo.',
    'support.categoryLabel': 'Tipo',
    'support.category.bug': 'Bug',
    'support.category.feedback': 'Segnalazione',
    'support.category.other': 'Altro',
    'support.messageLabel': 'Descrizione',
    'support.messagePlaceholder': 'Cosa è successo? Cosa ti aspettavi? I passaggi per riprodurre il problema ci aiutano a intervenire prima.',
    'support.messageTooShort': 'Inserisci almeno 10 caratteri.',
    'support.attachmentsLabel': 'Screenshot (opzionale)',
    'support.attachmentsHint': 'Fino a 5 immagini PNG/JPEG/WebP, max 2 MB ciascuna.',
    'support.attachments.add': 'Aggiungi',
    'support.attachments.remove': 'Rimuovi immagine',
    'support.attachments.max': 'Puoi allegare al massimo 5 immagini.',
    'support.attachments.invalidType': 'Usa solo immagini PNG, JPEG o WebP.',
    'support.attachments.tooLarge': 'Ogni immagine deve essere di 2 MB o meno.',
    'support.attachments.uploadFailed': 'Caricamento immagine non riuscito. Riprova o invia senza screenshot.',
    'support.diagnostics.toggle': 'Cosa inviamo automaticamente',
    'support.diagnostics.route': 'Pagina corrente',
    'support.diagnostics.release': 'Versione app',
    'support.diagnostics.company': 'Azienda selezionata',
    'support.diagnostics.errors': 'Errori recenti in questa sessione',
    'support.diagnostics.none': 'nessuno registrato',
    'support.consentLabel': 'Autorizzo l\'invio di dati tecnici di diagnostica per la risoluzione del problema.',
    'support.consentRequired': 'Conferma l\'invio dei dati tecnici prima di inviare.',
    'support.submit': 'Invia segnalazione',
    'support.submitting': 'Invio in corso…',
    'support.success': 'Segnalazione inviata. Ti contatteremo se necessario.',
    'support.successTitle': 'Grazie',
    'support.successDescription': 'La segnalazione è stata inviata al nostro team. Puoi continuare a usare HiroMatch.',
    'support.sendAnother': 'Invia un\'altra segnalazione',
    'support.backToDashboard': 'Torna alla dashboard',
    'support.errorGeneric': 'Impossibile inviare la segnalazione. Riprova più tardi.',
    'support.errorUnavailable': 'L\'invio email non è disponibile al momento. Riprova tra qualche minuto.',
    'support.errorRateLimit': 'Troppe segnalazioni nell\'ultima ora. Attendi prima di inviarne un\'altra.',

    // Onboarding eligibility / upgrade-instead-of-create modal
    'onboarding.upgradeInsteadOfCreate.title': 'Hai già un\'azienda',
    'onboarding.upgradeInsteadOfCreate.body': 'Ogni account può gestire un\'azienda SME. Sei admin di:',
    'onboarding.upgradeInsteadOfCreate.planLabel': 'Piano',
    'onboarding.upgradeInsteadOfCreate.tip': 'Ti serve più capacità? Aggiorna il piano corrente invece di creare una nuova azienda.',
    'onboarding.upgradeInsteadOfCreate.cta.upgrade': 'Aggiorna piano',

    // Profile page
    'profile.title': 'Il mio profilo',
    'profile.subtitle': 'Gestisci account, piano e sicurezza',
    'profile.tab.account': 'Account',
    'profile.tab.billing': 'Piano',
    'profile.tab.notifications': 'Notifiche',
    // v3-notif (27/05/26) — User notification preferences tab (IT)
    'notifPrefs.heading': 'Notifiche via email',
    'notifPrefs.subheading': 'Scegli cosa ti arriva in casella. La campanella in app continua a tenerti aggiornato in tempo reale.',
    'notifPrefs.usingDefaultsBanner': 'Stai usando i valori di default consigliati. Modifica un interruttore per personalizzare.',
    'notifPrefs.cat.application_received.title': 'Nuove candidature',
    'notifPrefs.cat.application_received.desc': 'Quando un candidato si candida — è il segnale garantito anche se lo screening fallisce o viene bloccato dai limiti del piano.',
    'notifPrefs.cat.application_screened.title': 'Screening completato',
    'notifPrefs.cat.application_screened.desc': 'Esito dello screening AI. Di default lo vedi solo in app — attiva se vuoi anche un’email di riepilogo.',
    'notifPrefs.cat.application_state.title': 'Cambi di stato della candidatura',
    'notifPrefs.cat.application_state.desc': 'Assunto, rifiutato, ritirato, offerta rifiutata, errore pipeline.',
    'notifPrefs.cat.interview_lifecycle.title': 'Ciclo intervista',
    'notifPrefs.cat.interview_lifecycle.desc': 'Programmazione, annullamento o report pronto — calendar-critical.',
    'notifPrefs.cat.team_events.title': 'Eventi team',
    'notifPrefs.cat.team_events.desc': 'Risposte agli inviti e rimozione membri.',
    'notifPrefs.master.inApp.title': 'Notifiche in-app (campanella)',
    'notifPrefs.master.inApp.desc': 'Interruttore generale. Quando è spento la campanella resta silenziosa per ogni categoria.',
    'notifPrefs.digest.title': 'Digest giornaliero',
    'notifPrefs.digest.desc': 'Raggruppa le email non urgenti in un unico riepilogo. Partecipano solo le posizioni che hanno attivato il digest.',
    'notifPrefs.digest.hourLabel': 'Ora di consegna (fuso locale)',
    'notifPrefs.digest.hourHint': '0–23 — ad esempio 9 per un riepilogo mattutino.',
    'notifPrefs.save': 'Salva preferenze',
    'notifPrefs.saved': 'Preferenze salvate',
    'notifPrefs.saveError': 'Impossibile salvare le preferenze',
    'notifPrefs.reset': 'Ripristina default',
    'notifPrefs.resetConfirm.title': 'Ripristinare le preferenze?',
    'notifPrefs.resetConfirm.description': 'Le tue preferenze personalizzate verranno rimosse e tornerà la configurazione di default.',
    'notifPrefs.resetDone': 'Preferenze ripristinate',
    'notifPrefs.complianceNote': 'Email di fatturazione, sicurezza e reset password vengono sempre inviate, indipendentemente da queste impostazioni.',
    'profile.tab.security': 'Sicurezza',
    'profile.tab.companies': 'Aziende',
    'profile.header.companiesOwned': '{n} aziende possedute',
    'profile.header.individual': 'Account individuale',
    'profile.header.agencyOwner': 'Agency Owner',
    'profile.header.agencyMember': 'Membro Agency',
    'profile.header.invitedMember': 'Membro del Team',
    'profile.header.memberOf': 'Membro di {owner}',
    'profile.billing.movedFromSettings': 'Le impostazioni del piano sono state spostate qui dalle Impostazioni Azienda — ogni utente gestisce la propria subscription sul proprio profilo.',
    'subscription.member.cardTitle': 'Informazioni Piano',
    'subscription.member.managedByOwner': 'Questo piano è gestito da {owner}',
    'subscription.member.contactToChange': 'Contatta l\'amministratore per modificare il piano',
    'subscription.member.contactEmail': 'Contatto amministratore',
    'upgrade.member.title': 'Limite del piano raggiunto',
    'upgrade.member.description': 'Contatta {owner} per aggiornare il piano del tuo team.',
    'upgrade.member.cta': 'Chiudi',
    'companies.create.member.title': 'Non puoi creare un\'azienda',
    'companies.create.member.description': 'Sei membro di un\'altra organizzazione. Chiedi all\'amministratore di rimuoverti prima di creare la tua azienda.',
    'invitation.error.target_is_agency_owner': 'Questo utente è un agency owner e non può essere invitato come membro.',
    'invitation.error.target_belongs_to_other_owner': 'Questo utente appartiene già a un\'altra organizzazione. Deve uscirne prima di unirsi alla tua.',
    'invitation.error.fallback': 'Impossibile inviare l\'invito',
    'memberships.leave.cta': 'Lascia',
    'memberships.leave.title': 'Lasciare questa azienda?',
    'memberships.leave.description': 'Perderai l\'accesso a {company}. Potrai essere re-invitato in futuro da un amministratore.',
    'memberships.leave.confirm': 'Lascia azienda',
    'memberships.leave.success': 'Hai lasciato {company}',
    'memberships.leave.error.owner': 'Non puoi lasciare un\'azienda di cui sei proprietario. Eliminala invece.',
    'memberships.leave.error.last_admin': 'Sei l\'ultimo admin. Promuovi un altro membro prima di uscire.',
    'memberships.leave.error.fallback': 'Impossibile lasciare l\'azienda',
    'memberships.delete.cta': 'Elimina',
    'memberships.delete.title': 'Eliminare questa azienda?',
    'memberships.delete.description': 'Verrà eliminata definitivamente {company} con tutti i suoi dati (posizioni, candidati, membri, fatturazione). Questa azione non può essere annullata.',
    'memberships.delete.typeToConfirm': 'Scrivi {name} per confermare',
    'memberships.delete.confirm': 'Elimina azienda',
    'memberships.delete.success': 'Azienda {company} eliminata',
    'memberships.delete.error.fallback': 'Impossibile eliminare l\'azienda',
    'profile.displayName': 'Nome Visualizzato',
    'profile.email': 'Email',
    'profile.timeZone': 'Fuso orario',
    'profile.timeZoneDesc': 'Usato per la pianificazione colloqui e le date nel tuo orario locale.',
    'profile.saveChanges': 'Salva Modifiche',
    'profile.profileUpdated': 'Profilo aggiornato',
    'profile.changeAvatar': 'Cambia Avatar',
    'profile.memberships': 'Le Mie Appartenenze',
    'profile.notifications': 'Preferenze Notifiche',
    'profile.emailNotif': 'Notifiche email',
    'profile.emailNotifDesc': 'Ricevi aggiornamenti e avvisi via email',
    'profile.inAppNotif': 'Notifiche in-app',
    'profile.inAppNotifDesc': 'Mostra notifiche all\'interno della piattaforma',
    'profile.notifyAbout': 'Notificami per:',
    'profile.newApps': 'Nuove candidature',
    'profile.newAppsDesc': 'Quando un candidato si candida a una posizione',
    'profile.screeningDone': 'Screening completato',
    'profile.screeningDoneDesc': 'Quando lo screening AI termina l\'elaborazione',
    'profile.interviewReports': 'Report colloqui',
    'profile.interviewReportsDesc': 'Quando un report del colloquio è pronto',
    'profile.offerResponses': 'Risposte offerte',
    'profile.offerResponsesDesc': 'Quando un candidato risponde a un\'offerta',
    'profile.changePassword': 'Cambia Password',
    'profile.currentPassword': 'Password Attuale',
    'profile.newPassword': 'Nuova Password',
    'profile.confirmPassword': 'Conferma Nuova Password',
    'profile.updatePassword': 'Aggiorna Password',
    'profile.passwordUpdated': 'Password aggiornata',
    'profile.passwordMismatch': 'La nuova password e la conferma non corrispondono.',
    'profile.passwordTooWeak': 'Password troppo debole. Usa almeno 6 caratteri.',
    'profile.passwordWrongCurrent': 'La password attuale non è corretta.',
    'profile.passwordRequiresRecentLogin': 'Per sicurezza, accedi di nuovo per cambiare la password.',
    'profile.passwordGenericError': 'Impossibile aggiornare la password. Riprova.',
    'profile.signInMethods': 'Metodi di accesso',
    'profile.signInGoogle': 'Google',
    'profile.signInEmailPassword': 'Email e password',
    'profile.signInGoogleOnlyDesc': 'Accedi con Google. Puoi aggiungere una password HiroMatch qui sotto per accedere anche con email.',
    'profile.signInPasswordOnlyDesc': 'Accedi con la tua email e password.',
    'profile.signInBothDesc': 'Puoi accedere con Google o con email e password.',
    'profile.hiromatchPassword': 'Reimposta password',
    'profile.resetPassword': 'Reimposta password',
    'profile.resetPasswordButton': 'Reimposta',
    'profile.setPasswordDesc': 'Aggiungi una password per accedere anche con email oltre a Google.',
    'profile.passwordResetDesc': 'Ti invieremo un link sicuro via email per impostare una nuova password.',
    'profile.passwordResetEmailTo': 'Il link di reset verrà inviato a {email}.',
    'profile.setPassword': 'Imposta password HiroMatch',
    'profile.sendPasswordReset': 'Invia link di reset password',
    'profile.passwordResetSending': 'Invio in corso…',
    'profile.passwordResetSent': 'Controlla la posta — ti abbiamo inviato un link per impostare o cambiare la password.',
    'profile.passwordResetError': 'Impossibile inviare il link di reset. Riprova.',
    'profile.identitySection': 'Identità',
    'profile.preferencesSection': 'Preferenze',
    'profile.securitySection': 'Sicurezza',
    'profile.dangerZone': 'Zona pericolosa',
    'profile.uploadAvatar.tooLarge': 'Immagine troppo grande. Max 2MB.',
    'profile.uploadAvatar.invalidType': 'Formato non supportato. Usa PNG, JPEG o WebP.',
    'profile.uploadAvatar.failed': 'Caricamento avatar fallito. Riprova.',
    'profile.uploadAvatar.success': 'Avatar aggiornato',
    'profile.memberships.empty': 'Non sei ancora in nessuna azienda — creane una o accetta un invito.',
    'profile.appearance': 'Aspetto',
    'profile.theme': 'Tema',
    'profile.language': 'Lingua',
    'profile.languageSaveError': 'Impossibile salvare la lingua.',
    'profile.light': 'Chiaro',
    'profile.dark': 'Scuro',
    'profile.system': 'Sistema',
    'profile.logout': 'Esci',
    'profile.logoutDesc': 'Disconnettiti dal tuo account su questo dispositivo.',
    'profile.disableAccount': 'Disabilita Account',
    'profile.disableAccountShort': 'Disabilita',
    'profile.disableAccountDesc': 'Disabilita temporaneamente il tuo account. Verrai rimosso da tutte le aziende e non potrai più accedere. Contatta l\'assistenza per riattivarlo.',
    'profile.disableAccountConfirm': 'Sei sicuro? Il tuo account verrà disabilitato, sarai rimosso da tutte le aziende e non potrai più accedere. Contatta l\'assistenza per riattivarlo.',
    'profile.accountDisabled': 'Account disabilitato',
    'profile.permanentDeleteAccount': 'Elimina Account',
    'profile.deleteAccountShort': 'Elimina',
    'profile.permanentDeleteAccountDesc': 'Elimina permanentemente il tuo account e tutti i dati associati. Questa azione è irreversibile.',
    'profile.permanentDeleteAccountConfirm': 'Questa azione è permanente e non può essere annullata. Il tuo account, tutti i dati e le appartenenze verranno eliminati definitivamente.',
    'profile.accountPermanentlyDeleted': 'Account eliminato definitivamente',
    'profile.typeEmail': 'Digita la tua email per confermare',

    // Dashboard
    'dashboard.openPositions': 'Posizioni Aperte',
    'dashboard.activePostings': 'Annunci Attivi',
    'dashboard.applications': 'Candidature',
    'dashboard.processing': 'In Elaborazione',
    'dashboard.awaitingDecision': 'In Attesa',
    'dashboard.offerAcceptance': 'Accettazione Offerte',
    'dashboard.medianTimeToHire': 'Tempo Mediano di Assunzione',
    'dashboard.medianTimeToFill': 'Tempo Mediano di Copertura',
    'dashboard.tooltip.openPositions': '{count} posizioni aperte nel periodo selezionato; {aging} aperte da oltre 30 giorni.',
    'dashboard.tooltip.activePostings': 'Annunci di lavoro attivi su tutte le posizioni nel periodo selezionato.',
    'dashboard.tooltip.applications': 'Candidature ricevute nel periodo selezionato. Totale assoluto: {total}.',
    'dashboard.tooltip.processing': 'Candidature ricevute nel periodo selezionato e attualmente in elaborazione AI.',
    'dashboard.tooltip.awaitingDecision': 'Candidature ricevute nel periodo selezionato e in attesa di una decisione del recruiter.',
    'dashboard.tooltip.offerAcceptance': 'HIRED / (HIRED + OFFER_DECLINED) per offerte fatte nel periodo selezionato.',
    'dashboard.tooltip.medianTimeToHire': 'Giorni dalla candidatura all\'assunzione (mediana) nel periodo selezionato. Target: 20 giorni.',
    'dashboard.tooltip.medianTimeToFill': 'Giorni dalla creazione della posizione alla prima assunzione (mediana). Target: 30 giorni.',
    'dashboard.client': 'Cliente',
    'dashboard.aggregated_topPositions': 'Top posizioni su tutti i clienti',
    'dashboard.aggregated_recentApps': 'Candidature recenti su tutti i clienti',
    'dashboard.aggregated_trend': 'Andamento candidature (portafoglio)',
    'dashboard.aggregated_timeInState': 'Tempo mediano per stato (portafoglio)',
    'dashboard.portfolio_aiCredits.title': 'AI Credits — totale portafoglio',
    'dashboard.portfolio_aiCredits.subtitle': 'Somma di tutti i clienti',
    'dashboard.portfolio_aiCredits.unlimited': 'Almeno un cliente ha piano illimitato',
    'dashboard.portfolio_aiCredits.used': '{used} di {granted} usati',
    'dashboard.portfolio_aiCredits.remaining': '{remaining} residui',
    'dashboard.recentApplications': 'Candidature Recenti',
    'dashboard.viewAll': 'Vedi tutte',
    'dashboard.noRecent': 'Nessuna candidatura recente',
    'dashboard.candidate': 'Candidato',
    'dashboard.position': 'Posizione',
    'dashboard.source': 'Fonte',
    'dashboard.score': 'Punteggio',
    'dashboard.status': 'Stato',
    'dashboard.7days': '7 giorni',
    'dashboard.30days': '30 giorni',
    'dashboard.90days': '90 giorni',
    'dashboard.allTime': 'Tutto il periodo',
    'dashboard.tab_company': 'Azienda',
    'dashboard.tab_plan': 'Il mio Piano',
    'dashboard.plan_limits': 'Limiti del Piano',
    'dashboard.plan_ai_credits': 'Crediti AI',
    'dashboard.plan_info': 'Info sul Piano',
    'dashboard.plan_info_desc': 'Queste metriche mostrano l\'utilizzo aggregato di tutte le tue aziende clienti. Passa alla tab di un\'azienda specifica per vedere le metriche dettagliate.',
    'dashboard.aggregated_metrics': 'Tutte le Aziende - Aggregato',
    'dashboard.aggregated_funnel': 'Funnel di Recruiting - Tutte le Aziende',
    'dashboard.conversion_kpis': 'Tassi di Conversione',
    'dashboard.loading': 'Caricamento...',
    'dashboard.loading_plan_data': 'Caricamento dati del piano...',
    'dashboard.loading_companies': 'Caricamento dati... ({loaded}/{total} aziende)',
    'dashboard.aggregated_positions_tooltip': 'Posizioni aperte totali di tutte le tue aziende',
    'dashboard.aggregated_postings_tooltip': 'Annunci attivi totali di tutte le tue aziende',
    'dashboard.aggregated_applications_tooltip': 'Candidature nel periodo selezionato. Totale storico: {total}',
    'dashboard.aggregated_processing_tooltip': 'Candidature in elaborazione di tutte le aziende',
    'dashboard.aggregated_awaiting_tooltip': 'Candidature in attesa di decisione di tutte le aziende',
    'dashboard.tth_tooltip': 'Giorni dalla candidatura all\'assunzione (mediana). Obiettivo: 20 giorni',
    'dashboard.ttf_tooltip': 'Giorni dalla creazione posizione alla prima assunzione (mediana). Obiettivo: 30 giorni',
    'dashboard.applicantToInterview': 'Candidato → Colloquio',
    'dashboard.interviewToHire': 'Colloquio → Assunzione',
    'dashboard.applicantToHire': 'Candidato → Assunzione',
    'dashboard.applicantToReject': 'Candidato → Rifiuto',
    'dashboard.recruitmentFunnel': 'Funnel di Recruiting',
    'dashboard.pipelineStatus': 'Stato pipeline',
    'dashboard.pipelineStatusHint': 'Snapshot delle candidature ricevute nel periodo selezionato. Tocca una cella per aprire la lista filtrata.',
    'dashboard.pipelineTab.action': 'Azione',
    'dashboard.pipelineTab.pipeline': 'Pipeline',
    'dashboard.pipelineTab.outcomes': 'Esiti',
    'dashboard.pipelineTab.actionHint': 'Richiede attenzione oggi — decisioni, elaborazione, upgrade o errori.',
    'dashboard.pipelineTab.pipelineHint': 'Candidature in movimento o in attesa di entrare nello screening AI.',
    'dashboard.pipelineTab.outcomesHint': 'Risultati chiusi nel periodo selezionato.',
    'dashboard.funnelOutcomes': 'Esiti chiusi',
    'dashboard.funnelOutcomesHint': 'Risultati terminali nel periodo (non inclusi nel funnel di successo sopra).',
    'dashboard.pipelineTooltip.new': 'Nuove candidature non ancora elaborate dall\'AI.',
    'dashboard.pipelineTooltip.ready': 'CV salvato — pronto per avviare lo screening AI.',
    'dashboard.pipelineTooltip.upgrade': 'Bloccato dai limiti del piano — upgrade o attendi il reset mensile.',
    'dashboard.pipelineTooltip.processing': 'Screening AI in corso.',
    'dashboard.pipelineTooltip.stuck': 'Elaborazione bloccata — potrebbe servire stop o retry.',
    'dashboard.pipelineTooltip.awaiting': 'Screening completato — in attesa della tua decisione.',
    'dashboard.pipelineTooltip.error': 'Errore pipeline — rivedi e riprova o rifiuta.',
    'dashboard.pipelineTooltip.hired': 'Segnati come assunti nel periodo.',
    'dashboard.pipelineTooltip.rejected': 'Rifiutati nel periodo.',
    'dashboard.pipelineTooltip.withdrawn': 'Ritirati nel periodo.',
    'dashboard.pipelineTooltip.offerDeclined': 'Offerta rifiutata nel periodo.',
    'dashboard.noDataPeriod': 'Nessun dato per questo periodo',
    'dashboard.applicationsByChannel': 'Candidature per Canale',
    'dashboard.noData': 'Nessun dato',
    'dashboard.applied': 'Candidati',
    'dashboard.hired': 'Assunti',
    'dashboard.hireRate': 'Tasso assunzione',
    'dashboard.insufficientData': 'dati insufficienti',
    'dashboard.topPositions': 'Posizioni per Volume',
    'dashboard.issuesReview': '{count} problema/i da revisionare',
    'dashboard.noPositionsData': 'Nessuna posizione con candidature in questo periodo',
    'dashboard.rejected': 'Rifiutati',
    'dashboard.topAwaiting': 'In attesa',
    'dashboard.topErrors': 'Errori',
    'dashboard.rejectRate': 'Tasso Rifiuto',
    'dashboard.medianTimePerState': 'Tempo Mediano per Stato (giorni)',
    'dashboard.noHistoryData': 'Nessun dato storico disponibile per candidature chiuse',
    'dashboard.days': 'giorni',
    'dashboard.median': 'mediana',
    'dashboard.applicationTrend': 'Tendenza Candidature',
    'dashboard.noDataForPeriod': 'Nessun dato per questo periodo',
    'dashboard.stateNew': 'Nuovo',
    'dashboard.stateProcessing': 'In Elaborazione',
    'dashboard.stateAwaitingDecision': 'In Attesa',
    'dashboard.stateReviewRequired': 'Revisione Richiesta',
    'dashboard.stateRejected': 'Rifiutato',
    'dashboard.stateHired': 'Assunto',
    'dashboard.stateOfferDeclined': 'Offerta Rifiutata',
    'dashboard.stateWithdrawn': 'Ritirato',
    'dashboard.funnelApplied': 'Candidati',
    'dashboard.funnelScreened': 'Screening',
    'dashboard.funnelInterview': 'Colloquio',
    'dashboard.funnelDecision': 'Decisione',
    'dashboard.funnelHired': 'Assunti',
    'dashboard.tooltipScreened': 'Ha punteggio AI, report di screening, o è avanzato oltre l\'elaborazione iniziale',
    'dashboard.tooltipInterview': 'Almeno un colloquio programmato o completato',
    'dashboard.tooltipDecision': 'In stato IN_ATTESA, OFFERTA_RIFIUTATA, o ASSUNTO',

    // Job Positions
    'positions.newPosition': 'Nuova Posizione',
    'positions.searchPositions': 'Cerca posizioni...',
    'positions.allStatus': 'Tutti gli Stati',
    'positions.draft': 'Bozza',
    'positions.active': 'Attiva',
    'positions.closed': 'Chiusa',
    'positions.cancelled': 'Annullata',
    'positions.title': 'Titolo',
    'positions.status': 'Stato',
    'positions.apps': 'Candidature',
    'positions.applicationsTab': 'Candidature',
    'positions.postings': 'Annunci',
    'positions.created': 'Creata',
    'positions.updated': 'Aggiornata',
    'positions.noPositions': 'Nessuna posizione trovata',
    'emptyState.noCompany.title': 'Crea prima la tua azienda',
    'emptyState.noCompany.body': 'Non sei ancora in nessuna azienda. Creane una per iniziare a gestire posizioni, candidati e il tuo team.',
    'emptyState.noCompany.cta': 'Crea azienda',
    'emptyState.noCompany.body.positions': 'Le posizioni vivono dentro un\'azienda. Crea la tua per iniziare a pubblicare ruoli e ricevere candidature.',
    'emptyState.noCompany.body.applications': 'Le candidature vivono dentro un\'azienda. Crea la tua per iniziare a riceverle e valutarle.',
    'positions.editPosition': 'Modifica Posizione',
    'positions.closePosition': 'Chiudi Posizione',
    'positions.activatePosition': 'Attiva Posizione',
    'positions.deletePosition': 'Elimina Posizione',
    'positions.deleteConfirm': 'Scegli come eliminare questa posizione:',
    'positions.softDeleteTitle': 'Cancella (soft delete)',
    'positions.softDeleteDesc': 'Imposta lo stato a Cancellato. Tutti i dati vengono conservati nel database.',
    'positions.hardDeleteTitle': 'Elimina definitivamente',
    'positions.hardDeleteDesc': 'Rimuove la posizione e i suoi annunci dal database. Le candidature vengono conservate per audit.',
    'positions.softDeleteAction': 'Cancella Posizione',
    'positions.hardDeleteAction': 'Elimina Definitivamente',
    'positions.positionCancelled': 'Posizione cancellata',
    'positions.positionDeleted': 'Posizione eliminata definitivamente',
    'positions.positionUpdated': 'Posizione aggiornata',
    'positions.mustBeActiveToPost': 'Attiva la posizione prima di pubblicare un annuncio',
    'positions.positionCreated': 'Posizione creata',
    'positions.loadFailed': 'Impossibile caricare la posizione',
    'positions.atLeastOneMember': 'Assegna almeno un membro del team a questa posizione',
    'positions.description': 'Descrizione',
    'positions.seniority': 'Esperienza',
    'positions.contractType': 'Tipo Contratto',
    'positions.workMode': 'Modalità Lavoro',
    'positions.location': 'Sede',
    'positions.notSpecified': 'Non specificato',
    'positions.salaryMin': 'Stipendio Min',
    'positions.salaryMax': 'Stipendio Max',
    'positions.currency': 'Valuta',
    'positions.team': 'Team',
    'positions.overviewLanguageHint': 'Usata nel posting pubblico e per screening AI e report sulle candidature ricevute.',
    'positions.overviewNoAssignedMembers': 'Nessun membro assegnato',
    'positions.allTeams': 'Tutti i team',
    'positions.noTeam': 'Nessun team',
    'positions.aiAssistant': 'Assistente AI',
    'positions.closeAI': 'Chiudi AI',
    'positions.aiTryAsking': 'Prova a chiedere:',
    'positions.aiPromptWrite': 'Scrivi una descrizione lavoro',
    'positions.aiPromptRequirements': 'Suggerisci i requisiti',
    'positions.aiPromptImprove': 'Migliora la descrizione',
    'positions.aiSuggestions': 'Suggerimenti AI',
    'positions.aiDismiss': 'Ignora',
    'positions.aiApplySuggestions': 'Applica i suggerimenti',
    'positions.aiAutoFill': 'Compilazione automatica',
    'positions.aiAutoFillLoading': 'Generazione in corso…',
    'positions.aiAutoFillTooltip': "Chatta con l'AI per generare suggerimenti per i campi",
    'positions.aiAutoFillReadyTooltip': "L'AI è pronta a compilare il form — clicca per applicare",
    'positions.aiAskPlaceholder': "Chiedi aiuto all'AI…",
    'positions.descriptionPlaceholder': 'Descrizione del lavoro, responsabilità, requisiti…',
    'positions.titleExample': 'es. Senior Frontend Developer',
    'positions.selectPlaceholder': 'Seleziona…',
    'positions.selectLocation': 'Seleziona sede…',
    'positions.selectTeam': 'Seleziona team…',
    'positions.other': 'Altro…',
    'positions.customSeniority': 'Inserisci livello personalizzato…',
    'positions.customContractType': 'Inserisci tipo contratto personalizzato…',
    'positions.noCompanyLocations': 'Aggiungi prima le sedi in Azienda',
    'positions.noCompanyTeams': 'Aggiungi prima i team in Azienda',
    'positions.noMembersFound': 'Nessun membro trovato',
    'positions.seniorityJunior': 'Junior',
    'positions.seniorityJuniorMid': 'Junior–Mid',
    'positions.seniorityMid': 'Mid',
    'positions.seniorityMidSenior': 'Mid–Senior',
    'positions.senioritySenior': 'Senior',
    'positions.contractFullTime': 'Full-time',
    'positions.contractPartTime': 'Part-time',
    'positions.contractFreelance': 'Freelance',
    'positions.contractInternship': 'Stage',
    'positions.contractTemporary': 'Temporaneo',
    'positions.workModeOnSite': 'In sede',
    'positions.workModeHybrid': 'Ibrido',
    'positions.workModeRemote': 'Da remoto',
    'positions.savingState': 'Salvataggio…',
    // v3-notif (27/05/26) — Position notification settings section (IT)
    'positionNotif.section.title': 'Notifiche team',
    'positionNotif.section.desc': 'Decidi cosa emette questa posizione ai membri del team. Le preferenze utente si applicano sopra — l’email parte solo se entrambi i livelli sono d’accordo.',
    'positionNotif.usingDefaultsBanner': 'Stai usando i default di sistema — salva per bloccare i valori correnti su questa posizione.',
    'positionNotif.readonlyBanner': 'Serve permesso write o admin per modificare queste impostazioni.',
    'positionNotif.emitApplicationReceived': 'Emetti su nuova candidatura',
    'positionNotif.emitApplicationReceived.hint': 'Segnale garantito — viene emesso anche se lo screening successivo fallisce.',
    'positionNotif.emitApplicationScreened': 'Emetti su screening completato',
    'positionNotif.emitApplicationScreened.hint': 'Controlla notifiche in-app (campanella) ed email opzionale a screening finito. Di default è spento — lo score resta visibile sulla card candidatura.',
    'positionNotif.emitApplicationState': 'Emetti su cambi di stato',
    'positionNotif.emitApplicationState.hint': 'Assunto, rifiutato, ritirato, offerta rifiutata, errore pipeline.',
    'positionNotif.emitInterviewLifecycle': 'Emetti su ciclo intervista',
    'positionNotif.emitInterviewLifecycle.hint': 'Programmata, annullata, report pronto.',
    'positionNotif.enableDailyDigest': 'Includi nel digest giornaliero',
    'positionNotif.enableDailyDigest.hint': 'Quando attivo, chi ha abilitato il digest riceve un riepilogo aggregato per questa posizione.',
    'positionNotif.save': 'Salva impostazioni',
    'positionNotif.saved': 'Impostazioni notifiche salvate',
    'positionNotif.saveError': 'Impossibile salvare le impostazioni',
    'positions.requirePhone': 'Richiedi numero di telefono',
    'positions.requireLinkedIn': 'Richiedi profilo LinkedIn',
    'positions.socialProfiles': 'Profili social',
    'positions.addSocialProfile': 'Aggiungi',
    'positions.socialProfilesEmpty': 'Nessun campo social nel form di candidatura. Aggiungine uno per raccogliere LinkedIn, Instagram, Facebook o altri link.',
    'positions.applicationDocuments': 'Documenti candidatura',
    'positions.addDocumentField': 'Aggiungi',
    'positions.applicationDocumentsEmpty': 'Nessun documento nel form di candidatura. Aggiungi Curriculum, Lettera di presentazione o Portfolio.',
    'positions.documentRequired': 'Obbligatorio',
    'positions.removeDocumentField': 'Rimuovi documento',
    'positions.documentType.resume': 'Curriculum',
    'positions.documentType.presentation_letter': 'Lettera di presentazione',
    'positions.documentType.portfolio': 'Portfolio',
    'positions.documentTypeHint.resume': 'PDF curriculum / CV',
    'positions.documentTypeHint.presentation_letter': 'PDF lettera di presentazione',
    'positions.documentTypeHint.portfolio': 'PDF portfolio (solo visualizzazione recruiter in MVP)',
    'positions.socialRequired': 'Obbligatorio',
    'positions.socialCustomLabel': 'Etichetta personalizzata',
    'positions.removeSocialProfile': 'Rimuovi profilo social',
    'positions.socialType.linkedin': 'LinkedIn',
    'positions.socialType.instagram': 'Instagram',
    'positions.socialType.facebook': 'Facebook',
    'positions.socialType.tiktok': 'TikTok',
    'positions.socialType.website': 'Sito web',
    'positions.socialType.custom': 'Personalizzato',
    'positions.socialTypeHint.linkedin': 'URL profilo LinkedIn',
    'positions.socialTypeHint.instagram': 'URL profilo Instagram',
    'positions.socialTypeHint.facebook': 'URL profilo Facebook',
    'positions.socialTypeHint.tiktok': 'URL profilo TikTok',
    'positions.socialTypeHint.website': 'Sito personale o portfolio',
    'positions.socialTypeHint.custom': 'Scegli un\'etichetta personalizzata per questo link',
    'apply.socialRequired': '{label} è obbligatorio',
    'apply.socialInvalid': 'Inserisci un URL valido per {label}',
    'positions.notifyCandidate': 'Notifica email ai candidati',
    'positions.allowDuplicateApplications': 'Permetti candidature duplicate',
    'positions.allowDuplicateApplicationsHint': 'Lo stesso candidato può ripresentarsi con dati identici',
    'positions.autoRejectScore': 'Soglia auto-rifiuto punteggio',
    'positions.outputLanguage': 'Lingua AI e candidati',
    'positions.outputLanguageHint': 'Modificando questa impostazione cambiano report di screening e colloquio AI, email ai candidati e la pagina pubblica — etichette del form, badge (es. Da remoto) e schermata di conferma.',
    'positions.outputLanguageIt': 'Italiano',
    'positions.outputLanguageEn': 'Inglese',
    // Sprint 3 (S3.E) — wizard di creazione
    'positions.wizardCreatedTitle': 'Posizione creata',
    'positions.wizardCreatedSubtitle': 'Attivala ora per iniziare a raccogliere candidati, oppure continua a modificarla in bozza.',
    'positions.wizardLandingLabel': 'Pagina pubblica',
    'positions.wizardLandingHint': 'Il link funzionerà solo dopo l\'attivazione.',
    'positions.wizardSaveAsDraft': 'Salva come bozza',
    'positions.wizardActivate': 'Attiva ora',
    'positions.linkCopied': 'Link copiato negli appunti',
    'positions.activated': 'Posizione attivata. Il link è ora pubblico.',
    'positions.activatedPopupBlocked': 'Posizione attivata, ma il browser ha bloccato la nuova tab. Usa “Vedi pagina pubblica” dal dettaglio per aprirla.',
    'positions.copy': 'Copia',
    'positions.openLink': 'Apri',
    'positions.assignedMembers': 'Membri Assegnati',
    'positions.assignedMembersCreator': 'Creatore',
    'positions.assignedMembersAllAssigned': 'Tutti i membri sono assegnati',
    'positions.assignedMembersSearch': 'Cerca per nome o email…',
    'positions.searchMembers': 'Cerca membri...',
    
    'positions.overview': 'Panoramica',
    'positions.history': 'Cronologia',
    'positions.versions': 'Versioni',
    'positions.noHistory': 'Nessuna cronologia disponibile',
    'positions.noVersions': 'Nessuna versione disponibile',
    'positions.noApplications': 'Nessuna candidatura',
    'positions.current': 'Corrente',
    'positions.view': 'Visualizza',
    'positions.version': 'Versione',
    'positions.salary': 'Stipendio',
    'positions.contract': 'Contratto',
    'positions.options': 'Opzioni',
    'positions.viewPosting': 'Visualizza Annuncio',
    'positions.copyUrl': 'Copia URL',
    'positions.postingActivated': 'Annuncio attivato',
    'positions.postingDeactivated': 'Annuncio disattivato',
    'positions.noDetails': 'Nessun dettaglio',

    // Applications
    'applications.searchCandidates': 'Cerca candidati...',
    'applications.allStatus': 'Tutti gli Stati',
    'applications.new': 'Nuovo',
    'applications.processing': 'In Elaborazione',
    'applications.processingStuck': 'Elaborazione bloccata',
    'applications.inQueue': 'In coda',
    'applications.awaitingDecision': 'In Attesa',
    'applications.interview': 'Colloquio',
    'applications.rejected': 'Rifiutato',
    'applications.hired': 'Assunto',
    'applications.reviewRequired': 'Revisione Richiesta',
    'applications.withdrawn': 'Ritirato',
    'applications.offerDeclined': 'Offerta Rifiutata',
    'applications.candidate': 'Candidato',
    'applications.email': 'Email',
    'applications.position': 'Posizione',
    'applications.source': 'Fonte',
    'applications.applied': 'Candidatura',
    'applications.updated': 'Aggiornato',
    'applications.score': 'Punteggio',
    'applications.status': 'Stato',
    'applications.statusPinTooltip': 'Fissa uno stato in cima (si combina con l\'ordinamento colonne)',
    'applications.allPositions': 'Tutte le Posizioni',
    'applications.team': 'Team',
    'applications.allTeams': 'Tutti i team',
    'applications.star': 'Preferito',
    'applications.starAction': 'Aggiungi alla shortlist',
    'applications.unstarAction': 'Rimuovi dalla shortlist',
    'applications.unreadIndicator': 'Candidatura non letta',
    'applications.bulkMarkRead': 'Segna come letta',
    'applications.bulkMarkReadSuccess': '{count} candidati segnati come letti',
    'applications.bulkMarkReadFailed': 'Impossibile segnare come letti — riprova',
    'applications.bulkMarkUnread': 'Segna come non letta',
    'applications.bulkMarkUnreadSuccess': '{count} candidati segnati come non letti',
    'applications.bulkMarkUnreadFailed': 'Impossibile segnare come non letti — riprova',
    'applications.sortByStar': 'Ordina per shortlist',
    'applications.sortByInbox': 'Ordina per lettura',
    'applications.allStarred': 'Tutti i candidati',
    'applications.starredOnly': 'Solo in shortlist',
    'applications.unstarredOnly': 'Non in shortlist',
    'applications.noApplications': 'Nessuna candidatura trovata',
    'applications.noApplicationsForFilter': 'Nessun candidato corrisponde ai filtri attivi.',
    'applications.clearFilters': 'Rimuovi filtri',
    'applications.loadMore': 'Carica altre',
    'applications.hydratingList': 'Caricamento… {loaded} / {total}',
    'pagination.showing': 'Visualizzati {from}–{to} di {total}',
    'pagination.pageOf': 'Pagina {page} di {totalPages}',
    'pagination.previous': 'Precedente',
    'pagination.next': 'Successiva',
    'applications.loadingMore': 'Caricamento…',
    // Sprint 5 (S5.D) — selezione multipla
    'applications.bulkSelectAll': 'Seleziona tutte',
    'applications.bulkSelectedCount': '{count} selezionati',
    'applications.bulkClear': 'Deseleziona',
    'applications.bulkReject': 'Rifiuta',
    'applications.bulkRejectTitle': 'Rifiuta {count} candidati',
    'applications.bulkRejectDesc': 'La nota e l\'opt-in email valgono per tutti i candidati selezionati.',
    'applications.bulkRejectConfirm': 'Rifiuta {count}',
    'applications.bulkRejectSuccess': '{count} candidati rifiutati',
    'applications.bulkRejectPartial': '{count} candidati hanno avuto errore — controlla le righe e riprova',
    'applications.bulkProcess': 'Elaborazione',
    'applications.bulkStop': 'Arresta elaborazione',
    'applications.bulkStopTooltip': 'Interrompe lo screening AI in corso senza riavviarlo (non consuma crediti)',
    'applications.bulkStopSuccess': 'Elaborazione arrestata per {count} candidati',
    'applications.bulkStopPartial': '{count} candidati non sono stati arrestati — controlla le righe e riprova',
    'applications.bulkStopSkipped': 'Nessuna elaborazione in corso da arrestare per {count} righe selezionate',
    'applications.bulkStopClearedStale': 'Indicatore di elaborazione obsoleto rimosso per {count} candidati',
    'applications.bulkStopApiUnavailable': 'Arresto elaborazione non disponibile sul server. Esegui il deploy delle Cloud Functions (abort-stuck) e riprova.',
    'applications.rowStopping': 'Arresto…',
    'applications.bulkProcessSuccess': '{count} candidati avviati per lo screening AI',
    'applications.bulkProcessPartial': '{count} candidati non sono stati processati — controlla le righe e riprova',
    'applications.queueActive': 'Attive',
    'applications.queueArchive': 'Archivio',
    'applications.allSources': 'Tutte le fonti',
    'applications.bulkArchive': 'Archivia',
    'applications.bulkUnarchive': 'Ripristina',
    'applications.bulkArchiveSuccess': '{count} candidati archiviati',
    'applications.bulkArchivePartial': '{count} candidati non archiviati — controlla le righe e riprova',
    'applications.bulkUnarchiveSuccess': '{count} candidati ripristinati in coda attiva',
    'applications.bulkUnarchivePartial': '{count} candidati non ripristinati — controlla le righe e riprova',
    'applications.bulkUnarchiveUnavailable': 'Ripristino non riuscito — le API archivio potrebbero non essere deployate. Esegui firebase deploy --only functions e riprova.',
    'applications.bulkDelete': 'Elimina definitivamente',
    'applications.bulkDeleteTitle': 'Eliminare {count} candidati archiviati?',
    'applications.bulkDeleteDescription': 'Operazione irreversibile. Candidature, documenti, cronologia pipeline e file caricati verranno rimossi definitivamente dal server.',
    'applications.bulkDeleteConfirm': 'Elimina {count}',
    'applications.bulkDeleteSuccess': '{count} candidati eliminati definitivamente',
    'applications.bulkDeletePartial': '{count} candidati non eliminati — controlla le righe e riprova',
    'applications.bulkDeleteUnavailable': 'Eliminazione non riuscita — le API bulk delete potrebbero non essere deployate. Esegui firebase deploy --only functions e riprova.',
    'applications.archivedBanner': 'Questo candidato è in archivio e non compare nella coda attiva.',
    'applications.archiveAction': 'Archivia',
    'applications.unarchiveAction': 'Ripristina in coda attiva',
    // Pending billing recovery (process-now / process-pending)
    'application.pendingBilling.badge': 'In attesa di piano',
    'application.pendingBilling.tooltip': 'CV salvato — aggiorna il piano per processare questo candidato',
    'application.pendingBilling.processNow': 'Processa ora',
    'application.pendingBilling.processing': 'Elaborazione…',
    'application.pendingBilling.processSuccess': 'Elaborazione avviata. La pipeline è in esecuzione.',
    'application.pendingBilling.processBilling': 'Piano ancora insufficiente. Aggiorna il piano per continuare.',
    'application.pendingBilling.processAll': 'Processa tutti ({count})',
    'application.pendingBilling.reviewInApplications': 'Vai ad Applications ({count})',
    'application.openApplicationsConfirm.title': 'Rivedi i candidati da processare',
    'application.openApplicationsConfirm.body':
      '{count} candidati sono pronti per lo screening AI. Li vedrai in Applications con il filtro corretto.',
    'application.openApplicationsConfirm.credits':
      'Il piano consente circa {count} elaborazioni in questo periodo — procedi a batch dalla toolbar.',
    'application.openApplicationsConfirm.hint':
      'Verranno selezionate tutte le righe corrispondenti. Clicca Processing quando sei pronto; le code grandi partono a batch da 30.',
    'application.openApplicationsConfirm.confirm': 'Apri Applications ({count})',
    'applications.bulkReadySelected':
      '{count} candidati selezionati — clicca Processing nella toolbar quando sei pronto.',
    'application.pendingBilling.bulkProcessing': 'Elaborazione di {count} candidati…',
    'application.pendingBilling.bulkSuccess': '{processed} elaborati, {skipped} in attesa',
    'application.pendingBilling.bulkAllSkipped': 'Piano esaurito. Aggiorna il piano per processare i {count} candidati rimanenti.',
    'application.pendingBilling.bannerTitle': '{count} candidati pronti per essere processati',
    'application.pendingBilling.bannerBody': 'L\'upgrade del piano ha sbloccato {count} CV in attesa. Esegui lo screening AI ora.',
    'application.pendingBilling.bannerProgressTitle': 'Elaborazione candidati: {done} di {total}',
    'application.pendingBilling.bannerProgressBody':
      'Lo screening AI è in corso. Le righe completate escono dalla coda automaticamente.',
    'application.pendingBilling.bannerInboundScreening':
      '+ {count} candidature da posting in screening automatico.',
    'application.identityMismatch.badge': 'Mismatch',
    'application.identityMismatch.badgeContact': 'Dati contatto diversi',
    'application.identityMismatch.tooltipTitle': 'I dati del form di candidatura non corrispondono al resume',
    'application.identityMismatch.tooltipTitleContact': 'I contatti nel form differiscono dal resume',
    'application.identityMismatch.panelTitle': 'Mismatch',
    'application.identityMismatch.panelTitleContact': 'Dati contatto diversi',
    'application.identityMismatch.panelIntro': 'I dati inseriti nel form di candidatura non corrispondono al resume analizzato.',
    'application.identityMismatch.panelIntroContact':
      'Email, telefono o LinkedIn nel form differiscono dal resume — spesso un errore di digitazione o un contatto alternativo. Lo screening ha usato il profilo del resume.',
    'application.identityMismatch.panelFooter':
      'Lo screening ha valutato il profilo presente nel resume. Il nome in testa alla pagina riflette il form.',
    'application.identityMismatch.field.name': 'Nome',
    'application.identityMismatch.field.email': 'Email',
    'application.identityMismatch.field.phone': 'Telefono',
    'application.identityMismatch.field.linkedin': 'LinkedIn',
    'application.identityMismatch.fieldLine': '{label}: {formValue} (form) → {resumeValue} (resume)',
    'application.processAllConfirm.title': 'Avvia lo screening AI',
    'application.processAllConfirm.bodyFirstTime':
      '{count} candidati non sono ancora stati processati e entreranno nella pipeline AI.',
    'application.processAllConfirm.bodyRetry':
      '{count} candidati erano stati bloccati da un limite di billing dopo l\'avvio dello screening. Verranno riprocessati.',
    'application.processAllConfirm.bodyMixed':
      '{firstTime} candidati verranno processati per la prima volta e {retry} verranno riprocessati dopo un precedente blocco billing.',
    'application.processAllConfirm.confirm': 'Processa {count}',

    'application.bulkProcessConfirm.title': 'Processa i candidati selezionati',
    'application.bulkProcessConfirm.intro': 'Verranno avviati per lo screening AI:',
    'application.bulkProcessConfirm.lineFirstTime':
      '{count} screening per la prima volta (mai processati)',
    'application.bulkProcessConfirm.lineRestartProcessing':
      '{count} attualmente in elaborazione — verranno fermati e riavviati',
    'application.bulkProcessConfirm.lineBillingRetry':
      '{count} bloccati da limite billing — verranno ritentati',
    'application.bulkProcessConfirm.lineReprocess':
      '{count} già sottoposti a screening — verranno riprocessati',
    'application.bulkProcessConfirm.linePipelineRetry':
      '{count} falliti in precedenza — verranno ritentati',
    'application.bulkProcessConfirm.creditsWarning':
      'Il riprocessamento e il riavvio delle candidature bloccate consumano crediti AI.',
    'application.bulkProcessConfirm.confirm': 'Processa {count}',

    // BUG-09 — PENDING_UPGRADE: candidatura ricevuta ma cap mensile esaurito.
    'application.pendingUpgrade.badge': 'In attesa di upgrade',
    'application.pendingUpgrade.tooltip':
      'Avevi raggiunto il limite mensile di candidati quando questo CV è arrivato. Aggiorna il piano o attendi il prossimo periodo per sbloccare lo screening AI.',
    'application.pendingUpgrade.detailTitle': 'Elaborazione AI bloccata',
    'application.pendingUpgrade.detailBody':
      'Avevi raggiunto il limite mensile di candidati del tuo piano quando questa candidatura è stata inviata. Il CV è salvato — aggiorna il piano o attendi il prossimo reset, poi clicca "Processa ora" per eseguire lo screening AI.',
    'application.pendingUpgrade.processNow': 'Processa ora',
    'application.pendingUpgrade.upgradeCta': 'Aggiorna piano',
    'application.pendingUpgrade.bannerTitle':
      '{count} candidati in attesa di screening AI',
    'application.pendingUpgrade.bannerBody':
      'Il tuo piano ha messo in coda questi candidati prima dell\'elaborazione AI. Aggiorna per sbloccarli tutti, o processali uno per uno.',

    // READY_TO_PROCESS — post-upgrade, persistito su BE, in attesa di azione recruiter.
    'application.readyToProcess.badge': 'Da processare',
    'application.readyToProcess.tooltip':
      'CV salvato — il tuo piano può processare questo candidato. Clicca Processa ora per avviare lo screening AI.',
    'application.readyToProcess.detailTitle': 'Pronto per lo screening AI',
    'application.readyToProcess.detailBody':
      'Questo candidato è in attesa di screening AI. Clicca Processa ora per avviare la pipeline.',
    'application.readyToProcess.bannerTitle': '{count} candidati pronti per essere processati',
    'application.readyToProcess.bannerBody':
      'Esegui lo screening AI ora, o processa i candidati uno per uno dalla lista.',

    // Postings
    'postings.newPosting': 'Nuovo Annuncio',
    'postings.position': 'Posizione',
    'postings.channel': 'Canale',
    'postings.status': 'Stato',
    'postings.apps': 'Cand.',
    'postings.published': 'Pubblicato',
    'postings.created': 'Creato',
    'postings.url': 'URL',
    'postings.copy': 'Copia',
    'postings.active': 'Attivo',
    'postings.inactive': 'Inattivo',
    'postings.activate': 'Attiva',
    'postings.deactivate': 'Disattiva',
    'postings.statusUpdateFailed': 'Impossibile aggiornare lo stato dell\'annuncio',
    'postings.noPostings': 'Nessun annuncio',
    'postings.newJobPosting': 'Nuovo Annuncio di Lavoro',
    'postings.editPosting': 'Modifica Annuncio',
    'postings.titleOverride': 'Titolo alternativo (opzionale)',
    'postings.titlePlaceholder': 'Lascia vuoto per usare il titolo della posizione',
    'postings.selectPosition': 'Seleziona posizione...',
    'postings.creating': 'Creazione...',
    'postings.createPosting': 'Crea Annuncio',
    'postings.postingCreated': 'Annuncio creato',
    'postings.postingUpdated': 'Annuncio aggiornato',
    'postings.postingDeleted': 'Annuncio eliminato',
    'postings.deleteFailed': 'Impossibile eliminare l\'annuncio',
    'postings.deleteDisabledActive': 'Disattiva l\'annuncio prima di eliminarlo',
    'postings.deleteDisabledApps': 'Elimina prima tutte le candidature collegate a questo annuncio.',
    'postings.deleteConfirmTitle': 'Eliminare l\'annuncio?',
    'postings.deleteConfirmBody': 'Rimuove definitivamente l\'annuncio disattivato. Operazione irreversibile.',
    'postings.customChannel': 'Canale personalizzato…',
    'postings.customChannelPlaceholder': 'es. Instagram, gruppo WhatsApp, job fair',
    'postings.urlCopied': 'URL copiato',

    // Settings
    'settings.general': 'Generale',
    'settings.teams': 'Team',
    'settings.members': 'Membri',
    'settings.plan': 'Piano',
    'settings.plan.hero.title': 'Piano attivo su questa azienda',
    'settings.plan.hero.descSme': 'I limiti qui sotto valgono per questo workspace. AI Credits e membri sono ereditati dall\'abbonamento scelto dal proprietario dell\'azienda.',
    'settings.plan.hero.descAgency': 'Questo è un piano Agency: i cap qui sotto sono per singolo workspace cliente. Aggiungere una nuova azienda aggiunge un seat all\'abbonamento con addebito proporzionale.',
    'settings.plan.managedBy': 'Abbonamento gestito da {name}',
    'settings.plan.managedByYou': 'Gestisci questo abbonamento dal tuo profilo',
    'settings.plan.priceCustom': 'Prezzo personalizzato',
    'settings.plan.priceFree': 'Gratis / 7 giorni di prova',
    'settings.plan.perMonth': 'mese',
    'settings.plan.limits.title': 'Limiti per azienda',
    'settings.plan.limits.positions': 'Job attive',
    'settings.plan.limits.candidates': 'Candidati AI/mese',
    'settings.plan.limits.seats': 'Membri (seats)',
    'settings.plan.limits.aiCredits': 'AI Credits/mese',
    'settings.plan.limits.hint': 'I numeri sono per workspace. I Candidati AI sono il volume di CV elaborabili con AI screening. Gli AI Credits coprono tutte le altre azioni AI: chat candidato, assistente Job Position, trascrizione chiamate, AutoFill JD e interview report.',
    'settings.plan.features.title': 'Cosa include questo piano',
    'settings.plan.features.aiScreening': 'Screening AI di ogni CV ricevuto',
    'settings.plan.features.callTranscription': 'Trascrizione delle interview call',
    'settings.plan.features.interviewReports': 'Report di colloquio generati dall\'AI',
    'settings.plan.features.aiAutofill': 'AI Autofill della job description',
    'settings.plan.features.publicLandingPage': 'Landing page pubblica delle posizioni',
    'settings.plan.features.emailNotifications': 'Notifiche email e automation lifecycle',
    'settings.plan.features.multiCompany': 'Gestione multi-azienda (Agency)',
    'settings.plan.features.trialCreditsHint': 'Disponibile durante la prova entro i 25 AI credits inclusi',
    'settings.plan.footer.note': 'Per modificare o aggiornare l\'abbonamento, vai alla sezione Piano nel tuo profilo.',
    'settings.plan.cta.manage': 'Gestisci abbonamento',
    'settings.plan.cta.goToProfile': 'Apri Piano nel profilo',
    'settings.plan.loadError': 'Impossibile caricare il piano dell\'azienda. Riprova tra qualche secondo.',
    'settings.companyInfo': 'Informazioni Azienda',
    'settings.logo': 'Logo',
    'settings.upload': 'Carica',
    'settings.logoUrlPlaceholder': 'Oppure incolla URL: https://...',
    'settings.companyName': 'Nome Azienda',
    'settings.publicName': 'Nome Pubblico',
    'settings.description': 'Descrizione',
    'settings.noLogo': 'Nessun logo caricato',
    'settings.defaultOutputLanguage': 'Lingua predefinita AI e candidati',
    'settings.defaultOutputLanguageHint': 'Ereditata da ogni nuova posizione (screening, report, email, posting pubblico). Le posizioni esistenti mantengono la loro impostazione.',
    'settings.employeeCount': 'Numero Dipendenti',
    'settings.avgSalaryMin': 'Stipendio medio min',
    'settings.avgSalaryMax': 'Stipendio medio max',
    'settings.currency': 'Valuta',
    'settings.offices': 'Sedi',
    'settings.links': 'Link',
    'settings.offices.addCta': 'Aggiungi sede',
    'settings.links.addCta': 'Aggiungi link',
    'settings.offices.empty': 'Nessuna sede ancora. Aggiungine una per mostrarla sulle offerte di lavoro.',
    'settings.links.empty': 'Nessun link ancora. Aggiungi il sito, LinkedIn, GitHub o altri profili social.',
    'settings.saveChanges': 'Salva Modifiche',
    'settings.settingsSaved': 'Impostazioni salvate',
    'settings.deleteCompany': 'Elimina Azienda',
    'settings.deleteCompanyDesc': 'Rimuovi {name} e tutti i dati associati permanentemente.',
    'settings.addTeam': 'Aggiungi Team',
    'settings.editTeam': 'Modifica Team',
    'settings.teamName': 'Nome',
    'settings.teamDescription': 'Descrizione',
    'settings.teamMembers': 'Membri',
    'settings.teamSaved': 'Team salvato',
    'settings.inviteMember': 'Invita Membro',
    'settings.inviteDesc': 'Verrà generato un link di invito e inviato via email. Il membro completerà il proprio profilo al momento della registrazione.',
    'settings.inviteSent': 'Invito inviato',
    'settings.inviteLinkCopied': 'Link di invito copiato',
    'settings.editMember': 'Modifica Membro',
    'settings.memberUpdated': 'Membro aggiornato',
    'settings.removeMember': 'Rimuovi Membro',
    'settings.removeConfirm': 'Sei sicuro? Questa azione non può essere annullata.',
    'settings.memberRemoved': 'Membro rimosso',
    'settings.filteredBy': 'Filtrato per:',
    'settings.clear': 'Cancella',
    'settings.sendInvite': 'Invia Invito',
    'settings.copyInviteLink': 'Copia Link Invito',
    'settings.resendInvite': 'Reinvia invito',
    'settings.name': 'Nome',
    'settings.email': 'Email',
    // Sprint 6 (S6.A) — invite dialog enhancements
    'settings.firstName': 'Nome',
    'settings.lastName': 'Cognome',
    'settings.roleHint': 'Admin gestisce billing e membri. Write può creare posizioni e prendere decisioni. Read è solo osservatore.',
    'settings.typeHint': 'Interno = dipendente. Esterno = freelance / agenzia / partner — separati per reporting.',
    'settings.role': 'Ruolo',
    'settings.team': 'Team',
    'settings.type': 'Tipo',
    'settings.status': 'Stato',
    'settings.joined': 'Iscritto',
    'settings.internal': 'Interno',
    'settings.external': 'Esterno',
    'settings.selectTeam': 'Seleziona team...',
    'settings.pendingInvite': 'Invito in attesa',
    'settings.pending': 'In attesa',
    'settings.declinedInvite': 'Invito rifiutato',
    'settings.declined': 'Rifiutato',
    'settings.active': 'Attivo',
    'settings.inactive': 'Inattivo',
    'settings.deleteCompanyTitle': 'Elimina Azienda',
    'settings.deleteCompanyConfirm': 'Sei sicuro di voler eliminare permanentemente {name}? Tutte le posizioni, candidature e dati andranno persi per sempre.',
    'settings.deleteCompanySuccess': 'Azienda eliminata',
    'settings.deleteCompanyError': 'Impossibile eliminare l\'azienda',

    // Team page
    'team.addMember': 'Aggiungi Membro',
    'team.editMember': 'Modifica Membro',
    'team.removeMember': 'Rimuovi Membro',

    // Notifications
    'notifications.title': 'Notifiche',
    'notifications.markAllRead': 'Segna tutto come letto',
    'notifications.empty': 'Nessuna notifica',
    'notifications.dismissed': 'Notifica eliminata',
    'notifications.deleteFailed': 'Impossibile eliminare la notifica',
    'notifications.loadMore': 'Carica altre',
    'notifications.loadingMore': 'Caricamento…',
    'notifications.showingCount': 'Mostrate {count} notifiche',
    'notifications.unreadCount': '{count} non lette',

    // Common
    'common.save': 'Salva',
    'common.cancel': 'Annulla',
    'common.moreActions': 'Altre azioni',
    'errorBoundary.title': 'Qualcosa è andato storto',
    'errorBoundary.body': 'Abbiamo registrato l\'errore e lo stiamo analizzando.',
    'errorBoundary.reloadHint': 'Prova a ricaricare la pagina.',
    // v3.2 polish (21/05/26) — pay-as-you-grow confirm modal per add company in agency
    'addCompany.seatConfirm.title': 'Conferma nuovo seat azienda',
    'addCompany.seatConfirm.subtitle': 'Aggiungere una azienda aumenta la quantità della tua subscription Agency. Verrà addebitato l\'importo prorated immediatamente.',
    'addCompany.seatConfirm.currentSeats': 'Seats attuali',
    'addCompany.seatConfirm.newSeats': 'Seats dopo l\'aggiunta',
    'addCompany.seatConfirm.pricePerSeat': 'Prezzo per seat / mese',
    'addCompany.seatConfirm.proratedNow': 'Addebito immediato (prorated)',
    'addCompany.seatConfirm.newMonthlyTotal': 'Nuovo totale mensile',
    'addCompany.seatConfirm.fineprint': 'L\'addebito viene generato da Stripe al momento della creazione dell\'azienda. Puoi gestire la subscription da Profilo → Piano.',
    'addCompany.seatConfirm.cta': 'Conferma e procedi',
    // Sprint 4 (S4.C) — manual application by recruiter
    'manualApply.cta': 'Aggiungi candidato',
    'manualApply.title': 'Aggiungi candidato manualmente',
    'manualApply.subtitle': 'Il CV passa per la stessa pipeline di screening del form pubblico (cap, dedup, score AI).',
    'manualApply.aiNotice': 'Questo candidato non ha visto il form pubblico di candidatura. In qualità di datore di lavoro, sei tenuto a informarlo che l\'IA viene utilizzata per assistere lo screening, in linea con la nostra Informativa sull\'uso dell\'IA.',
    'manualApply.position': 'Posizione',
    'manualApply.positionPlaceholder': 'Seleziona una posizione…',
    'manualApply.noActivePositions': 'Nessuna posizione attiva — attivane una prima',
    'manualApply.noPositionsInCompany': 'Nessuna posizione — creane una in Posizioni prima',
    'manualApply.source': 'Fonte',
    'manualApply.sourcePlaceholder': 'Seleziona una fonte…',
    'manualApply.firstName': 'Nome',
    'manualApply.lastName': 'Cognome',
    'manualApply.email': 'Email',
    'manualApply.phone': 'Telefono',
    'manualApply.linkedin': 'URL LinkedIn',
    'manualApply.cv': 'CV (PDF)',
    'manualApply.cvHint': 'Clicca per caricare — max 15 MB',
    'manualApply.submit': 'Invia',
    'manualApply.success': 'Candidato aggiunto — pipeline in partenza.',
    'manualApply.errors.positionRequired': 'Seleziona la posizione per cui il candidato si propone.',
    'manualApply.errors.sourceRequired': 'Seleziona la fonte della candidatura.',
    'manualApply.errors.noActivePosting': 'Questa posizione non ha annunci attivi. Creane almeno uno prima di aggiungere un candidato.',
    'manualApply.errors.noPostingForSource': 'Nessun annuncio attivo per la fonte "{source}". Crea prima un annuncio per questo canale.',
    'manualApply.errors.firstNameRequired': 'Il nome è obbligatorio.',
    'manualApply.errors.lastNameRequired': 'Il cognome è obbligatorio.',
    'manualApply.errors.emailRequired': "L'email è obbligatoria.",
    'manualApply.errors.emailInvalid': 'Formato email non valido.',
    'manualApply.errors.cvRequired': 'Carica il CV del candidato.',
    'manualApply.errors.cvTooLarge': 'Il CV supera il limite di 15 MB.',
    'common.delete': 'Elimina',
    'common.edit': 'Modifica',
    'common.add': 'Aggiungi',
    'common.remove': 'Rimuovi',
    'common.search': 'Cerca',
    'common.filter': 'Filtra',
    'common.actions': 'Azioni',
    'common.back': 'Indietro',
    'common.select': 'Seleziona...',
    'common.close': 'Chiudi',
    'common.loading': 'Caricamento...',
    'common.noResults': 'Nessun risultato',

    // Responsive table
    'table.columns': 'Colonne',
    'table.visibleColumns': 'Colonne visibili',
    'table.density': 'Densità',
    'table.densityCompact': 'Compatta',
    'table.densityComfortable': 'Comoda',
    'table.resetColumns': 'Reset colonne',

    // Onboarding
    'onboarding.title': 'Crea la tua azienda',
    'onboarding.subtitle': 'Configura la tua organizzazione per iniziare il recruiting',
    'onboarding.companyName': 'Nome Azienda',
    'onboarding.publicName': 'Nome Pubblico',
    'onboarding.publicNameHint': 'Usa il Nome Azienda se lasciato vuoto.',
    'onboarding.description': 'Descrizione',
    'onboarding.descriptionHint': 'min. {min} caratteri',
    'onboarding.continue': 'Continua',
    'onboarding.creating': 'Creazione…',
    'onboarding.joinExisting': 'Entrerò in un\'azienda esistente',
    'onboarding.successTitle': 'Tutto pronto!',
    'onboarding.successDescSkipped': 'Il tuo account è pronto! 🎉',
    'onboarding.successDescCreated': 'La tua azienda è stata creata. Iniziamo ad assumere.',
    'onboarding.goToDashboard': 'Vai alla Dashboard',

    // Application Detail
    'appDetail.tabScreening': 'AI Screening',
    'appDetail.tabResume': 'Curriculum',
    'appDetail.tabPresentationLetter': 'Lettera di presentazione',
    'appDetail.tabPortfolio': 'Portfolio',
    'appDetail.tabDocuments': 'Documenti',
    'appDetail.tabInterview': 'Colloquio',
    'appDetail.tabJourney': 'Percorso',
    'appDetail.tabHistory': 'Storico',
    'appDetail.tabNotes': 'Note',
    'appDetail.notesPrivateSection': 'Nota privata',
    'appDetail.notesYourPrivateNote': 'La tua nota privata',
    'appDetail.notesYourTeamNote': 'La tua nota per il team',
    'appDetail.notesYourNote': 'La tua nota',
    'appDetail.notesPlaceholder': 'Aggiungi una nota privata o visibile al team su questo candidato…',
    'appDetail.notesVisibilityPrivate': 'Privata',
    'appDetail.notesVisibilityPrivateHint': 'Solo tu (e l\'assistente AI per te) potete vedere questa nota.',
    'appDetail.notesVisibilityPublic': 'Team',
    'appDetail.notesVisibilityPublicHint': 'Visibile a tutto il team e inclusa nel contesto AI.',
    'appDetail.notesSave': 'Salva nota',
    'appDetail.notesSaveSuccess': 'Nota salvata',
    'appDetail.notesSaveError': 'Impossibile salvare la nota',
    'appDetail.notesLoadError': 'Impossibile caricare le note',
    'appDetail.notesEdit': 'Modifica',
    'appDetail.notesCancel': 'Annulla',
    'appDetail.notesCreatedAt': 'Creata il {date}',
    'appDetail.notesUpdatedAt': 'Aggiornata {date}',
    'appDetail.notesTeamSection': 'Note del team',
    'appDetail.notesUnknownAuthor': 'Membro del team',
    'appDetail.analyzing': 'L\'AI sta analizzando questo candidato...',
    'appDetail.noReport': 'Nessun report di screening disponibile per questo candidato.',
    'appDetail.pros': 'Pro',
    'appDetail.cons': 'Contro',
    'appDetail.strengths': 'Punti di Forza',
    'appDetail.weaknesses': 'Debolezze',
    'appDetail.missingData': 'Dati Mancanti',
    'appDetail.suggestedQuestions': 'Domande di Colloquio Suggerite',
    'appDetail.aiChat': 'Chat AI',
    'appDetail.scheduleInterview': 'Trascrizione colloquio',
    'appDetail.reject': 'Rifiuta',
    'appDetail.rejectCandidate': 'Rifiuta Candidato',
    'appDetail.rejectNote': 'Nota di rifiuto (opzionale)...',
    'appDetail.confirmReject': 'Rifiuta',
    'appDetail.rejectSendEmail': 'Invia email di rifiuto al candidato',
    'appDetail.rejectSendEmailHint': 'Messaggio cortese e brandizzato nella lingua del candidato. Seleziona per inviare al rifiuto.',
    'appDetail.withdrawCandidate': 'Ritira candidatura',
    'appDetail.withdrawConfirm': 'La candidatura sarà segnata come ritirata. Potrai riattivare il candidato in seguito dalla pagina di dettaglio, se necessario.',
    'appDetail.withdrawNote': 'Nota di ritiro (facoltativa)...',
    'appDetail.confirmWithdraw': 'Ritira',
    'appDetail.viewResume': 'Visualizza CV',
    'appDetail.downloadResume': 'Scarica CV',
    'appDetail.noResume': 'Nessun file CV disponibile per l\'anteprima',
    'appDetail.viewPresentationLetter': 'Visualizza lettera',
    'appDetail.downloadPresentationLetter': 'Scarica lettera',
    'appDetail.noPresentationLetter': 'Nessuna lettera di presentazione disponibile per l\'anteprima',
    'appDetail.presentationLetterContent': 'Contenuto estratto',
    'appDetail.viewPortfolio': 'Visualizza portfolio',
    'appDetail.downloadPortfolio': 'Scarica portfolio',
    'appDetail.noPortfolio': 'Nessun file portfolio disponibile per l\'anteprima',
    'appDetail.processPortfolio': 'Elabora portfolio',
    'appDetail.processPortfolioTodo': 'L\'elaborazione AI del portfolio non è ancora disponibile',
    'appDetail.documents': 'Documenti Candidatura',
    'appDetail.download': 'Scarica',
    'appDetail.appliedOn': 'Candidatura',
    'appDetail.analyzed': 'Analizzati',
    'appDetail.consentTitle': 'Consensi candidato (form pubblico di candidatura)',
    'appDetail.consentGdpr': 'GDPR — trattamento dati personali',
    'appDetail.consentAi': 'AI — analisi della candidatura',
    'appDetail.consentGranted': 'Accettato',
    'appDetail.consentNotRecorded': 'Non registrato',
    'appDetail.consentNotRecordedHint': 'Candidatura precedente alla raccolta consensi o inserimento manuale',
    'appDetail.aiAuditTitle': 'Audit elaborazione AI (Art. 12)',
    'appDetail.aiAuditToggle': 'Dettagli tecnici',
    'appDetail.aiAuditModel': 'Modello',
    'appDetail.aiAuditProvider': 'Provider',
    'appDetail.aiAuditPromptHash': 'Hash prompt',
    'appDetail.aiAuditDocumentHash': 'Hash resume',
    'appDetail.aiAuditProcessedDocuments': 'Documenti elaborati',
    'appDetail.aiAuditGeneratedAt': 'Generato il',
    'appDetail.aiAuditProfile': 'Profilo',
    'appDetail.basedOn': 'Basato su',
    'appDetail.scoreHistory': 'Storico punteggi',
    'appDetail.screeningHistory': 'Storico Screening',
    'appDetail.versions': '{count} versioni',
    'appDetail.contact': 'Contatto',
    'appDetail.contactFromForm': 'Contatto — form di candidatura',
    'appDetail.contactFromResume': 'Contatto — dal curriculum',
    'appDetail.email': 'Email',
    'appDetail.phone': 'Telefono',
    'appDetail.phoneFromForm': 'Telefono (form di candidatura)',
    'appDetail.phoneFromResume': 'Telefono (curriculum)',
    'appDetail.portfolio': 'Portfolio',
    'appDetail.locationFromResume': 'Località — dal curriculum',
    'appDetail.currentPosition': 'Posizione Attuale',
    'appDetail.experience': 'Esperienza',
    'appDetail.skills': 'Competenze',
    'appDetail.education': 'Formazione',
    'appDetail.languages': 'Lingue',
    'appDetail.present': 'Presente',
    'appDetail.structuredPending': 'I dati strutturati saranno disponibili dopo il completamento dello screening',
    'appDetail.rawResume': 'Contenuto CV Originale',
    'appDetail.resumePending': 'Il contenuto del CV sarà disponibile dopo il completamento dello screening',
    'appDetail.noInterviews': 'Nessun colloquio pianificato',
    'appDetail.noInterviewsHint': 'Usa il menu Azioni per pianificare una trascrizione colloquio per questo candidato',
    'appDetail.transcript': 'Trascrizione',
    'appDetail.aiReport': 'Report AI',
    'appDetail.candidate': 'Candidato',
    'appDetail.creator': 'Creatore',
    'appDetail.requestApproval': 'Richiedi Approvazione',
    'appDetail.makeOffer': 'Invia Offerta',
    'appDetail.markHired': 'Segna come Assunto',
    'appDetail.comingSoon': 'Presto',
    'appDetail.comingSoonMsg': 'Questa funzionalità sarà disponibile a breve! Stiamo lavorando per portare {label} nel tuo flusso di lavoro.',
    'appDetail.aiAssistant': 'Assistente AI',
    'appDetail.suggestedPrompts': 'Suggerimenti:',
    'appDetail.askCandidate': 'Chiedi di questo candidato...',
    'appDetail.candidateRejected': 'Candidato rifiutato',
    'appDetail.applications': 'Candidature',
    'appDetail.notFoundTitle': 'Candidatura non trovata',
    'appDetail.notFoundBody': 'Il candidato potrebbe aver ritirato la candidatura o il link non è più valido. Riapri la candidatura dalla lista Candidature.',
    'appDetail.backToList': 'Torna alle Candidature',
    'appDetail.hireCandidate': 'Segna come assunto',
    'appDetail.hireNote': 'Nota assunzione (facoltativa)...',
    'appDetail.confirmHire': 'Segna come assunto',
    'appDetail.candidateHired': 'Candidato segnato come assunto',
    'appDetail.hireFailed': 'Impossibile segnare come assunto',
    'appDetail.rejectFailed': 'Impossibile rifiutare il candidato',
    'appDetail.resumeDownloadFailed': 'Impossibile scaricare il CV',
    'appDetail.linkCopied': 'Link copiato',
    'appDetail.copied': 'Copiato!',
    'appDetail.actionRequired': 'Azione richiesta',
    'appDetail.yearsExp': '{years} anni di esperienza',
    'appDetail.journeyApplied': 'Candidatura',
    'appDetail.journeyScreening': 'AI Screening',
    'appDetail.journeyAwaiting': 'In Attesa di Decisione',
    'appDetail.journeyInterview': 'Colloquio',
    'appDetail.journeyHired': 'Assunto',
    'appDetail.journeyRejected': 'Rifiutato',
    'appDetail.journeyAppliedInfo': 'Candidatura via {channel} il {date}',
    'appDetail.journeyScreeningScore': 'Punteggio AI: {score}/100',
    'appDetail.journeyPending': 'In attesa...',
    'appDetail.journeyWaiting': 'In attesa di azione del recruiter',
    'appDetail.journeyScheduled': 'Pianificato per il {date}',
    'appDetail.journeyInterviewCancelled': 'Era il {date} — annullato',
    'appDetail.addParticipantHint': 'Aggiungi almeno 1 membro dell\'azienda',
    'appDetail.participants': 'Partecipanti',
    'appDetail.interviewScheduled': 'Colloquio Pianificato!',
    'appDetail.viewInCalendar': 'Visualizza in Google Calendar',
    'appDetail.withdraw': 'Ritira',
    'appDetail.candidateWithdrawn': 'Candidato ritirato',
    'appDetail.reactivate': 'Riattiva',
    'appDetail.reactivateCandidate': 'Riattiva candidato',
    'appDetail.reactivateConfirm': 'Il candidato tornerà in Attesa decisione. Screening e colloqui precedenti restano disponibili. Potrai rifiutare, assumere o avviare nuove pipeline.',
    'appDetail.reactivateNote': 'Nota (opzionale)',
    'appDetail.reactivateNotePlaceholder': 'Motivo della riattivazione…',
    'appDetail.confirmReactivate': 'Riattiva',
    'appDetail.candidateReactivated': 'Candidato riattivato',
    'appDetail.reactivateFailed': 'Impossibile riattivare il candidato',
    'appDetail.withdrawFailed': 'Impossibile ritirare',
    'appDetail.stateTransitions': 'Transizioni di Stato',
    'appDetail.noHistory': 'Nessuna storia disponibile',
    'appDetail.historyInterviewSchedulingStarted': 'Avvio pianificazione colloquio',
    'appDetail.historyInterviewScheduledFor': 'Colloquio pianificato per il {date}',
    'appDetail.historyTranscriptionBotScheduled': 'Notetaker trascrizione: ingresso previsto alle {joinAt}',
    'appDetail.historyTranscriptionBotScheduledLate':
      'Notetaker trascrizione: slot colloquio {meeting}, ingresso effettivo {joinAt} (la pipeline ha finito dopo l’orario — il bot entra appena possibile)',
    'appDetail.historyTranscriptionBotCancelled': 'Notetaker trascrizione annullato{suffix}',
    'appDetail.historyInterviewRescheduledFromTo': 'Colloquio riprogrammato: {previousDate} → {date}',
    'appDetail.historyInterviewCalendarUpdated': 'Calendario aggiornato — colloquio il {date} (non è ancora lo svolgimento del colloquio)',
    'appDetail.historyInterviewCalendarUpdatedShort': 'Passaggio calendario e inviti completato',
    'appDetail.historyInterviewPipelineCompleted': 'Pipeline di pianificazione colloquio completata',
    'appDetail.historyInterviewCancelledEvent': 'Colloquio annullato',
    'appDetail.executorScreeningDocuments': 'Resume e documenti elaborati',
    'appDetail.executorGenerateReport': 'Report AI generato',
    'appDetail.executorSendEmailReport': 'Email report inviata',
    'appDetail.docTypeResume': 'Curriculum',
    'appDetail.docTypePresentationLetter': 'Lettera di presentazione',
    'appDetail.docTypePortfolio': 'Portfolio',
    'appDetail.docTypeResumeContent': 'Contenuto curriculum',
    'appDetail.docTypeResumeStructured': 'Curriculum strutturato',
    'appDetail.docTypePresentationLetterContent': 'Contenuto lettera',
    'appDetail.docTypeScreeningReport': 'Report screening',
    'appDetail.historyDetailUploaded': 'Caricati: {docs}',
    'appDetail.historyDetailGenerated': 'Generati: {docs}',
    'appDetail.historyDetailBasedOn': 'Basato su: {docs}',
    'appDetail.historyDetailScore': 'Score {score}',
    'appDetail.executorCreateCalendar': 'Evento Google Calendar e Meet',
    'appDetail.executorIngestTranscript': 'Trascrizione caricata dal provider',
    'appDetail.executorGenerateInterviewReport': 'Report colloquio AI generato',
    'appDetail.executorSendInterviewInvite': 'Email personalizzata al candidato (se abilitata)',
    'appDetail.pipelineDebug': 'Dettaglio Debug Pipeline',

    // Interview Modal
    'interviewModal.title': 'Pianifica Colloquio',
    'interviewModal.titleTranscript': 'Pianifica trascrizione colloquio',
    'interviewModal.dateTime': 'Data e Ora *',
    'interviewModal.dateLabel': 'Data *',
    'interviewModal.timeLabel': 'Ora *',
    'interviewModal.endTimeLabel': 'Fine *',
    'interviewModal.derivedDurationHint': 'Durata: {minutes} min (da inizio e fine)',
    'interviewModal.pickDate': 'Seleziona una data',
    'interviewModal.participants': 'Partecipanti *',
    'interviewModal.searchMembers': 'Cerca membri del team...',
    'interviewModal.addHint': 'Aggiungi almeno 1 membro dell\'azienda',
    'interviewModal.duration': 'Durata *',
    'interviewModal.type': 'Tipo *',
    'interviewModal.notes': 'Note',
    'interviewModal.notesPlaceholder': 'Aggiungi note per il colloquio (es. aree di focus, istruzioni di preparazione)',
    'interviewModal.sendEmail': 'Invia email personalizzata al candidato',
    'interviewModal.sendEmailDesc': 'Invito email personalizzato in aggiunta all\'invito Calendar',
    'interviewModal.createGoogleCalendar': 'Crea evento Google Calendar',
    'interviewModal.createGoogleCalendarDesc': 'Se disattivato, incolla il link della riunione (Zoom, Google Meet, Teams, ecc.) — usato dal bot trascrittore / Recall.',
    'interviewModal.googleCalendarComingSoon': 'La creazione dell\'evento in Google Calendar da qui non è ancora disponibile — per ora usa il link esterno sopra.',
    'interviewModal.externalMeetingUrl': 'Link riunione esterno',
    'interviewModal.externalMeetingUrlDesc':
      'Incolla il link della videochiamata da qualsiasi piattaforma (Google Meet, Microsoft Teams, Zoom, Outlook, ecc.). Il bot HiroMatch entrerà nella call all\'orario pianificato — chi ha creato la riunione dovrà ammetterlo — e genererà una trascrizione al termine del colloquio.',
    'interviewModal.externalMeetingUrlPlaceholder': 'https://…',
    'interviewModal.enableTranscription': 'Trascrizione riunione',
    'interviewModal.enableTranscriptionDesc': 'Pianifica un partecipante automatico che si unisce al meeting e produce la trascrizione dopo la chiamata. Disattiva in modifica per annullare il notetaker già pianificato.',
    'interviewModal.schedule': 'Pianifica trascrizione colloquio',
    'interviewModal.scheduling': 'Pianificazione trascrizione...',
    'interviewModal.scheduled': 'Trascrizione colloquio pianificata!',
    'interviewModal.viewCalendar': 'Visualizza in Google Calendar',
    'interviewModal.candidate': 'Candidato',
    'interviewModal.creator': 'Creatore',
    'interviewModal.failed': 'Impossibile pianificare la trascrizione colloquio',
    'interviewModal.blocked.trialTitle': 'Trascrizione call non inclusa nel Trial',
    'interviewModal.blocked.trialBody':
      'La pianificazione colloqui con trascrizione AI è disponibile da Starter in su. Aggiorna il piano per pianificare colloqui e generare trascrizioni delle call.',
    'interviewModal.blocked.creditsTitle': 'Crediti AI esauriti',
    'interviewModal.blocked.creditsBody':
      'Hai esaurito i crediti AI per questo periodo di fatturazione. Aggiorna il piano o attendi il prossimo reset per pianificare nuove trascrizioni colloquio.',
    'interviewModal.blocked.upgradeCta': 'Aggiorna piano',
    'interviewModal.videoCall': 'Videochiamata',
    'interviewModal.phoneCall': 'Telefonata',
    'interviewModal.inPerson': 'Di persona',
    'interviewModal.titleEdit': 'Modifica colloquio',
    'interviewModal.update': 'Salva modifiche',
    'interviewModal.updating': 'Aggiornamento...',
    'interviewModal.updated': 'Colloquio aggiornato!',
    'appDetail.interviewColWhen': 'Data',
    'appDetail.interviewColParticipants': 'Partecipanti',
    'appDetail.interviewColLinks': 'Meet / Calendar',
    'appDetail.interviewColNumber': '#',
    'appDetail.interviewColCreatedAt': 'Creato il',
    'appDetail.interviewColCreatedBy': 'Creato da',
    'appDetail.interviewEdit': 'Modifica',
    'appDetail.interviewCancel': 'Annulla colloquio',
    'appDetail.interviewCancelled': 'Colloquio annullato',
    'appDetail.interviewCancelFailed': 'Impossibile annullare il colloquio',
    'appDetail.cancelInterviewTitle': 'Annulla colloquio',
    'appDetail.cancelInterviewDesc':
      'È possibile avvisare i partecipanti tramite Google Calendar e email. Puoi aggiungere un messaggio opzionale nell\'email di cancellazione.',
    'appDetail.cancelInterviewMessageLabel': 'Messaggio ai partecipanti (opzionale)',
    'appDetail.cancelInterviewMessagePlaceholder':
      'Breve nota inclusa nell\'email (es. motivo o prossimi passi).',
    'appDetail.cancelInterviewNotifyLabel': 'Come notificare',
    'appDetail.cancelInterviewSendGoogle': 'Invia aggiornamento tramite Google Calendar (evento annullato)',
    'appDetail.cancelInterviewSendEmail': 'Invia email di cancellazione ai partecipanti',
    'appDetail.cancelInterviewChannelsRequired': 'Seleziona almeno un canale di notifica.',
    'appDetail.cancelRecordingOnlyHint': 'Si tratta solo di una registrazione con bot trascrittore — nessun evento calendario verrà annullato e non verranno inviate email.',
    'appDetail.cancellingInterview': 'Annullamento…',
    'appDetail.interviewExpandHint': 'Trascrizione e report',
    'appDetail.noTranscript': 'Nessuna trascrizione.',
    'appDetail.interviewNoAiReport': 'Nessun report AI.',
    'appDetail.interviewRegenerateReport': 'Rigenera report',
    'appDetail.interviewReportGenerating': 'Generazione in corso…',
    'appDetail.interviewReportFailedHint': 'Generazione report fallita. Puoi riprovare.',
    'appDetail.interviewRegenerateReportSuccess': 'Generazione report avviata.',
    'appDetail.interviewRegenerateReportFailed': 'Impossibile rigenerare il report.',
    'appDetail.interviewColStatus': 'Stato',
    'appDetail.interviewColTranscript': 'Trascrizione',
    'appDetail.interviewColRecallBot': 'Notetaker',
    'appDetail.interviewJoinBot': 'Join ora',
    'appDetail.interviewJoinBotSuccess': 'Notetaker inviato: dovrebbe entrare a breve nella call.',
    'appDetail.interviewJoinBotFailed': 'Impossibile avviare il notetaker. Controlla il link e la configurazione Recall.',
    'appDetail.interviewTranscriptOn': 'Sì',
    'appDetail.interviewTranscriptOff': 'No',
    'appDetail.interviewColDetails': 'Trascrizione e report',
    'appDetail.interviewNotesLabel': 'Note',
    'appDetail.interviewStatus.scheduled': 'Programmato',
    'appDetail.interviewStatus.updated': 'Aggiornato',
    'appDetail.interviewStatus.completed': 'Completato',
    'appDetail.interviewStatus.cancelled': 'Annullato',

    // Login
    'login.title': 'Accedi al tuo account',
    'login.subtitle': 'Inserisci le tue credenziali per continuare',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.submit': 'Accedi',
    'login.submitting': 'Accesso in corso...',
    'login.google': 'Accedi con Google',
    'login.googleFailed': 'Accesso con Google fallito',
    'login.forgot': 'Password dimenticata?',
    'login.noAccount': 'Non hai un account?',
    'login.signup': 'Registrati',
    'login.error': 'Credenziali non valide. Riprova.',
    'login.invalidEmail': 'Inserisci un indirizzo email valido.',
    'login.accountDisabled': 'Questo account non è attivo al momento. Contattare l\'assistenza.',
    'login.or': 'oppure',
    'login.forgotTitle': 'Reimposta la password',
    'login.forgotSubtitle': 'Inserisci la tua email: ti invieremo un link per scegliere una nuova password.',
    'login.forgotSubmit': 'Invia link di reset',
    'login.forgotSubmitting': 'Invio in corso…',
    'login.forgotSuccessTitle': 'Controlla la posta',
    'login.forgotSuccessBody': 'Se esiste un account con questa email, riceverai a breve un link per reimpostare la password.',
    'login.forgotError': 'Qualcosa è andato storto. Riprova.',
    'login.backToLogin': 'Torna al login',
    'login.passwordResetDone': 'Password aggiornata. Ora puoi accedere.',

    // Signup
    'signup.title': 'Crea il tuo account',
    'signup.subtitle': 'Inizia con hiromatch',
    'signup.name': 'Nome Visualizzato',
    'signup.namePlaceholder': 'Il tuo nome',
    'signup.email': 'Email',
    'signup.password': 'Password',
    'signup.confirm': 'Conferma Password',
    'signup.submit': 'Crea account',
    'signup.submitting': 'Creazione account...',
    'signup.google': 'Registrati con Google',
    'signup.googleFailed': 'Registrazione con Google fallita',
    'signup.hasAccount': 'Hai già un account?',
    'signup.login': 'Accedi',
    'signup.passwordMismatch': 'Le password non corrispondono',
    'signup.error': 'Registrazione fallita. Riprova.',
    'signup.accountType.title': 'Che tipo di account stai creando?',
    'signup.accountType.sme.title': "Sono un'azienda",
    'signup.accountType.sme.desc': 'Gestisci una sola azienda con il tuo team',
    'signup.accountType.sme.price': 'da €99/mese',
    'signup.accountType.sme.cta': 'Seleziona',
    'signup.accountType.agency.title': "Sono un'agenzia",
    'signup.accountType.agency.desc': 'Gestisci più clienti con un team di recruiter',
    'signup.accountType.agency.price': 'da €149/recruiter/mese',
    'signup.accountType.agency.cta': 'Seleziona',
    'signup.agencyTier.title': 'Quale piano agency?',
    'signup.agencyTier.starter': 'Agency Starter',
    'signup.agencyTier.starterDesc': '€149/recruiter/mese · 5 clienti, 15 jobs/recruiter',
    'signup.agencyTier.pro': 'Agency Professional',
    'signup.agencyTier.proDesc': '€249/recruiter/mese · 20 clienti, 50 jobs/recruiter, transcripts',
    'signup.agencyTier.cadence': 'Cadenza',
    'signup.agencyTier.monthly': 'Mensile',
    'signup.agencyTier.annual': 'Annuale (-10%)',
    'signup.agencyTier.continue': 'Continua',
    'signup.agencyTier.back': 'Indietro',
    'signup.agencyTier.contactSupport': 'Contatta supporto',
    'signup.agencyTier.error': "Impossibile attivare l'account agency. Riprova.",
    'subscription.agency.title': 'Subscription Agency',
    'subscription.agency.currentPlan': 'Piano corrente',
    'subscription.agency.calculator.title': 'Calcola il tuo prezzo',
    'subscription.agency.calculator.recruiters': 'Numero recruiter',
    'subscription.agency.calculator.basePrice': 'Prezzo base per recruiter',
    'subscription.agency.calculator.volumeDiscount': 'Sconto volume',
    'subscription.agency.calculator.annualDiscount': 'Sconto annuale',
    'subscription.agency.calculator.effective': 'Effettivo per recruiter',
    'subscription.agency.calculator.total': 'TOTALE MENSILE',
    'subscription.agency.calculator.nextTier': 'Aggiungi {n} recruiter per sbloccare -{p}%',
    'subscription.agency.cta.upgrade': 'Upgrade',
    'subscription.agency.cta.downgrade': 'Downgrade',
    'subscription.agency.cta.current': 'Piano corrente',
    'subscription.agency.cta.contact': 'Contattaci',
    'dashboard.agency.tier.starter': 'Agency Starter',
    'dashboard.agency.tier.pro': 'Agency Professional',
    'dashboard.agency.clients': '{used}/{cap} clienti',
    'dashboard.agency.recruiters': '{n} recruiter',
    'dashboard.agency.totalMonth': '€{amount}/mese',
    'dashboard.agency.manage': 'Gestisci subscription',
    'dashboard.agency.jobsCap': 'Active jobs (cross-cliente)',
    'dashboard.agency.clientsCap': 'Aziende cliente',
    'dashboard.agency.recruitersWidget': 'Recruiter attivi',
    'dashboard.agency.transcripts': 'Transcripts',
    'onboarding.upgradeInsteadOfCreate.agencyCap.title': 'Hai raggiunto il massimo',
    'onboarding.upgradeInsteadOfCreate.agencyCap.body': 'Hai usato {used}/{cap} clienti del tuo piano. Aggiorna ad Agency Professional per gestirne fino a 20.',
    'onboarding.upgradeInsteadOfCreate.agencyCap.cta': 'Aggiorna ad Agency Pro',

    // Public Posting
    'apply.loading': 'Caricamento posizione...',
    'apply.notFound': 'Posizione non trovata',
    'apply.notFoundDesc': 'Questa posizione non esiste o il link non è valido.',
    'apply.closed': 'Posizione chiusa',
    'apply.closedDesc': 'Questa posizione non accetta più candidature.',
    'apply.success': 'Candidatura inviata!',
    'apply.thankYou': 'Grazie',
    'apply.received': 'La tua candidatura per {position} presso {company} è stata ricevuta con successo.',
    'apply.contact': 'Ti contatteremo all\'indirizzo {email} se il tuo profilo corrisponde alla posizione.',
    'apply.goodLuck': 'In bocca al lupo! 🍀',
    'apply.about': 'Chi è {company}',
    'apply.employees': '{count} dipendenti',
    'apply.submitTitle': 'Invia la tua candidatura',
    'apply.firstName': 'Nome *',
    'apply.lastName': 'Cognome *',
    'apply.email': 'Email *',
    'apply.phone': 'Telefono',
    'apply.linkedIn': 'LinkedIn',
    'apply.resume': 'Curriculum *',
    'apply.dragResume': 'Trascina il tuo curriculum qui o clicca per sfogliare',
    'apply.pdfMax': 'PDF, max 10MB',
    'apply.submit': 'Invia Candidatura',
    'apply.submitting': 'Invio in corso...',
    'apply.gdprConsentRequired': 'Devi accettare il trattamento dei dati personali per inviare la candidatura.',
    'apply.aiConsentRequired': 'Devi confermare di essere informato/a sull\'uso dell\'intelligenza artificiale per inviare la candidatura.',
    'apply.poweredBy': 'Powered by',
    'apply.workMode.On-site': 'In presenza',
    'apply.workMode.Hybrid': 'Ibrido',
    'apply.workMode.Remote': 'Da remoto',
    'apply.contractType.Full-time': 'Tempo pieno',
    'apply.contractType.Part-time': 'Part-time',
    'apply.contractType.Freelance': 'Freelance',
    'apply.contractType.Internship': 'Stage',
    'apply.contractType.Temporary': 'Temporaneo',
    'apply.seniority.Junior': 'Junior',
    'apply.seniority.Junior-Mid': 'Junior-Mid',
    'apply.seniority.Mid': 'Mid',
    'apply.seniority.Mid-Senior': 'Mid-Senior',
    'apply.seniority.Senior': 'Senior',
    'apply.firstNameRequired': 'Il nome è obbligatorio',
    'apply.lastNameRequired': 'Il cognome è obbligatorio',
    'apply.emailRequired': 'L\'email è obbligatoria',
    'apply.emailInvalid': 'Indirizzo email non valido',
    'apply.phoneRequired': 'Il numero di telefono è obbligatorio',
    'apply.phoneInvalid': 'Numero non valido.',
    'apply.linkedInRequired': 'Il profilo LinkedIn è obbligatorio',
    'apply.linkedInInvalid': 'URL LinkedIn non valido — incolla l\'URL completo del profilo o il tuo nome vanity (es. marco-rossi).',
    'apply.resumeRequired': 'Il curriculum è obbligatorio',
    'apply.documentRequired': '{label} è obbligatorio',
    'apply.documentInvalid': 'Solo file PDF fino a 10 MB per {label}',
    'apply.rateLimit': 'Hai raggiunto il limite di candidature. Riprova più tardi.',
    'apply.duplicate': 'Hai già inviato una candidatura per questa posizione.',
    'apply.validationError': 'Dati non validi. Controlla tutti i campi e riprova.',
    'apply.genericError': 'Si è verificato un errore. Riprova più tardi.',
    'apply.networkError': 'Errore di rete. Controlla la connessione e riprova.',

    // Billing
    'billing.aiNotEnabled': 'L\'Assistente AI non è disponibile nel tuo piano attuale. [Aggiorna il piano](/profile?tab=billing) per abilitarlo.',
    'aiChat.plan_required': '🔒 L\'assistente AI è incluso a partire dal piano **Starter**. [Aggiorna il piano](/profile?tab=billing) per attivarlo.',
    'aiChat.balance_exhausted': '💳 Hai esaurito i crediti AI inclusi nel piano per questo mese. [Aggiorna piano](/profile?tab=billing) o attendi il prossimo ciclo.',
    'aiChat.demo_unavailable': 'Le funzionalità AI non sono disponibili in modalità demo.',
    'aiChat.generic_error': 'Qualcosa è andato storto. Riprova.',
    // FloatingAiChat UI chrome
    'aiChat.title': 'Assistente AI',
    'aiChat.newChat': 'Nuova chat',
    'aiChat.expand': 'Espandi',
    'aiChat.minimize': 'Riduci',
    'aiChat.close': 'Chiudi assistente AI',
    'aiChat.open': 'Apri assistente AI',
    'aiChat.send': 'Invia messaggio',
    'aiChat.tryAsking': 'Prova a chiedere:',
    'aiChat.placeholder.default': 'Chiedi all\'assistente AI...',
    // Pagina candidature
    'aiChat.applications.contextLabel': 'Contesto: candidature, punteggi, posizioni',
    'aiChat.applications.placeholder': 'Chiedi qualcosa sulle candidature...',
    'aiChat.applications.prompt1': 'Riassumi la pipeline candidature corrente',
    'aiChat.applications.prompt2': 'Quali candidati hanno i punteggi più alti?',
    'aiChat.applications.prompt3': 'Mostrami le candidature che richiedono attenzione',
    'aiChat.applications.prompt4': 'Confronta i candidati per una specifica posizione',
    // Pagina dettaglio posizione
    'aiChat.positionDetail.contextLabel': 'Contesto: {title} — candidati, colloqui e descrizione',
    'aiChat.positionDetail.placeholder': 'Chiedi sui candidati, colloqui o questa posizione...',
    'aiChat.positionDetail.prompt1': 'Quanti candidati ho e qual è la suddivisione per stato?',
    'aiChat.positionDetail.prompt2': 'Chi sono i 3 candidati con il punteggio più alto?',
    'aiChat.positionDetail.prompt3': 'Confronta i colloqui dei candidati che ne hanno già sostenuto uno',
    'aiChat.positionDetail.prompt4': 'Suggerisci miglioramenti per questa descrizione',
    // Pagina dettaglio candidatura (review candidato)
    'aiChat.candidate.contextLabel': 'Contesto: curriculum, lettera di presentazione, portfolio, report screening, colloqui, dati strutturati, posizione',
    'aiChat.candidate.prompt1': 'Quali sono i punti di forza principali di questo candidato?',
    'aiChat.candidate.prompt2': 'Riassumi cosa è stato discusso nei colloqui',
    'aiChat.candidate.prompt3': 'Suggerisci domande di follow-up sulla base di quanto già discusso',
    'trial.banner.info': 'Sei in prova HiroMatch — {days} giorni rimanenti',
    'trial.banner.warning': 'Mancano solo {days} giorni al termine del tuo trial',
    'trial.banner.urgent_today': '⚠️ Il tuo trial scade oggi',
    'trial.banner.urgent_tomorrow': '⚠️ Il tuo trial scade domani',
    'trial.banner.cta_choose_plan': 'Scegli un piano',
    'trial.banner.cta_upgrade_now': 'Upgrade ora',
    'trial.banner.cta_unlock': 'Sblocca HiroMatch',
    'trial.expired.title': 'Trial scaduto',
    'trial.expired.body': 'Il tuo periodo di prova di 7 giorni è terminato il {date}.',
    'trial.expired.subtitle': 'Per continuare a usare HiroMatch, scegli un piano:',
    'trial.expired.cta': 'Vedi i piani',
    'trial.expired.banner.message': 'Il trial è terminato. Puoi consultare i dati — scegli un piano per agire di nuovo.',
    'trial.expired.banner.withDate': 'Il trial è terminato il {date}. Puoi consultare i dati — scegli un piano per agire di nuovo.',
    'trial.expired.support': 'Hai domande? Scrivici a',
    'trial.upgradeWall.title': 'Upgrade richiesto',
    'trial.upgradeWall.trialExpiredBody': 'Il tuo trial è terminato. Per continuare ad usare questa funzionalità, scegli un piano.',
    'trial.upgradeWall.cta': "Vai all'upgrade",
    // v3.2 polish (21/05/26) — copy dinamico per ogni reason di cap raggiunto
    'upgradeWall.seats.title': 'Hai raggiunto il massimo',
    'upgradeWall.seats.body': 'Hai raggiunto il numero massimo di utenti per questa azienda. Aggiorna il piano per invitare altri membri.',
    'upgradeWall.positions.title': 'Hai raggiunto il massimo',
    'upgradeWall.positions.body': 'Hai raggiunto il numero massimo di posizioni attive del tuo piano. Aggiorna per aprirne altre.',
    'upgradeWall.candidates.title': 'Hai raggiunto il massimo',
    'upgradeWall.candidates.body': 'Continuiamo a ricevere le candidature, ma non saranno elaborate dall\'AI finché non aggiorni il piano o il contatore si resetta a inizio mese.',
    'upgradeWall.structural.title': 'Hai raggiunto il massimo',
    'upgradeWall.structural.body': 'Hai raggiunto un limite del tuo piano attuale. Aggiorna per continuare.',
    'upgradeWall.balance.title': 'AI Credits esauriti',
    'upgradeWall.balance.body': 'Hai usato tutti gli AI Credits di questo mese. I candidati continuano ad arrivare ed essere elaborati. Aggiorna il piano o acquista un top-up per sbloccare chat, trascrizioni, autofill e interview report.',
    'upgradeWall.planRequired.title': 'Piano superiore richiesto',
    'upgradeWall.planRequired.body': 'Questa funzionalità è disponibile dal piano Starter in su. Aggiorna il piano per sbloccarla.',

    // Subscription (pricing v3)
    'subscription.title': 'Abbonamento',
    'subscription.currentPlan': 'Piano corrente',
    'subscription.toggle.monthly': 'Mensile',
    'subscription.toggle.annual': 'Annuale',
    'subscription.toggle.annualSave': 'Risparmia {percent}%',
    'subscription.priceFree': 'Gratis',
    'subscription.perMonth': '/mese',
    'subscription.perYearBilled': 'Fatturato €{total}/anno',
    'subscription.card.cta_upgrade': 'Upgrade',
    'subscription.card.cta_downgrade': 'Downgrade',
    'subscription.card.cta_current': 'Piano corrente',
    'subscription.card.cta_trial_active': 'Trial attivo',
    'subscription.card.cta_trial_included': 'Trial gratuito incluso',
    'subscription.agency.loadingContext': 'Caricamento del tuo piano Agency… Se ci vuole più di qualche secondo, prova a ricaricare la pagina.',
    'subscription.card.recommended': '⭐ Raccomandato',
    'subscription.card.priceTrial': 'Gratis',
    'subscription.card.priceTrialPeriod': '/ {days}gg',
    'subscription.card.trialBadge': '{days} giorni gratuiti',
    'subscription.card.activeBadge': 'Piano attivo',
    'subscription.limits.seats': '{n} utenti',
    'subscription.limits.positions': '{n} job attivi',
    'subscription.limits.candidates': '{n} candidati AI / mese',
    'subscription.limits.candidatesNoMonth': '{n} candidati AI',
    // v3.5: pool unico "AI Credits"
    'subscription.limits.aiCompany': '{n} AI Credits',
    'subscription.limits.aiCompanyMonthly': '{n} AI Credits / mese',
    // @deprecated v3.5
    'subscription.limits.aiUser': '{n} AI credits personali',
    'subscription.limits.aiCreditsTotal': '{n} AI Credits totali',
    'subscription.limits.aiCreditsTotalMonthly': '{n} AI Credits totali / mese',
    'subscription.limits.transcripts': '{n} trascrizioni',
    'subscription.compare.title': 'Confronto piani',
    'subscription.compare.feature': 'Caratteristica',
    'subscription.compare.row.positions': 'Job Position',
    'subscription.compare.row.candidates': 'Candidati AI / mese',
    'subscription.compare.row.aiCompany': 'AI Credits / mese',
    // @deprecated v3.5
    'subscription.compare.row.aiUser': 'AI credits personali',
    'subscription.compare.row.transcripts': 'Trascrizioni / mese',
    'subscription.compare.row.seats': 'Utenti',
    'subscription.compare.row.callTranscription': 'Trascrizione call',
    'subscription.compare.creditsExplainer.title': 'Differenza tra Candidati AI e AI Credits',
    'subscription.compare.creditsExplainer.candidatesAi': 'I “Candidati AI” sono il volume mensile di candidati elaborabili con CV screening, generazione interview report, trascrizione call e report, e AutoFill Job Position.',
    'subscription.compare.creditsExplainer.aiCredits': 'Gli "AI Credits" coprono tutte le azioni AI diverse dallo screening CV: chat candidato, assistente Job Position, trascrizione chiamate, AutoFill JD, interview report.',
    // v3.5: pool unico
    'subscription.poolLabel.aiCompany': 'AI Credits',
    // @deprecated v3.5
    'subscription.poolLabel.aiUser': 'AI credits (utente)',
    'subscription.poolLabel.candidates': 'Candidati',
    'subscription.poolLabel.transcripts': 'Trascrizioni',
    'subscription.poolLabel.seats': 'Utenti',
    'subscription.poolLabel.positions': 'Job Position',
    'subscription.poolLabel.ai_company': 'AI credits (azienda)',
    'subscription.poolLabel.ai_user': 'AI credits (utente)',
    'subscription.poolLabel.recruiter_seats': 'Recruiter (posti)',
    'subscription.poolLabel.agency_clients': 'Aziende cliente',
    // ─── Pricing v3.2 (21/05/26) — Agency tier display & chooser ───────────
    'subscription.tier.freemium_trial': 'Trial',
    'subscription.tier.sme_starter': 'Starter',
    'subscription.tier.sme_growth': 'Growth',
    'subscription.tier.sme_professional': 'Professional',
    'subscription.tier.agency_starter': 'Agency Starter',
    'subscription.tier.agency_medium': 'Agency Medium',
    'subscription.tier.agency_professional': 'Agency Professional',
    'subscription.tier.agency_enterprise': 'Agency Enterprise',
    'subscription.agency.pricePerCompany': '€{price} × {n} aziende = €{total}/mese',
    'subscription.agency.pricePerCompanyShort': '€{price}/azienda/mese',
    'subscription.agency.companiesUsedOf': '{used} di {cap} aziende',
    'subscription.agency.companiesCap': 'Fino a {n} aziende gestite',
    'subscription.agency.companiesUnlimited': 'Aziende gestite illimitate',
    'subscription.agency.chooserTitle': 'Quante aziende cliente gestisci?',
    'subscription.agency.chooserSubtitle': 'Pre-acquista i seats per il tuo tier subscription. Puoi modificare il numero in seguito dal portal Stripe.',
    'subscription.agency.chooserConfirm': 'Procedi al pagamento',
    'subscription.agency.chooserCancel': 'Annulla',
    'subscription.agency.chooserTierBand': 'Tier {tier}: {min}–{max} aziende',
    'subscription.agency.chooserTotalPreview': 'Pagherai €{total}/mese per {n} aziende',
    'subscription.agency.contactSales': 'Contatta il sales',
    'subscription.agency.enterpriseSalesPrompt': 'Per 20+ aziende gestite, il nostro team sales ti preparerà un piano Enterprise su misura.',
    'subscription.feature.q3_2026': 'Coming Soon',
    'subscription.feature.q3_2026_tooltip': 'In sviluppo — disponibile prossimamente',
    'subscription.feature.comingSoon': 'Coming Soon',
    'subscription.compare.row.crossClientMatching': 'Matching AI cross-client',
    'subscription.compare.row.centralizedCandidateDb': 'DB candidati centralizzato',
    'subscription.compare.row.accountManager': 'Account manager',
    'subscription.compare.row.teamDedicato': 'Team dedicato',
    // ─── v3.2 Tabella comparativa Agency ──────────────────────────────────
    'subscription.agency.compare.title': 'Confronto piani Agency',
    'subscription.agency.compare.row.companies': 'Aziende gestite',
    'subscription.agency.compare.row.pricePerCompany': 'Prezzo / azienda / mese',
    'subscription.agency.compare.row.jobsPerCompany': 'Job attivi / azienda',
    'subscription.agency.compare.row.candidatesPerCompany': 'Candidati AI / azienda / mese',
    // v3.2 (21/05 19:30 ROLLBACK 19:28) — Utenti e AI Credits sono PER AZIENDA
    // (ereditati dal tier SME equivalente). Il modello "subscription-wide"
    // \u00e8 stato revertato dal CEO per competitivit\u00e0 vs i piani SME.
    'subscription.agency.compare.row.seatsPerCompany': 'Utenti / azienda',
    'subscription.agency.compare.row.aiCreditsPerCompany': 'AI Credits / azienda / mese',
    // v3.2 polish (21/05/26) — summary grid 4 celle nell header card
    'subscription.agency.summary.jobs': '{n} job',
    'subscription.agency.summary.candidates': '{n} candidati AI',
    'subscription.agency.summary.seats': '{n} utenti',
    'subscription.agency.summary.aiCredits': '{n} AI Credits',
    'subscription.agency.summary.perCompany': 'per azienda',
    'subscription.agency.summary.perCompanyMonth': 'per azienda / mese',
    // Legacy keys mantenute per backward compat read-time — non pi\u00f9 renderizzate.
    'subscription.agency.compare.row.seatsTotal': 'Utenti totali subscription',
    'subscription.agency.compare.row.aiCreditsTotal': 'AI Credits totali / mese',
    'subscription.agency.compare.row.aiUserPerCompany': 'AI Credits / utente / mese',
    'subscription.agency.compare.row.support': 'Canale supporto',
    'subscription.agency.compare.row.centralizedDb': 'DB candidati centralizzato',
    'subscription.agency.compare.row.crossClientAi': 'Matching AI cross-client',
    'subscription.agency.compare.row.accountManager': 'Account manager dedicato',
    'subscription.agency.compare.row.whiteLabel': 'White-label',
    'subscription.agency.compare.row.customAi': 'Modelli AI custom',
    'subscription.agency.compare.row.csm': 'Team CSM dedicato',
    'subscription.agency.support.email': 'Email prioritaria',
    'subscription.agency.support.chat': 'Chat dedicata',
    'subscription.agency.support.accountManager': 'Account manager',
    'subscription.agency.support.csmTeam': 'Team CSM dedicato',
    'subscription.agency.support.custom': 'Personalizzato',
    'subscription.agency.enterprise.description': 'Per grandi agenzie di headhunting che gestiscono 20+ aziende cliente. Contratto su misura, team dedicato, white-label, modelli AI personalizzati per i tuoi clienti.',
    'subscription.agency.enterprise.contactCta': 'Contattaci',
    'subscription.usage.title': 'Utilizzo (questo mese)',
    'subscription.usage.unlimited': 'Illimitato',
    'subscription.usage.limitReached': 'Hai raggiunto il limite — fai upgrade per più capacity',
    'subscription.usage.empty': 'Nessun dato di utilizzo disponibile.',
    'subscription.portal.title': 'Pagamento',
    'subscription.portal.cta': 'Gestisci su Stripe',
    'subscription.portal.adminOnly': 'Solo gli admin possono gestire la fatturazione.',
    'subscription.checkout.success_toast': 'Piano aggiornato a {plan}',
    'subscription.checkout.applied': 'Aggiornamento applicato — ricarico…',
    'subscription.checkout.error': 'Aggiornamento abbonamento fallito',
    'subscription.checkout.processing': 'Elaborazione…',
    'subscription.checkout.downgrade_confirm_title': 'Conferma downgrade',
    'subscription.checkout.downgrade_confirm_body': 'Confermi il downgrade da {from} a {to}? Eventuali job/candidati eccedenti potrebbero diventare inaccessibili.',
    'subscription.checkout.threeDS_required': 'Verifica richiesta dalla tua banca. Apri il portale Stripe per completare.',
    'subscription.loading': 'Caricamento piani…',
    'subscription.loadError': 'Impossibile caricare i piani.',
    'subscription.retry': 'Riprova',
    'subscription.agencyDiscovery.title': "Sei un'agenzia di recruiting?",
    'subscription.agencyDiscovery.subtitle': 'Gestisci più aziende clienti con un unico account',
    'subscription.agencyDiscovery.note': 'La tua azienda corrente diventerà il primo cliente della tua agency. Nessun dato verrà perso.',
    'agency.convert.cta.starter': 'Diventa Agency',
    'agency.convert.cta.pro': 'Diventa Agency Pro',
    'agency.tier.enterprise.title': 'Agency Enterprise',
    'agency.tier.enterprise.badge': 'Personalizzato',
    'agency.tier.enterprise.priceLabel': 'Da €499',
    'agency.tier.enterprise.priceUnit': '/recruiter/mese',
    'agency.tier.enterprise.feat1': 'Aziende clienti illimitate',
    'agency.tier.enterprise.feat2': 'Job illimitati per recruiter',
    'agency.tier.enterprise.feat3': 'White-label completo (logo, dominio, email)',
    'agency.tier.enterprise.feat4': 'CSM dedicato + modelli AI custom',
    'agency.tier.enterprise.feat5': 'Setup fee €2-5k · Contratto su misura',
    'agency.tier.enterprise.cta': 'Contatta il sales',
    'agency.tier.enterprise.contactSubject': 'Richiesta pricing Agency Enterprise',
    'agency.tier.enterprise.contactBody': 'Ciao team HiroMatch,\n\nSono interessato al tier Agency Enterprise. Qualche info sulla mia agenzia:\n\n- Numero di recruiter: \n- Numero di aziende clienti: \n- Job/mese stimati: \n\nIn attesa di un vostro contatto.',
    'agency.contactModal.title': 'Gestisci più Aziende?',
    'agency.contactModal.body': 'Contattaci e ti aiuteremo ad attivare il piano multi-azienda giusto per te.',
    'agency.contactModal.cta': 'Contattaci',
    'agency.contactModal.mailtoSubjectAddCompany': 'Richiesta piano multi-azienda — aggiungi azienda',
    'agency.contactModal.mailtoBodyAddCompany': 'Ciao team HiroMatch,\n\nVorrei gestire più di un\'azienda sul mio account.\n\nNome azienda: \nEmail: \n\nGrazie!',
    'agency.contactModal.mailtoSubjectSignup': 'Richiesta piano multi-azienda — registrazione',
    'agency.contactModal.mailtoBodySignup': 'Ciao team HiroMatch,\n\nMi sto registrando e vorrei informazioni sui piani multi-azienda.\n\nNome azienda o agenzia: \nEmail: \n\nGrazie!',
    'agency.convert.confirmTitle': 'Confermi la conversione?',
    'agency.convert.confirmBody': 'Il tuo account passerà a {plan} e la tua azienda corrente diventerà il primo cliente. Sarai reindirizzato al checkout Stripe.',
    'agency.convert.confirmCta': 'Procedi',
    'agency.convert.success': 'Conversione completata. Ora gestisci la tua agency.',
    'upgrade.smeCap.title': "Hai già un'azienda",
    'upgrade.smeCap.body': 'Sei admin di "{name}". Ogni account può gestire una SME, ma puoi convertirti in Agency per gestirne più di una.',
    'upgrade.agencyStarterCap.title': 'Hai raggiunto il massimo',
    'upgrade.agencyStarterCap.body': 'Hai usato {used}/{cap} clienti su Agency Starter. Aggiorna ad Agency Professional per arrivare a 20.',
    'upgrade.agencyProCap.title': 'Hai raggiunto il massimo su Agency Pro',
    'upgrade.agencyProCap.body': 'Hai raggiunto {used}/{cap} clienti, il limite di Agency Professional. Per gestirne di più contatta il supporto (Agency Enterprise).',
    // v3.2 polish (21/05/26) — copy dinamico per next tier (Starter→Medium / Medium→Professional)
    'upgrade.agencyTieredCap.title': 'Hai raggiunto il massimo',
    'upgrade.agencyTieredCap.body': 'Hai usato {used}/{cap} clienti su Agency {currentLabel}. Aggiorna ad Agency {nextLabel} per arrivare a {nextCap}.',
    'upgrade.cta.upgradeAgencyNext': 'Aggiorna ad Agency {nextLabel}',
    'upgrade.cta.upgradeSme': 'Aggiorna piano',
    'upgrade.cta.upgradeAgency': 'Aggiorna ad Agency Pro',
    'upgrade.cta.convertToAgency': 'Diventa Agency',
    'upgrade.cta.contactSupport': 'Contatta supporto',

    // Dashboard usage widgets (pricing v3 caps)
    'usage.section_title': 'Utilizzo questo mese',
    'usage.agency_overview': 'Panoramica Agency',
    'usage.plan_level': 'Il tuo Piano',
    'usage.selected_company': 'Azienda Selezionata',
    'usage.ai_company': 'AI Credits',
    // @deprecated v3.5
    'usage.ai_user': 'AI Credits Personali',
    'usage.candidates': 'Candidati elaborati',
    'usage.transcripts': 'Trascrizioni',
    'usage.positions': 'Posizioni attive',
    'usage.seats': 'Posti del team',
    'usage.of': 'di',
    'usage.unlimited': 'Illimitato',
    'usage.reset_date': 'Reset il {date}',
    'usage.approaching_cap': 'Stai per esaurire — ',
    'usage.cap_reached': 'Cap raggiunto — ',
    'usage.cta_upgrade': 'Upgrade',
    'usage.cta_upgrade_to_starter': 'Upgrade a Starter',
    'usage.trial_no_ai': 'AI non disponibile in prova',
    'usage.load_error': "Impossibile caricare l'utilizzo",
    'usage.retry': 'Riprova',
  },
};

/**
 * App UI language: English unless the user saved a choice in Profile.
 * Browser locale is intentionally ignored — recruiters pick language explicitly.
 */
export const DEFAULT_APP_LANGUAGE: Language = 'en';

/** @deprecated Browser locale is no longer used for app UI defaults. */
export function detectBrowserLanguage(): Language {
  return DEFAULT_APP_LANGUAGE;
}

/** Resolve UI language outside React providers (e.g. global error boundary). */
export function resolveAppLanguage(): Language {
  if (typeof window !== 'undefined') {
    if (isPublicApplyContext()) return DEFAULT_APP_LANGUAGE;
    const saved = localStorage.getItem('app-language');
    if (saved === 'it' || saved === 'en') return saved;
  }
  return DEFAULT_APP_LANGUAGE;
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [language, setLanguageState] = useState<Language>(() => {
    // 1. localStorage (explicit choice from Profile)
    // 2. English default
    // After login, useEffect syncs users.preferredLanguage from the API.
    return resolveAppLanguage();
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('app-language', lang);
    setDateLocale(lang);
  }, []);

  useEffect(() => {
    setDateLocale(language);
  }, [language]);

  /** After login, align UI with `users.preferredLanguage` (default English when unset). */
  useEffect(() => {
    if (!user) return;
    if (isPublicApplyContext()) {
      setLanguageState(DEFAULT_APP_LANGUAGE);
      setDateLocale(DEFAULT_APP_LANGUAGE);
      return;
    }
    const pl = user.preferredLanguage;
    const resolved: Language = pl === 'en' || pl === 'it' ? pl : DEFAULT_APP_LANGUAGE;
    setLanguageState(resolved);
    localStorage.setItem('app-language', resolved);
    setDateLocale(resolved);
  }, [user?.uid, user?.preferredLanguage]);

  useEffect(() => {
    document.documentElement.lang = language === 'it' ? 'it' : 'en';
  }, [language]);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    let text = translations[language][key] || translations.en[key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(`{${k}}`, String(v));
      });
    }
    return text;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
